import { db, auth, storage } from './config';
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import {
  collection,
  getDocs,
  getDocsFromServer,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  limit,
  onSnapshot,
  or,
  writeBatch,
  serverTimestamp,
  increment,
  orderBy,
  arrayUnion,
  deleteField,
  runTransaction
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { buildReopenLegalQueuePatch, renewalContractDocUrl } from '../utils/renewalLegalQueue';
import {
  assessContractIntegrity,
  pickDomainSyncPatch,
  canSkipLegalToImplementation,
  proposalOptionHasCommercialLines,
  mergePreserveProposalLines,
  LEGAL_STATUS_NOT_REQUIRED,
  LEGAL_SKIP_REASON_GEMINI_EXISTING
} from '../utils/contractIntegrity';
import { preferGoogleOpportunity } from '../utils/googleOpportunity';
import {
  renewalStageToPipelineStage,
  pipelineStageToRenewalStage,
  syncRenewalStageFields,
  mergeRenewalDisplayFromOriginal
} from '../utils/renewalPipelineStage';
import {
  isCrmLeadCommercialBaja,
  hasStaleDiscardFlag,
  mergeLeadStatusFields,
  isOnboardingExpedienteComplete,
  shouldLinkCrmLeadToContract,
  buildHideFromSalesPipelinePatch,
  shouldPreserveAdvancedPipelineStage,
  onboardingInvitePipelinePatch,
  buildPipelineStageAlignPatch
} from '../utils/salesPipelineVisibility';
import {
  BILLING_FREQ_MULTIPLIERS,
  resolveDisplayBillingFrequency
} from '../utils/contractDisplayAmount';
import { licenseDiscountsDropped, mergePreservedDiscount } from '../utils/licenseDiscountPolicy';
import { mergeVigenciaIntoProposal, preferCompleteProposal, rebaseProposalEdits } from '../utils/proposalCommercial';
import {
  isPorCobrarSatSku,
  matchCatalogSkuKey,
  sanitizeLicenseUnitPrice,
  catalogMonthlyUnit,
  catalogPriceTypeFromLabel,
  displayLicenseLabel
} from '../utils/licenseCatalogMatch';
import {
  buildCreateFields,
  normalizeSupportTicket
} from '../utils/supportTicketSchema';
import { enrichTicketSla, buildSlaFieldsForCreate } from '../dashboard/support/supportSla';
import { mergeCommercialTerms, resolveTermDiscountPolicy } from '../utils/commercialTermsCatalog';
import {
  billingLicenseDiscount,
  billingLicenseUnit,
  canEditBillingAdjustment,
  lockBillingAdjustmentOnInvoice,
} from '../utils/billingAdjustment';

export {
  normalizeTermLabel,
  inferTermTypeFromLabel,
  findTermOption,
  canonicalTermLabel,
  termSelectOptions,
  resolveTermType,
  resolveTermDiscountPolicy,
  mergeCommercialTerms,
  DEFAULT_COMMERCIAL_TERMS
} from '../utils/commercialTermsCatalog';

export {
  renewalStageToPipelineStage,
  pipelineStageToRenewalStage,
  syncRenewalStageFields,
  mergeRenewalDisplayFromOriginal
};
export { isPorCobrarSatSku, catalogPriceTypeFromLabel };

// ==========================================
// 🔌 CONFIGURACIÓN DE CONEXIÓN (RESPETANDO .ENV)
// ==========================================
/** En prod nunca usar localhost aunque .env.local lo haya embebido en el build. */
export function resolveApiBaseUrl() {
  const fromEnv = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
  if (typeof window === 'undefined') return fromEnv;

  const host = window.location.hostname;
  const isLocalDev = host === 'localhost' || host === '127.0.0.1';

  if (isLocalDev) {
    return fromEnv || 'http://localhost:8080';
  }
  if (host === 'www.plexarian.com' || host === 'plexarian.com') {
    return 'https://nexus.plexarian.com';
  }
  if (fromEnv.includes('localhost') || fromEnv.includes('127.0.0.1')) {
    return '';
  }
  return fromEnv;
}

export const API_BASE_URL = resolveApiBaseUrl();

/**
 * Base URL para jobs largos (cruce GWS↔PC, etc.).
 * En producción evita el rewrite de Firebase Hosting (~60s) y habla
 * directo con Cloud Run (timeout del servicio, p. ej. 3600s).
 */
export function resolveLongJobApiBaseUrl() {
  if (typeof window === 'undefined') return API_BASE_URL || '';
  const host = window.location.hostname;
  const isLocalDev = host === 'localhost' || host === '127.0.0.1';
  if (isLocalDev) return API_BASE_URL || 'http://localhost:8080';
  const fromEnv = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
  if (fromEnv && !fromEnv.includes('localhost') && !fromEnv.includes('127.0.0.1')) {
    return fromEnv;
  }
  return 'https://nexus-backend-125878838452.us-central1.run.app';
}

if (!API_BASE_URL) {
  console.log("🔌 Conectando API en modo relativo (mismo host)");
} else {
  console.log("🔌 Conectando API a:", API_BASE_URL);
}

/**
 * Función genérica para peticiones al servidor
 */
export const apiCall = async (endpoint, method = 'POST', body = null, isFormData = false, signal = null) => {
  try {
    const headers = isFormData ? {} : { 'Content-Type': 'application/json' };
    const options = {
      method,
      headers
    };
    if (signal) options.signal = signal;

    if (method !== 'GET' && method !== 'HEAD' && body !== null) {
      options.body = isFormData ? body : JSON.stringify(body);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.error || `Error del servidor: ${response.status}`);
      err.status = response.status;
      if (errorData.code) err.code = errorData.code;
      if (errorData.issues) err.issues = errorData.issues;
      if (errorData.hint) err.hint = errorData.hint;
      if (errorData.domain) err.domain = errorData.domain;
      throw err;
    }
    return await response.json();
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    if (!error?.status || error.status >= 500) {
      console.error(`Error en API (${endpoint}):`, error);
    }
    throw error;
  }
};

// ==========================================
// 🚀 FUNCIONES DEL SERVIDOR (VERTEX AI & CLOUD RUN)
// ==========================================

export const verifyClientDomain = async (dominio) => {
  return await apiCall(`/api/licencias/verify-client/${dominio}`, 'GET');
};

async function clientSpaceAuthHeaders(extra = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión con Google Workspace.');
  const token = await user.getIdToken();
  return { Accept: 'application/json', ...extra, Authorization: `Bearer ${token}` };
}

async function clientSpaceFetch(endpoint, method = 'GET', body = null) {
  const headers = await clientSpaceAuthHeaders(
    method === 'GET' || method === 'HEAD' ? {} : { 'Content-Type': 'application/json' }
  );
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
}

/** Sesión del espacio: cliente de Partner y si es administrador de consola. */
export const fetchClientSpaceSession = async () => clientSpaceFetch('/api/client-space/session', 'POST', {});

/** Liga de Por Cobrar e instrucciones. Solo administrador. */
export const fetchClientSpaceBilling = async () => clientSpaceFetch('/api/client-space/billing', 'GET');

/** Cuentas del dominio que el administrador autorizó a solicitar licencias. */
export const fetchLicenseDelegates = async () => clientSpaceFetch('/api/client-space/license-delegates', 'GET');

export const addLicenseDelegate = async (email) => (
  clientSpaceFetch('/api/client-space/license-delegates', 'POST', { email })
);

export const removeLicenseDelegate = async (email) => (
  clientSpaceFetch(`/api/client-space/license-delegates?email=${encodeURIComponent(email)}`, 'DELETE')
);

export const getClientCatalog = async (dominio) => {
  return await clientSpaceFetch(`/api/licencias/catalogo/${encodeURIComponent(dominio)}`, 'GET');
};

/** Sube OC firmada (PDF) para dominios que la exigen en el portal. */
export const uploadClientPurchaseOrder = async ({ dominio, usuario, file }) => {
  const form = new FormData();
  form.append('file', file);
  form.append('dominio', dominio);
  form.append('usuario', usuario);
  const headers = await clientSpaceAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/licencias/client-po-upload`, {
    method: 'POST',
    headers,
    body: form
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error al subir la OC (${response.status})`);
  }
  return data;
};

export const uploadClientCsf = async ({ dominio, usuario, file }) => {
  const form = new FormData();
  form.append('file', file);
  form.append('dominio', dominio);
  form.append('usuario', usuario);
  const headers = await clientSpaceAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/licencias/csf-upload`, {
    method: 'POST',
    headers,
    body: form
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error al subir la constancia (${response.status})`);
  }
  return data;
};

/** Alta de licencias desde portal cliente (licencias.plexarian.com).
 *  Si hay una solicitud PENDIENTE del dominio, responde needsConfirmation + pendientes (HTTP 409)
 *  sin lanzar error, para que la UI pida confirmación. Reenviar con confirmDuplicate: true. */
export const submitClientLicenseRequest = async (payload) => {
  const headers = await clientSpaceAuthHeaders({ 'Content-Type': 'application/json' });
  const response = await fetch(`${API_BASE_URL}/api/licencias/solicitar`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (response.status === 409 && data.needsConfirmation) {
    return data;
  }
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

export const checkDomainBatch = async (domains) => {
  return await apiCall('/api/domain/check-batch', 'POST', { domains });
};

export const checkDomainAvailability = async (domain) => {
  return await apiCall('/api/domain/check-availability', 'POST', { domain });
};

/** Actualiza dominio canónico del cliente (contrato + CRM + SkillSync + ligados). Solo Dirección/Gerencia. */
export const updateClientDomainCentral = async ({
  domain,
  contractId = null,
  ticketId = null,
  leadId = null,
  note = '',
  requesterRole = '',
  requesterEmail = ''
}) => {
  return await apiCall('/api/domain/client', 'PATCH', {
    domain,
    contractId,
    ticketId,
    leadId,
    note,
    requesterRole,
    requesterEmail
  });
};

export const validateDocs = async (formData) => {
  return await apiCall('/api/onboarding/validate-docs', 'POST', formData, true);
};

export const initializeOnboarding = async (data) => {
  return await apiCall('/api/onboarding/initialize-onboarding', 'POST', data);
};

export const finalizeContract = async (contractData, { skipSow = false } = {}) => {
  return await apiCall('/api/onboarding/finalize-contract', 'POST', { contractData, skipSow });
};

/** Etapas donde el ejecutivo puede editar propuesta (hasta Firma inclusive). */
export const PROPOSAL_EDITABLE_PIPELINE_STAGES = new Set([
  'SALES_REVIEW',
  'ONBOARDING',
  'LEGAL_REVIEW',
  'SIGNING'
]);

export const canEditCommercialProposalForStage = (pipelineStage) =>
  PROPOSAL_EDITABLE_PIPELINE_STAGES.has(pipelineStage);

/**
 * Guarda el ajuste que ve Contabilidad. No toca licencias, propuesta, anexo ni el Google Doc.
 */
export const persistBillingAdjustment = async (contractId, adjustment, options = {}) => {
  const { isRenewal = false, clear = false } = options;
  if (!contractId) throw new Error('Falta el expediente.');
  const collectionName = await resolveContractCollectionName(contractId, { isRenewal });
  const contractRef = doc(db, collectionName, contractId);
  const snap = await getDoc(contractRef);
  if (!snap.exists()) throw new Error('No encontré el expediente.');
  const existing = snap.data();
  if (!canEditBillingAdjustment(existing)) {
    throw new Error('Este expediente ya no admite un ajuste de facturación.');
  }
  const lastUpdate = new Date().toISOString();
  if (clear) {
    await updateDoc(contractRef, { billingAdjustment: deleteField(), lastUpdate });
    return { billingAdjustment: null };
  }
  if (!adjustment?.active) throw new Error('El ajuste no es válido.');
  await updateDoc(contractRef, { billingAdjustment: adjustment, lastUpdate });
  return { billingAdjustment: adjustment };
};

/** Actualiza Google Docs (contrato + SOW) con licencias/propuesta actuales. */
export const regenerateCommercialGoogleDocs = async (contractId, { skipSow = false, createContractDoc = false } = {}) =>
  apiCall('/api/onboarding/regenerate-commercial-docs', 'POST', { contractId, skipSow, createContractDoc });

// Se agregó triggerNotification para el módulo de ventas
export const triggerNotification = async (event, data, options = {}) => {
  const { strict = false } = options;
  try {
    await apiCall('/notify-system', 'POST', { event, data });
  } catch (e) {
    if (strict) throw e;
    console.warn("No se pudo enviar la notificación:", e.message);
  }
};

export const createClientSpace = async (contractId, domain, executiveEmail, options = {}) => {
  return await apiCall('/api/chat/create-client-space', 'POST', {
    contractId,
    domain,
    executiveEmail,
    renewalId: options.renewalId || null,
    isRenewal: Boolean(options.isRenewal)
  });
};

/** El aviso al espacio exige el token de la sesión. apiCall no lo manda. */
async function chatStaffFetch(endpoint, method = 'GET', body = null) {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para avisar en el espacio.');
  const token = await user.getIdToken();
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`
  };
  if (method !== 'GET' && method !== 'HEAD') headers['Content-Type'] = 'application/json';
  const options = { method, headers };
  if (method !== 'GET' && method !== 'HEAD' && body != null) options.body = JSON.stringify(body);
  const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err = new Error(errorData.error || `Error del servidor: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export const getLicenseRequestTargets = async (contractId) =>
  chatStaffFetch(`/api/chat/contracts/${encodeURIComponent(contractId)}/license-requests`, 'GET');

export const sendLicenseRequestAlert = async (contractId, gwsId) =>
  chatStaffFetch(`/api/chat/contracts/${encodeURIComponent(contractId)}/license-requests`, 'POST', { gwsId });

export const joinClientChatSpace = async (contractId) =>
  chatStaffFetch(`/api/chat/contracts/${encodeURIComponent(contractId)}/space-join`, 'POST', {});

export const postAbsenceNotice = (payload) =>
  chatStaffFetch('/api/chat/absence-notice', 'POST', payload);

export const postTimeOffRequestNotice = (payload) =>
  chatStaffFetch('/api/chat/time-off-request-notice', 'POST', payload);

export const postLoanRequestNotice = (payload) =>
  chatStaffFetch('/api/chat/loan-request-notice', 'POST', payload);

export const postIncidenceRequestNotice = (payload) =>
  chatStaffFetch('/api/chat/incidence-request-notice', 'POST', payload);

export const postPayrollIncidenceNotice = (payload) =>
  chatStaffFetch('/api/chat/payroll-incidence-notice', 'POST', payload);

export const getGoogleUsdMxn = ({ months = [], lock = '' } = {}) => {
  const params = new URLSearchParams();
  if (months.length) params.set('months', months.join(','));
  if (lock) params.set('lock', lock);
  const query = params.toString();
  return chatStaffFetch(`/api/google-fx${query ? `?${query}` : ''}`, 'GET');
};

// ==========================================
// 🚀 FUNCIONES DE MARKETING HUB (RESTAURADAS)
// ==========================================

export const fetchMarketingContacts = async (params = {}) => {
  const { limit = 50, lastCreatedAt = null, search = '' } = params;
  try {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit);
    if (lastCreatedAt) queryParams.append('lastCreatedAt', lastCreatedAt);
    if (search) queryParams.append('search', search);

    const data = await apiCall(`/api/campaigns/contacts-paginated?${queryParams.toString()}`, 'GET');
    return data; // Returns { contacts, hasMore, total? }
  } catch (e) {
    console.error("Error fetching marketing contacts:", e);
    return { contacts: [], hasMore: false, total: 0 };
  }
};

export const fetchAudienceDirectory = async (params = {}) => {
  const {
    limit = 50,
    cursor = null,
    origin = 'all',
    status = 'valid',
    search = '',
    requireEmail = true,
    signal = null,
  } = params;
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('limit', String(limit));
    queryParams.append('origin', origin);
    queryParams.append('status', status);
    queryParams.append('requireEmail', requireEmail ? '1' : '0');
    if (cursor) queryParams.append('cursor', cursor);
    if (search) queryParams.append('search', search);

    const headers = { 'Content-Type': 'application/json' };
    if (auth.currentUser) {
      headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
    }
    const options = { method: 'GET', headers };
    if (signal) options.signal = signal;
    const response = await fetch(
      `${API_BASE_URL}/api/campaigns/audience-directory?${queryParams.toString()}`,
      options
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.error || `Error del servidor: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return await response.json();
  } catch (e) {
    if (e?.name === 'AbortError') throw e;
    console.error('Error fetching audience directory:', e);
    return { rows: [], hasMore: false, cursor: null, totals: null };
  }
};

export const fetchSuppressionStats = async () => {
  try {
    return await apiCall('/api/campaigns/suppression-stats', 'GET');
  } catch (e) {
    console.error("Error fetching suppression stats:", e);
    return { count: null };
  }
};

export const fetchBlacklistPaginated = async (params = {}) => {
  const { limit = 50, lastEmail = null, search = '', group = 'all', signal = null } = params;
  try {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit);
    if (lastEmail) queryParams.append('lastEmail', lastEmail);
    if (search) queryParams.append('search', search);
    if (group) queryParams.append('group', group);
    return await apiCall(`/api/campaigns/blacklist-paginated?${queryParams.toString()}`, 'GET', null, false, signal);
  } catch (e) {
    if (e?.name === 'AbortError') throw e;
    console.error("Error fetching blacklist:", e);
    return { entries: [], hasMore: false, total: 0 };
  }
};

export const fetchSuppressionEmails = async () => {
  try {
    return await apiCall('/api/campaigns/suppression-emails', 'GET');
  } catch (e) {
    console.error("Error fetching suppression emails:", e);
    return { emails: [], count: 0 };
  }
};

export const importMarketingContacts = async (contacts, importedBy) => {
  return await apiCall('/api/campaigns/import-contacts', 'POST', { contacts, importedBy });
};

export const fetchMarketingTags = async () => {
  try {
    const data = await apiCall('/api/campaigns/tags', 'GET');
    return data.tags || [];
  } catch (e) {
    console.error("Error fetching marketing tags:", e);
    return [];
  }
};

async function campaignAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  if (auth.currentUser) {
    headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
  }
  return headers;
}

export const estimateCampaignAudience = async ({ audienceType, selectedTags = [] }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/estimate-audience`, {
      method: 'POST',
      headers: await campaignAuthHeaders(),
      body: JSON.stringify({ audienceType, selectedTags }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.error || `Error del servidor: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return data;
  } catch (e) {
    console.error("Error estimating campaign audience:", e);
    return { count: 0, rawCount: 0, suppressedCount: 0, error: e.message };
  }
};

export const sendMarketingCampaign = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/api/campaigns/send`, {
    method: 'POST',
    headers: await campaignAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `Error del servidor: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return data;
};

export const submitGeminiFridayRsvp = async (payload) => {
  return await apiCall('/api/campaigns/event-rsvp', 'POST', payload);
};

export const resumeCampaignDelivery = async (campaignId) => {
  return await apiCall(`/api/campaigns/${campaignId}/resume-delivery`, 'POST', {});
};

export const sendCourtesyInvitationApi = async (leadId, data) => {
  return await apiCall(`/api/crm/leads/${leadId}/courtesy-invitation`, 'POST', data);
};

export const sendGoogleDayInvitationApi = async (leadId, data) => {
  return await apiCall(`/api/crm/leads/${leadId}/google-day-invitation`, 'POST', data);
};

/** Lead CRM vinculado a un contrato (por crmLeadId, token micrositio o dominio). */
export const getCrmLeadForContract = async (contract = {}) => {
  const leadId = contract.crmLeadId || contract.proposalMicrositeToken;
  if (leadId) {
    const lead = await getLeadById(leadId);
    if (lead && !lead.isContract) return lead;
  }
  const domain = (contract.dominio || contract.domain || '').trim();
  const companyName = (contract.razon_social || contract.nombre_comercial || contract.companyName || '').trim();
  try {
    if (domain) {
      const q = query(collection(db, 'crm_leads'), where('domain', '==', domain), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
      }
    }
    if (companyName) {
      const qCo = query(collection(db, 'crm_leads'), where('companyName', '==', companyName), limit(1));
      const snapCo = await getDocs(qCo);
      if (!snapCo.empty) {
        const d = snapCo.docs[0];
        return { id: d.id, ...d.data() };
      }
    }
    return null;
  } catch (e) {
    console.error('getCrmLeadForContract:', e);
    return null;
  }
};

export const fetchContractByDomain = async (domain) => {
  if (!domain) return null;
  try {
    let q = query(collection(db, "contratos"), where("dominio", "==", domain), limit(1));
    let snapshot = await getDocs(q);
    if (!snapshot.empty) return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };

    q = query(collection(db, "contratos"), where("domain", "==", domain), limit(1));
    snapshot = await getDocs(q);
    if (!snapshot.empty) return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    
    q = query(collection(db, "onboarding_invitations"), where("domain", "==", domain), limit(1));
    snapshot = await getDocs(q);
    if (!snapshot.empty) return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };

    // Fallback Final
    q = query(collection(db, "invitations"), where("domain", "==", domain), limit(1));
    snapshot = await getDocs(q);
    if (!snapshot.empty) return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };

    return null;
  } catch (e) {
    console.error("Error fetching contract:", e);
    return null;
  }
};

export const fetchCampaigns = async () => {
  try {
    const data = await apiCall('/api/campaigns/hub-campaigns', 'GET');
    return Array.isArray(data?.campaigns) ? data.campaigns : [];
  } catch (e) {
    console.error("Error fetching campaigns:", e);
    return [];
  }
};

export const fetchCampaignEvents = async (campaignId) => {
  try {
    const eventsRef = collection(db, "marketing_metrics");
    const q = query(eventsRef, where("campaignId", "==", campaignId));
    const snapshot = await getDocs(q);
    const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Si no hay en marketing_metrics, intentamos la colección antigua (events subcollection)
    if (events.length === 0) {
       const oldEventsRef = collection(db, "marketing_campaigns", campaignId, "events");
       const oldSnapshot = await getDocs(oldEventsRef);
       const oldEvents = oldSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
       return oldEvents.sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
    
    return events.sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp));
  } catch (e) {
    console.error("Error fetching campaign events:", e);
    return [];
  }
};

export const deleteCampaign = async (campaignId) => {
  try {
    await deleteDoc(doc(db, "marketing_campaigns", campaignId));
    return true;
  } catch (error) {
    console.error("Error deleting campaign:", error);
    throw error;
  }
};

const deepClean = (obj) => {
  return JSON.parse(JSON.stringify(obj, (key, value) => {
    return value === undefined ? null : value;
  }));
};

export const saveCampaignDraft = async (campaignData, existingId = null) => {
  try {
    const cleanedData = deepClean(campaignData);
    if (existingId) {
       const docRef = doc(db, "marketing_campaigns", existingId);
       await updateDoc(docRef, {
         ...cleanedData,
         updatedAt: serverTimestamp()
       });
       return existingId;
    } else {
       const docRef = await addDoc(collection(db, "marketing_campaigns"), {
         ...cleanedData,
         status: 'draft',
         createdAt: serverTimestamp(),
         updatedAt: serverTimestamp()
       });
       return docRef.id;
    }
  } catch (e) {
    console.error("Error saving campaign draft:", e);
    throw e;
  }
};

export const getCampaignById = async (campaignId) => {
  try {
    const docRef = doc(db, "marketing_campaigns", campaignId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() };
  } catch (e) {
    console.error("Error fetching campaign by ID:", e);
    throw e;
  }
};

// --- NUEVO: Gestión de Plantillas ---
export const fetchMarketingTemplates = async () => {
  try {
    const data = await apiCall('/api/campaigns/hub-templates', 'GET');
    return Array.isArray(data?.templates) ? data.templates : [];
  } catch (e) {
    console.error("Error fetching templates:", e);
    return [];
  }
};

export const saveMarketingTemplate = async (templateData, templateId = null) => {
  try {
    const cleanedData = deepClean(templateData);
    if (templateId) {
      const docRef = doc(db, "marketing_templates", templateId);
      await updateDoc(docRef, { ...cleanedData, updatedAt: serverTimestamp() });
      return templateId;
    } else {
      const docRef = await addDoc(collection(db, "marketing_templates"), {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    }
  } catch (e) {
    console.error("Error saving template:", e);
    throw e;
  }
};

export const deleteMarketingTemplate = async (templateId) => {
  try {
    await deleteDoc(doc(db, "marketing_templates", templateId));
    return true;
  } catch (e) {
    console.error("Error deleting template:", e);
    throw e;
  }
};

export const instantiateCampaignFromTemplate = async (templateId) => {
  try {
    const templateRef = doc(db, "marketing_templates", templateId);
    const templateSnap = await getDoc(templateRef);
    if (!templateSnap.exists()) throw new Error("Plantilla no existe");
    
    const templateData = templateSnap.data();
    // Creamos una copia como borrador, quitando metadatos de la plantilla
    const { createdAt, updatedAt, ...campaignData } = templateData;
    
    const newCampaignId = await saveCampaignDraft({
      ...campaignData,
      title: `${campaignData.title} (Copia)`,
    });
    
    return newCampaignId;
  } catch (e) {
    console.error("Error instantiating campaign:", e);
    throw e;
  }
};


export const fetchLeads = async (executiveEmail = "", status = "", role = "") => {
  const email = executiveEmail.toLowerCase().trim();
  const queryParams = new URLSearchParams({
    executiveEmail: email,
    status: status,
    role: role
  });
  return await apiCall(`/api/crm/leads?${queryParams.toString()}`, 'GET');
};

export const syncLeads = async (executiveEmail, opts = {}) => {
  return await apiCall('/api/crm/sync-leads', 'POST', {
    executiveEmail,
    ...opts
  });
};

/** Reabastece base fría en segundo plano (no bloquea el UI). Emite `crm-cold-base-replenished` al terminar. */
const _replenishTimers = {};
export const queueColdBaseReplenish = (executiveEmail) => {
  const email = String(executiveEmail || '').toLowerCase().trim();
  if (!email) return Promise.resolve(null);

  // Debounce: varias tipificaciones seguidas = un solo sync fast
  if (_replenishTimers[email]) clearTimeout(_replenishTimers[email]);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('crm-cold-base-replenish-started', {
      detail: { executiveEmail: email }
    }));
  }

  return new Promise((resolve) => {
    _replenishTimers[email] = setTimeout(() => {
      delete _replenishTimers[email];
      // fastMode: DNS+MX, sin HTTP/GWS ni índice completo de CRM → segundos, no minutos
      syncLeads(email, { fastMode: true, maxAdd: 5 })
        .then((result) => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('crm-cold-base-replenished', {
              detail: { executiveEmail: email, result }
            }));
          }
          resolve(result);
        })
        .catch((e) => {
          console.error('Error reabasteciendo base fría:', e);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('crm-cold-base-replenished', {
              detail: { executiveEmail: email, result: null, error: e.message }
            }));
          }
          resolve(null);
        });
    }, 800);
  });
};

// ==========================================
// 📊 GESTIÓN DE KPIS DIARIOS (NUEVO)
// ==========================================
export const saveDailyMetrics = async (email, metricsData, overrideDate = null) => {
  return await apiCall('/api/crm/daily-metrics', 'POST', { 
    email, 
    metricsData, 
    date: overrideDate // we will modify the backend to accept overrideDate
  });
};

export const getDailyMetrics = async (email, dateString) => {
  return await apiCall(`/api/crm/daily-metrics?email=${email}&date=${dateString}`, 'GET');
};

export const getDailyMetricsRange = async (email, startDate, endDate) => {
  const emailParam = email ? `email=${email}&` : '';
  return await apiCall(`/api/crm/daily-metrics?${emailParam}startDate=${startDate}&endDate=${endDate}`, 'GET');
};

export const scheduleImplementationAppointment = async (contractId, appointmentData) => {
  return await apiCall(`/api/crm/contracts/${contractId}/implementation-appointment`, 'POST', appointmentData);
};

export const createOpportunity = async (id, oppData) => {
  return await apiCall(`/api/crm/leads/${id}/opportunity`, 'POST', oppData);
};

/** Enlaces públicos para adjuntos de cita (presentación, casos de éxito). */
export const fetchMeetingDocLinks = async () => {
  try {
    const res = await apiCall('/api/crm/meeting-doc-links', 'GET');
    return res.links || res;
  } catch (e) {
    console.warn('[CRM] meeting-doc-links fallback:', e.message);
    const { getDefaultMeetingDocLinks } = await import('../utils/meetingDocs');
    return getDefaultMeetingDocLinks();
  }
};

export const scheduleAppointment = async (id, appointmentData) => {
  try {
    const res = await apiCall(`/api/crm/leads/${id}/appointment`, 'POST', appointmentData);
    
    // Si es exitoso, asegurarnos de registrar el primer contacto si no existe
    const leadRef = doc(db, "crm_leads", id);
    const leadSnap = await getDoc(leadRef);
    if (leadSnap.exists()) {
      const currentData = leadSnap.data();
      if (!currentData.firstContactDate) {
        await updateDoc(leadRef, {
          firstContactDate: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    }
    return res;
  } catch (e) {
    console.error("Error scheduling appointment:", e);
    throw e;
  }
};

export const importLeadsCSV = async (leads, importedBy) => {
  return await apiCall('/api/crm/import-csv', 'POST', { leads, importedBy });
};

export const lookupDomainOffer = async (domain) => {
  const clean = String(domain || '').trim();
  if (!clean) return { emailService: 'None', suggestedOfferKind: 'completo' };
  return apiCall(`/api/crm/domain-offer?domain=${encodeURIComponent(clean)}`, 'GET');
};

export const createLead = async (leadData) => {
  const response = await fetch(`${API_BASE_URL}/api/crm/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(leadData)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `Error del servidor: ${response.status}`);
    if (data.code) err.code = data.code;
    if (data.companyName) err.companyName = data.companyName;
    if (data.contractId) err.contractId = data.contractId;
    if (data.domain) err.domain = data.domain;
    if (data.pipelineStage) err.pipelineStage = data.pipelineStage;
    if (data.razonSocial) err.razonSocial = data.razonSocial;
    if (data.executiveEmail) err.executiveEmail = data.executiveEmail;
    throw err;
  }
  return data;
};

/** Ejecutivo: solicita reapertura de expediente cerrado + reasignación a sí mismo. */
export const requestContractReopen = async ({
  contractId,
  domain,
  requesterEmail,
  requesterName,
  companyName,
  note
} = {}) => {
  const response = await fetch(`${API_BASE_URL}/api/crm/reopen-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contractId,
      domain,
      requesterEmail,
      requesterName,
      companyName,
      note
    })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error al solicitar reapertura: ${response.status}`);
  }
  return data;
};

/**
 * Dirección: aprueba solicitud pendiente → lead CRM en Base fría + asigna al solicitante.
 * No mueve a Rev. Comercial (ese CTA admin sigue aparte).
 */
export const approveContractReopenRequest = async (contract, options = {}) => {
  if (!contract?.id) throw new Error('Expediente inválido.');
  const req = contract.pendingReopenRequest;
  if (!req || req.status !== 'pending') {
    throw new Error('No hay una solicitud de reapertura pendiente.');
  }

  const data = await apiCall('/api/crm/reopen-requests/approve', 'POST', {
    contractId: contract.id,
    actorName: options.actorName,
    actorEmail: options.actorEmail
  });

  return {
    id: contract.id,
    ...data,
    crmLeadId: data.crmLeadId,
    crmLeadPhase: data.crmLeadPhase ?? 0,
    salesPipelineVisible: data.salesPipelineVisible !== false,
    ejecutivoAsignado: data.ejecutivoAsignado,
    pendingReopenRequest: data.pendingReopenRequest
  };
};

/** Dirección: rechaza solicitud pendiente de reapertura a CRM. */
export const rejectContractReopenRequest = async (contract, options = {}) => {
  if (!contract?.id) throw new Error('Expediente inválido.');
  const req = contract.pendingReopenRequest;
  if (!req || req.status !== 'pending') {
    throw new Error('No hay una solicitud de reapertura pendiente.');
  }

  const data = await apiCall('/api/crm/reopen-requests/reject', 'POST', {
    contractId: contract.id,
    actorName: options.actorName,
    actorEmail: options.actorEmail,
    note: options.note || null
  });

  return {
    id: contract.id,
    ...data,
    pendingReopenRequest: data.pendingReopenRequest
  };
};

/** Auth Bearer para endpoints de alta GWS (rol validado en servidor). */
async function workspaceProvisionFetch(endpoint, body = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para esta acción.');
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `Error del servidor: ${response.status}`);
    err.status = response.status;
    err.payload = data;
    throw err;
  }
  return data;
}

/** Lee un contrato por id (para recuperar estado tras timeout del alta GWS). */
export const getContractById = async (contractId) => {
  if (!contractId) return null;
  const snap = await getDoc(doc(db, 'contratos', contractId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

/** Expediente en contratos o en renovaciones. Legal genera en las dos colecciones. */
export const loadContractSnapshot = async (contractId) => {
  if (!contractId) return null;
  const [renewalSnap, contractSnap] = await Promise.all([
    getDoc(doc(db, 'renewals_metadata', contractId)),
    getDoc(doc(db, 'contratos', contractId))
  ]);
  const renewal = renewalSnap.exists() ? renewalSnap.data() : null;
  const contract = contractSnap.exists() ? contractSnap.data() : null;
  const renewalIsReal = Boolean(
    renewal && (renewal.isRenewal || renewal.renewal_onboarding_completed || renewal.stage)
  );
  if (renewalIsReal) {
    return {
      id: renewalSnap.id,
      ...renewal,
      isRenewal: true,
      contractDocUrl: renewalContractDocUrl(renewal, contract?.contractDocUrl || null)
    };
  }
  if (contract) return { id: contractSnap.id, ...contract };
  if (renewal) return { id: renewalSnap.id, ...renewal, isRenewal: true };
  return null;
};

/** Preview checklist/snapshot de alta GWS (sin crear solicitud). */
export const previewGwsProvisionRequest = async (contractId) => {
  return workspaceProvisionFetch('/api/workspace/provision-requests/preview', { contractId });
};

/** Implementación: solicita alta GWS (congela snapshot del contrato). */
export const requestGwsProvision = async (contractId, { note } = {}) => {
  return workspaceProvisionFetch('/api/workspace/provision-requests', { contractId, note });
};

/** Dirección: aprueba y ejecuta alta (customer + SKUs + primer admin). */
export const approveGwsProvisionRequest = async (contractId) => {
  return workspaceProvisionFetch('/api/workspace/provision-requests/approve', { contractId });
};

/** Dirección: rechaza solicitud pendiente. */
export const rejectGwsProvisionRequest = async (contractId, { note } = {}) => {
  return workspaceProvisionFetch('/api/workspace/provision-requests/reject', { contractId, note });
};

/** Dirección: reintenta pasos pendientes tras fallo parcial. */
export const retryGwsProvisionRequest = async (contractId) => {
  return workspaceProvisionFetch('/api/workspace/provision-requests/retry', { contractId });
};

/** Quita administrador@{dominio} al cerrar la implementación. */
export const removeGwsProvisionAdmin = async (contractId) => {
  return workspaceProvisionFetch('/api/workspace/provision-requests/remove-admin', { contractId });
};

/** Restaura un lead en Baja Comercial a Pendientes (solo gerencia / dirección en servidor). */
export const restoreCommercialBaja = async (leadId, { requesterRole, requesterEmail, requesterName } = {}) => {
  const response = await fetch(`${API_BASE_URL}/api/crm/leads/${leadId}/restore-baja`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ requesterRole, requesterEmail, requesterName })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error al restaurar: ${response.status}`);
  }
  return data;
};

export const getLeadById = async (leadId) => {
  try {
    const docRef = doc(db, "crm_leads", leadId);
    let docSnap = await getDoc(docRef);
    const capHistory = (raw) => {
      const data = { ...raw };
      const hist = Array.isArray(data.history) ? data.history : [];
      if (hist.length > 80) {
        data.history = hist.slice(-80);
        data._historyTruncated = hist.length;
      }
      return data;
    };
    if (!docSnap.exists()) {
      const renewalRef = doc(db, "renewals_metadata", leadId);
      docSnap = await getDoc(renewalRef);
      if (!docSnap.exists()) {
        const contractRef = doc(db, "contratos", leadId);
        docSnap = await getDoc(contractRef);
        if (!docSnap.exists()) return null;
        return capHistory({ id: docSnap.id, ...docSnap.data(), isContract: true });
      }
      return capHistory({ id: docSnap.id, ...docSnap.data(), isRenewal: true });
    }
    return capHistory({ id: docSnap.id, ...docSnap.data() });
  } catch (e) {
    console.error("Error fetching lead by ID:", e);
    throw e;
  }
};

export const deleteLead = async (leadId) => {
  try {
    const leadRef = doc(db, "crm_leads", leadId);
    const leadSnap = await getDoc(leadRef);
    const wasColdBase = leadSnap.exists() && (Number(leadSnap.data().phase) || 0) === 0;
    const executiveEmail = leadSnap.exists()
      ? (leadSnap.data().executiveEmail || '').toLowerCase().trim()
      : null;

    await updateDoc(leadRef, {
      masterStatus: 'ELIMINADO',
      deletedAt: serverTimestamp()
    });

    if (wasColdBase && executiveEmail) {
      queueColdBaseReplenish(executiveEmail);
      return { success: true, replenishQueued: true, replenishExecutiveEmail: executiveEmail };
    }

    return { success: true, replenishQueued: false };
  } catch (e) {
    console.error("Error deleting lead:", e);
    throw e;
  }
};

export const getLeadByShortId = async (shortId) => {
  try {
    const raw = String(shortId || '').trim();
    const candidates = [...new Set([raw, raw.toLowerCase(), raw.toUpperCase()].filter(Boolean))];
    for (const code of candidates) {
      const found = await getLeadByShortIdExact(code);
      if (found) return found;
    }
    return null;
  } catch (e) {
    console.error("Error fetching lead by short ID:", e);
    return null;
  }
};

const getLeadByShortIdExact = async (shortId) => {
  try {
    let q = query(collection(db, "crm_leads"), where("shortId", "==", shortId));
    let querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      q = query(collection(db, "renewals_metadata"), where("shortId", "==", shortId));
      querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        q = query(collection(db, "contratos"), where("shortId", "==", shortId));
        querySnapshot = await getDocs(q);
        if (querySnapshot.empty) return null;
        const docData = querySnapshot.docs[0];
        return { id: docData.id, ...docData.data(), isContract: true };
      }
      const docData = querySnapshot.docs[0];
      return { id: docData.id, ...docData.data(), isRenewal: true };
    }
    const docData = querySnapshot.docs[0];
    return { id: docData.id, ...docData.data() };
  } catch (e) {
    console.error("Error fetching lead by short ID:", e);
    return null;
  }
};

export const updateLeadShortId = async (leadId, shortId) => {
  try {
    const leadRef = doc(db, "crm_leads", leadId);
    let snap = await getDoc(leadRef);
    if (snap.exists()) {
      await updateDoc(leadRef, { shortId });
    } else {
      const renRef = doc(db, "renewals_metadata", leadId);
      snap = await getDoc(renRef);
      if (snap.exists()) {
        await updateDoc(renRef, { shortId });
      } else {
        const contractRef = doc(db, "contratos", leadId);
        snap = await getDoc(contractRef);
        if (snap.exists()) {
          await updateDoc(contractRef, { shortId });
        }
      }
    }
    return true;
  } catch (e) {
    console.error("Error updating lead short ID:", e);
    return false;
  }
};

// ==========================================
// 📧 FUNCIONES DE CORREO (CLIENTES Y RH)
// ==========================================

// Enviar invitación por correo a cliente (Ventas)
export const sendClientInvitation = async (email, name, link, executiveName, executiveEmail, domain, movementType = 'Nuevo Cliente') => {
  return await apiCall('/api/onboarding/send-invite-email', 'POST', {
    clientEmail: email,
    clientName: name,
    link: link,
    executiveName: executiveName,
    executiveEmail: executiveEmail,
    domain: domain,
    type: movementType
  });
};

// Enviar Propuesta a Cliente
export const sendClientProposal = async (data) => {
  return await apiCall('/api/crm/send-proposal', 'POST', data);
};

// Registrar apertura/vista del Micrositio
export const recordProposalView = async (leadId) => {
  if (!leadId) return;
  try {
    const leadRef = doc(db, 'crm_leads', leadId);
    const snap = await getDoc(leadRef);
    if (snap.exists()) {
      await updateDoc(leadRef, {
        'proposal.views': increment(1),
        'proposal.lastViewedAt': serverTimestamp()
      });
    } else {
      const renRef = doc(db, 'renewals_metadata', leadId);
      await updateDoc(renRef, {
        'proposal.views': increment(1),
        'proposal.lastViewedAt': serverTimestamp()
      });
    }
  } catch(e) { 
    console.error('Error recording view', e); 
  }
};

// NUEVA: Enviar correo a Empleado (RH)
export const sendEmployeeInvitation = async (email, name, link, createdBy) => {
  return await apiCall('/api/onboarding/send-employee-email', 'POST', {
    email: email,
    name: name,
    link: link,
    createdBy: createdBy
  });
};

// ==========================================
// 1. GESTIÓN DE PRECIOS Y CATÁLOGO
// ==========================================
let priceCatalogCache = null;
let priceCatalogCacheAt = 0;
const PRICE_CATALOG_TTL_MS = 60 * 1000;

export const invalidatePriceCatalogCache = () => {
  priceCatalogCache = null;
  priceCatalogCacheAt = 0;
};

export const getPricesFromDB = async () => {
  if (priceCatalogCache && (Date.now() - priceCatalogCacheAt) < PRICE_CATALOG_TTL_MS) {
    return priceCatalogCache;
  }
  const querySnapshot = await getDocs(collection(db, "precios"));
  const prices = {};
  querySnapshot.forEach((docSnap) => {
    prices[docSnap.id] = docSnap.data();
  });
  priceCatalogCache = prices;
  priceCatalogCacheAt = Date.now();
  return prices;
};

export const addPriceToDB = async (skuName, priceData, featuresList = []) => {
  invalidatePriceCatalogCache();
  await setDoc(doc(db, "precios", skuName), {
    prices: priceData,
    features: featuresList
  });
};

export const updateSkuFeatures = async (skuName, featuresList) => {
  invalidatePriceCatalogCache();
  const priceRef = doc(db, "precios", skuName);
  await updateDoc(priceRef, { features: featuresList });
};

export const deletePriceFromDB = async (skuName) => {
  invalidatePriceCatalogCache();
  await deleteDoc(doc(db, "precios", skuName));
};

/** Vista previa PDF de orden de compra a Westcon (Gemini Enterprise Standard/Plus). */
export const previewPurchaseOrderPdf = async (contract, form = {}) => {
  const base = process.env.REACT_APP_API_URL || '';
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 45000) : null;
  let response;
  try {
    response = await fetch(`${base}/api/ordenes-compra/preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'X-PO-Client-Version': '2'
      },
      cache: 'no-store',
      signal: controller?.signal,
      body: JSON.stringify({ contract, form })
    });
  } catch (e) {
    if (e?.name === 'AbortError') {
      throw new Error('La vista previa tardó demasiado. Revisa que el API (:8080) esté corriendo y reinícialo.');
    }
    throw e;
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Error ${response.status} al generar PDF`);
  }
  const version = response.headers.get('X-PO-Template-Version');
  if (version && version !== '2') {
    console.warn('[PO] Backend con plantilla antigua (v' + version + '). Reinicia el servidor local.');
  }
  const blob = await response.blob();
  return {
    blob,
    folio: response.headers.get('X-PO-Folio'),
    pdfUrl: response.headers.get('X-PO-Pdf-Url'),
    pdfPreviewUrl: response.headers.get('X-PO-Pdf-Preview-Url')
  };
};

/** Lee una orden de compra guardada (borrador o enviada). */
export const fetchPurchaseOrderRecord = async (orderId) => {
  if (!orderId) return null;
  const ref = doc(db, 'ordenes_compra', orderId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

/** Envía orden de compra por correo (cuenta del ejecutivo) y guarda registro en Firestore. */
export const sendPurchaseOrder = async (contract, form = {}) => {
  const base = process.env.REACT_APP_API_URL || '';
  const response = await fetch(`${base}/api/ordenes-compra/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contract, form })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data.error || `Error ${response.status} al enviar OC`;
    if (data.savedButNotEmailed && data.folio) {
      throw new Error(`${detail} (folio ${data.folio} quedó guardado; puedes reenviar).`);
    }
    throw new Error(detail);
  }
  return data;
};

// ==========================================
// 2. GESTIÓN DE CONTRATOS
// ==========================================

const normalizeEmail = (email) => (email || '').toString().trim().toLowerCase();

const ejecutivoFromEmailFields = (email, name, id) => {
  const normalized = normalizeEmail(email);
  if (!normalized || normalized === 'pendiente@plexarian.com') return null;
  const displayName = (name || '').trim();
  return {
    email: normalized,
    name: displayName && displayName !== 'N/A (Asignación Pendiente)' ? displayName : normalized,
    id: id && id !== 'unknown' ? id : normalized
  };
};

/** Fecha de alta del expediente (compatibilidad fecha_creacion / SkillSync / restore). */
export const resolveContractCreationDate = (contract = {}) => {
  const raw = contract.fechaCreacion
    || contract.fecha_creacion
    || contract.ss_creationTime
    || contract._dataRestoredAt
    || contract.lastUpdate;
  if (!raw) return null;
  if (typeof raw === 'object' && raw.seconds != null) {
    return new Date(raw.seconds * 1000);
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Moneda comercial unificada: propuesta CRM gana sobre anexo/contrato legacy. */
export const resolveContractCurrency = (contract = {}) => {
  const raw = contract.proposal?.currency
    || contract.proposal?.anexo?.currency
    || contract.currency
    || contract.anexo?.currency
    || contract.meetingData?.currency
    || contract.opportunity?.currency
    || 'MXN';
  return raw === 'USD' ? 'USD' : 'MXN';
};

/** Precios por SKU sincronizados desde Por Cobrar (clave SAT u otro id). */
export const getCustomPricesMap = (contract = {}) => {
  const raw = contract?.custom_prices || contract?.por_cobrar_custom_prices || {};
  return raw && typeof raw === 'object' ? raw : {};
};

/**
 * Precio unitario mensual de una línea:
 * catálogo Nexus (si el SKU SAT mapea a Starter/Standard/…) → precio guardado saneado → custom_prices.
 * Evita usar el anual de Por Cobrar como mensual (6048 × 12 ≈ 72 mil / 1,680 × 3 × 12 ≈ 60 mil).
 */
export const resolveLicenseUnitPrice = (lic, contract = {}, priceList = {}, options = {}) => {
  const priceType = options.priceType === 'flex' ? 'flex' : 'commit';
  const otherType = priceType === 'flex' ? 'commit' : 'flex';
  const custom = getCustomPricesMap(contract)[lic?.sku] || {};
  const catalogUnit = catalogMonthlyUnit(lic, priceList, custom, priceType);
  const otherCatalogUnit = catalogMonthlyUnit(lic, priceList, custom, otherType);
  let raw = Number(lic?.precioUnitario) || 0;
  if (raw <= 0 && lic?.sku && Array.isArray(contract?.licencias)) {
    const match = contract.licencias.find((l) => l?.sku === lic.sku);
    raw = Number(match?.precioUnitario) || 0;
  }
  if (raw <= 0 && Number(custom.price) > 0) raw = Number(custom.price);
  if (raw <= 0) {
    const skuData = priceList[lic?.sku];
    raw = Number(skuData?.prices?.[priceType]) || 0;
  }
  return sanitizeLicenseUnitPrice(raw, catalogUnit, lic?.qty, otherCatalogUnit);
};

/** Sella precioUnitario y nombre comercial en cada línea (micrositio/PDF). */
export const stampPrecioUnitarioIntoProposal = (proposal, priceSource = {}, priceList = {}) => {
  if (!proposal || typeof proposal !== 'object') return proposal;
  const next = JSON.parse(JSON.stringify(proposal));
  const stampLines = (licencias = [], vigenciaLabel) => {
    const priceType = catalogPriceTypeFromLabel(vigenciaLabel);
    return (licencias || []).map((lic) => {
      const unit = resolveLicenseUnitPrice(lic, priceSource, priceList, { priceType });
      const custom = getCustomPricesMap(priceSource)[lic?.sku] || {};
      const skuLabel = displayLicenseLabel(lic, priceList, custom);
      const catalogKey = matchCatalogSkuKey(lic, priceList, custom);
      return {
        ...lic,
        ...(unit > 0 ? { precioUnitario: unit } : {}),
        ...(skuLabel ? { skuLabel } : {}),
        ...(catalogKey ? { catalogSku: catalogKey } : {})
      };
    });
  };
  if (Array.isArray(next.opciones) && next.opciones.length > 0) {
    const active = resolveActiveProposalOption(next);
    next.opciones = next.opciones.map((opt) => {
      const isActive = opt === active
        || (active && opt.id != null && opt.id === active.id);
      let lines = opt.licencias;
      if (!(Array.isArray(lines) && lines.length) && isActive && Array.isArray(priceSource.licencias) && priceSource.licencias.length) {
        lines = priceSource.licencias;
      }
      return {
        ...opt,
        licencias: stampLines(lines, opt.vigenciaLabel || next.vigenciaLabel)
      };
    });
  }
  if (Array.isArray(next.licencias) && next.licencias.length > 0) {
    next.licencias = stampLines(next.licencias, next.vigenciaLabel);
  }
  return next;
};

/** Etiqueta legible del SKU (catálogo / descripción PC; nunca la clave SAT sola). */
export const resolveLicenseLabel = (lic, contract = {}, priceList = {}) => {
  const custom = getCustomPricesMap(contract)[lic?.sku] || {};
  return displayLicenseLabel(lic, priceList, custom);
};

const licenseMonthlyFromCatalog = (contract = {}, priceList = {}) => {
  const anexo = contract?.anexo || {};
  const active = resolveActiveProposalOption(contract.proposal);
  const vigenciaLabel = anexo.vigenciaLabel || active?.vigenciaLabel || contract.proposal?.vigenciaLabel || 'Flexible';
  const { disableLicenseDiscount, priceType } = resolveTermDiscountPolicy(vigenciaLabel);
  let total = 0;
  resolveCommercialLicencias(contract).forEach((lic) => {
    const unit = resolveLicenseUnitPrice(lic, contract, priceList, { priceType });
    const desc = disableLicenseDiscount ? 0 : (lic.descuento || 0);
    total += unit * (1 - desc / 100) * (Number(lic.qty) || 0);
  });
  return total;
};

const oneTimeServicesTotal = (contract = {}) => {
  let total = 0;
  (resolveServiciosFromProposal(contract.proposal, contract.serviciosAdicionales) || []).forEach((serv) => {
    const unit = Number(serv.price) || 0;
    const desc = serv.descuento || 0;
    total += unit * (1 - desc / 100) * (Number(serv.qty) || 1);
  });
  return total;
};

/** Cuota mensual recurrente desde licencias + catálogo (ignora totales anexo inflados). */
export const getContractMonthlyAmount = (contract = {}, priceList = {}) => {
  const live = licenseMonthlyFromCatalog(contract, priceList);
  if (live > 0) return live;
  const anexo = contract?.anexo || {};
  const proposal = contract?.proposal || {};
  if (Number(anexo.totalMensual) > 0) return Number(anexo.totalMensual);
  if (Number(proposal.totalMensual) > 0) return Number(proposal.totalMensual);
  const freq = resolveDisplayBillingFrequency(contract);
  const mult = BILLING_FREQ_MULTIPLIERS[freq] || 1;
  if (mult > 1 && Number(anexo.totalContrato) > 0) return Number(anexo.totalContrato) / mult;
  if (mult > 1 && Number(proposal.totalContrato) > 0) return Number(proposal.totalContrato) / mult;
  return 0;
};

/** Monto a mostrar en tarjeta Kanban / totales según frecuencia de facturación. */
export const getContractDisplayAmount = (contract = {}, priceList = {}) => {
  const monthly = licenseMonthlyFromCatalog(contract, priceList);
  const oneTime = oneTimeServicesTotal(contract);
  const freq = resolveDisplayBillingFrequency(contract);
  const mult = BILLING_FREQ_MULTIPLIERS[freq] || 1;
  if (monthly > 0 || oneTime > 0) return monthly * mult + oneTime;

  const anexo = contract?.anexo || {};
  const proposal = contract?.proposal || {};
  if (freq === 'Mensual' && Number(anexo.totalMensual) > 0) return Number(anexo.totalMensual);
  if (Number(anexo.totalContrato) > 0) return Number(anexo.totalContrato);
  if (freq === 'Mensual' && Number(proposal.totalMensual) > 0) return Number(proposal.totalMensual);
  if (Number(proposal.totalContrato) > 0) return Number(proposal.totalContrato);
  return 0;
};

export { BILLING_FREQ_MULTIPLIERS, resolveDisplayBillingFrequency };

/** Oculta expediente operativo VERIFIED si el mismo dominio ya está en renovación/firma activa. */
export const isActiveRenewalInPipeline = (renewal = {}) => {
  if (renewal.facturado === true) return false;
  const stage = renewal.stage;
  if (['LEGAL', 'FIRMA', 'IMPLEMENTACION', 'CIERRE'].includes(stage)) return true;
  const ps = renewal.pipelineStage;
  if (ps && ps !== 'CLOSED' && ['SALES_REVIEW', 'LEGAL_REVIEW', 'SIGNING', 'IMPLEMENTATION', 'BILLING'].includes(ps)) {
    return true;
  }
  return false;
};

export const filterContratosHiddenByActiveRenewal = (contratosList = [], renewalsList = []) => {
  const activeDomains = new Set(
    renewalsList
      .filter(isActiveRenewalInPipeline)
      .map((r) => (r.dominio || r.domain || '').trim().toLowerCase())
      .filter(Boolean)
  );
  if (activeDomains.size === 0) return contratosList;
  return contratosList.filter((c) => {
    if (c.isRenewal) return true;
    const d = (c.dominio || c.domain || '').trim().toLowerCase();
    if (!d || !activeDomains.has(d)) return true;
    // Mientras hay renovación activa, no duplicar el expediente operativo en el tablero.
    return false;
  });
};

/** Ejecutivo asignado válido (no placeholder ni vacío). */
export const isEjecutivoAsignadoValid = (ej) => {
  if (!ej || typeof ej !== 'object') return false;
  const email = normalizeEmail(ej.email);
  const name = (ej.name || '').trim();
  if (!email || email === 'pendiente@plexarian.com') return false;
  if (!name || name === 'N/A (Asignación Pendiente)' || name === 'Sin ejecutivo') return false;
  return true;
};

/**
 * Resuelve ejecutivo con id de team_members (CRM, propuesta, creador de invitación, etc.).
 */
export const resolveEjecutivoAsignado = (lead = {}, overrides = {}, executivesList = []) => {
  const proposal = overrides.proposal ?? lead.proposal;
  const candidates = [
    overrides.ejecutivoAsignado,
    lead.ejecutivoAsignado,
    ejecutivoFromEmailFields(
      overrides.creatorEmail ?? lead.creatorEmail ?? (typeof lead.createdBy === 'string' ? lead.createdBy : lead.createdBy?.email),
      overrides.creatorName ?? lead.creatorName ?? lead.createdBy?.name,
      overrides.createdBy ?? (typeof lead.createdBy === 'string' ? lead.createdBy : lead.createdBy?.id)
    ),
    ejecutivoFromEmailFields(
      overrides.executiveEmail ?? lead.executiveEmail,
      overrides.executiveName ?? lead.executiveName,
      overrides.executiveId ?? lead.executiveId
    ),
    ejecutivoFromEmailFields(
      proposal?.executiveEmail,
      proposal?.executiveName,
      proposal?.executiveId
    )
  ].filter(Boolean);

  for (const raw of candidates) {
    const email = normalizeEmail(raw.email);
    if (!email) continue;
    if (email === 'pendiente@plexarian.com' || email === 'unknown') continue;
    const fromTeam = executivesList.find((ex) => normalizeEmail(ex.email) === email);
    if (fromTeam) {
      return {
        id: fromTeam.id,
        email: fromTeam.email || email,
        name: fromTeam.name || raw.name || email
      };
    }
    if (raw.id && raw.id !== 'unknown') {
      return { id: raw.id, email, name: raw.name || email };
    }
    return { id: email, email, name: raw.name || email };
  }
  return null;
};

/** Resuelve ejecutivo para contrato (nunca devuelve placeholder pendiente). */
export const pickEjecutivoForContract = (lead = {}, overrides = {}, executivesList = []) => {
  const candidates = [
    overrides.ejecutivoAsignado,
    lead.ejecutivoAsignado,
    resolveEjecutivoAsignado(lead, overrides, executivesList),
    resolveEjecutivoAsignado({}, overrides, executivesList),
    ejecutivoFromEmailFields(
      overrides.executiveEmail ?? lead.executiveEmail,
      overrides.executiveName ?? lead.executiveName,
      overrides.executiveId ?? lead.executiveId
    ),
    ejecutivoFromEmailFields(
      overrides.creatorEmail ?? lead.creatorEmail,
      overrides.creatorName ?? lead.creatorName,
      overrides.createdBy ?? lead.createdBy
    )
  ];
  for (const c of candidates) {
    if (isEjecutivoAsignadoValid(c)) return c;
  }
  return null;
};

/** Invitación de onboarding vinculada por dominio (ejecutivo que envió el link). */
export const getInvitationContextByDomain = async (domain) => {
  const d = (domain || '').trim();
  if (!d) return null;
  try {
    for (const col of ['invitations', 'onboarding_invitations']) {
      const q = query(collection(db, col), where('domain', '==', d), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
    }
  } catch (e) {
    console.warn('getInvitationContextByDomain:', e);
  }
  return null;
};

/** Invitación por ID de token (= mismo id que contratos cuando viene del Dashboard). */
export const getInvitationContextForContract = async (contract = {}) => {
  const tokenId = contract.id;
  if (tokenId) {
    try {
      for (const col of ['invitations', 'onboarding_invitations']) {
        const snap = await getDoc(doc(db, col, tokenId));
        if (snap.exists()) return { id: snap.id, ...snap.data() };
      }
    } catch (e) {
      console.warn('getInvitationContextForContract by id:', e);
    }
  }
  return getInvitationContextByDomain(contract.dominio || contract.domain);
};

/**
 * Al enviar invitación desde Dashboard Principal: crea/actualiza contratos/{tokenId}
 * con el ejecutivo que manda el link (sin depender del CRM).
 */
export const ensureDashboardContractFromInvitation = async (inviteData, tokenId) => {
  if (!tokenId) throw new Error('tokenId requerido para vincular invitación al Dashboard');
  const execs = await getExecutives().catch(() => []);
  const domain = (inviteData.domain || '').trim();
  const companyName = inviteData.clientName || domain || 'Cliente';
  const existingSnap = await getDoc(doc(db, 'contratos', tokenId));
  const existing = existingSnap.exists() ? existingSnap.data() : {};
  const resolvedEj = pickEjecutivoForContract({}, inviteData, execs)
    || pickEjecutivoForContract(existing, inviteData, execs);
  const ejecutivoAsignado = isEjecutivoAsignadoValid(resolvedEj)
    ? resolvedEj
    : (isEjecutivoAsignadoValid(existing.ejecutivoAsignado) ? existing.ejecutivoAsignado : null);

  // El token de onboarding es el mismo id que el contrato. Reenviar NO debe
  // devolver a Rev. Com. ni pisar anexo/propuesta de un expediente ya avanzado.
  const payload = existingSnap.exists()
    ? omitUndefinedDeep({
      clientEmail: inviteData.clientEmail || existing.clientEmail || '',
      movementType: inviteData.movementType || existing.movementType,
      creatorEmail: inviteData.creatorEmail || existing.creatorEmail,
      creatorName: inviteData.creatorName || existing.creatorName,
      createdBy: inviteData.createdBy || existing.createdBy,
      ejecutivoAsignado,
      salesPipelineVisible: true
    })
    : omitUndefinedDeep({
      clientName: companyName,
      companyName,
      nombre_comercial: companyName,
      razon_social: companyName,
      dominio: domain,
      domain,
      clientEmail: inviteData.clientEmail || '',
      movementType: inviteData.movementType || 'Nuevo Cliente',
      creatorEmail: inviteData.creatorEmail,
      creatorName: inviteData.creatorName,
      createdBy: inviteData.createdBy,
      ejecutivoAsignado,
      pipelineStage: 'SALES_REVIEW',
      salesStatus: 'PENDING',
      legalStatus: 'PENDING',
      estado: 'Negociación',
      salesPipelineVisible: true,
      fechaCreacion: new Date().toISOString(),
      anexo: DEFAULT_CONTRACT_ANEXO
    });

  await setDoc(doc(db, 'contratos', tokenId), payload, { merge: true });
  return { tokenId, ejecutivoAsignado };
};

/** ¿El contrato pertenece al ejecutivo logueado? (id team_members o email). */
export const contractBelongsToExecutive = (contract, executiveId, executiveEmail) => {
  const ej = contract?.ejecutivoAsignado;
  const ejEmail = normalizeEmail(ej?.email);
  const targetEmail = normalizeEmail(executiveEmail);
  if (ej) {
    if (executiveId && ej.id === executiveId) return true;
    if (targetEmail && ejEmail === targetEmail) return true;
  }
  const proposalEmail = normalizeEmail(
    contract?.executiveEmail || contract?.proposal?.executiveEmail || contract?.creatorEmail
  );
  if (targetEmail && proposalEmail === targetEmail) return true;
  return false;
};

/** Oculta contrato del tablero solo si el lead CRM está en baja comercial. */
export const hideLinkedContractForCrmLead = async (lead = {}) => {
  const domain = (lead.domain || '').trim().toLowerCase();
  if (!domain || lead.isContract) return false;
  const existing = await fetchContractByDomain(domain);
  if (!existing?.id) return false;

  const leadWithId = { ...lead, id: lead.id };
  const isBaja = isCrmLeadCommercialBaja(leadWithId);

  if (!isBaja) return false;
  // Un expediente con alta completa no sale del tablero por un cambio de semáforo en CRM.
  if (isOnboardingExpedienteComplete(existing)) return false;

  const reason = 'CRM_BAJA_COMERCIAL';
  await updateContractInDB(existing.id, {
    ...buildHideFromSalesPipelinePatch(reason, leadWithId),
    hiddenFromSalesPipelineAt: new Date().toISOString()
  });
  return true;
};

/** Persiste ejecutivo: invitación Dashboard (prioridad) → CRM → propuesta. */
export const backfillEjecutivoForContract = async (contract, executivesList = null) => {
  const execs = executivesList?.length ? executivesList : await getExecutives().catch(() => []);
  const invite = await getInvitationContextForContract(contract);
  const lead = await getCrmLeadForContract(contract);

  if (lead && !shouldLinkCrmLeadToContract(contract, lead)) {
    if (isCrmLeadCommercialBaja(lead) && !isOnboardingExpedienteComplete(contract)) {
      await updateContractInDB(contract.id, {
        ...buildHideFromSalesPipelinePatch('CRM_BAJA_COMERCIAL', { ...lead, id: lead.id }),
        hiddenFromSalesPipelineAt: new Date().toISOString()
      }).catch(() => {});
    }
    if (isEjecutivoAsignadoValid(contract.ejecutivoAsignado)) return contract.ejecutivoAsignado;
    return null;
  }

  const crmExecEmail = normalizeEmail(lead?.executiveEmail);
  const currentEjEmail = normalizeEmail(contract.ejecutivoAsignado?.email);
  const inviteExecEmail = normalizeEmail(
    invite?.ejecutivoAsignado?.email || invite?.creatorEmail
  );
  const hasInviteExecutive = isEjecutivoAsignadoValid(invite?.ejecutivoAsignado) || Boolean(inviteExecEmail);
  const needsReconcile =
    isEjecutivoAsignadoValid(contract.ejecutivoAsignado)
    && crmExecEmail
    && crmExecEmail !== currentEjEmail
    && !hasInviteExecutive;
  if (isEjecutivoAsignadoValid(contract.ejecutivoAsignado) && !needsReconcile) {
    return contract.ejecutivoAsignado;
  }
  const resolved = pickEjecutivoForContract(lead || {}, {
    proposal: contract.proposal || lead?.proposal,
    creatorEmail: invite?.creatorEmail || contract.creatorEmail || lead?.creatorEmail,
    creatorName: invite?.creatorName || contract.creatorName || lead?.creatorName,
    createdBy: invite?.createdBy || contract.createdBy || lead?.createdBy,
    executiveEmail: invite?.creatorEmail || contract.executiveEmail || lead?.executiveEmail
      || contract.proposal?.executiveEmail || lead?.proposal?.executiveEmail,
    executiveName: invite?.creatorName || contract.executiveName || lead?.executiveName
      || contract.proposal?.executiveName,
    ejecutivoAsignado: invite?.ejecutivoAsignado || contract.ejecutivoAsignado || lead?.ejecutivoAsignado
  }, execs);
  if (!resolved) return null;
  await updateContractInDB(contract.id, {
    ejecutivoAsignado: resolved,
    ...(invite?.creatorEmail && !contract.creatorEmail ? { creatorEmail: invite.creatorEmail } : {}),
    ...(invite?.creatorName && !contract.creatorName ? { creatorName: invite.creatorName } : {}),
    ...(lead?.id && !contract.crmLeadId && shouldLinkCrmLeadToContract(contract, lead)
      ? { crmLeadId: lead.id, proposalMicrositeToken: contract.proposalMicrositeToken || lead.id }
      : {}),
    isRenewal: contract.isRenewal
  });
  return resolved;
};

let ejecutivoBackfillTimer = null;
const ejecutivoBackfillInFlight = new Set();
let displayBackfillTimer = null;
let pipelineStageAlignTimer = null;
const pipelineStageAlignInFlight = new Set();
const displayBackfillInFlight = new Set();

/** Repara visibilidad Rev. Com. cuando el CRM ya envió onboarding pero el contrato perdió marcadores. */
export const repairCrmOnboardingPipelineVisibility = async (contract) => {
  if (!contract?.id || contract.isRenewal) return false;
  if (contract.removedFromSalesPipelineReason === 'CRM_BAJA_COMERCIAL') return false;
  if (isCrmLeadCommercialBaja({
    masterStatus: contract.masterStatus || contract.crmLeadMasterStatus,
    phase: contract.phase ?? contract.crmLeadPhase
  })) return false;

  const alreadyVisible = contract.salesPipelineVisible === true && !contract.removedFromSalesPipelineReason;
  if (alreadyVisible) return false;

  const lead = contract.crmLeadId ? await getLeadById(contract.crmLeadId).catch(() => null) : null;
  const onboardingSent = Boolean(
    lead?.onboardingStatus === 'ENVIADA'
    || lead?.onboardingSentAt
    || contract.onboardingStatus === 'ENVIADA'
    || contract.onboardingStatus === 'COMPLETADO'
    || contract.onboardingSentAt
    || contract.onboardingCrmStatus === 'PENDIENTE_CLIENTE'
    || contract.onboardingCrmStatus === 'COMPLETADO'
    || isOnboardingExpedienteComplete(contract)
  );

  if (!onboardingSent) return false;

  const patch = {
    salesPipelineVisible: true,
    removedFromSalesPipelineReason: deleteField(),
    hiddenFromSalesPipelineAt: deleteField(),
    pipelineStage: contract.pipelineStage === 'ONBOARDING' ? 'SALES_REVIEW' : (contract.pipelineStage || 'SALES_REVIEW'),
    onboardingStatus: contract.onboardingStatus || lead?.onboardingStatus || 'ENVIADA',
    onboardingSentAt: contract.onboardingSentAt || lead?.onboardingSentAt || new Date().toISOString(),
    onboardingCrmStatus: contract.onboardingCrmStatus || 'PENDIENTE_CLIENTE',
    clientOrigin: contract.clientOrigin || 'crm_onboarding_invite'
  };
  await updateContractInDB(contract.id, patch);
  return true;
};

/** Escribe la etapa que el tablero antes solo pintaba (firmado con FAD → Implementación). */
const schedulePipelineStageAlign = (contracts = []) => {
  if (pipelineStageAlignTimer) clearTimeout(pipelineStageAlignTimer);
  pipelineStageAlignTimer = setTimeout(async () => {
    const needs = contracts.filter((c) => c?.id && buildPipelineStageAlignPatch(c));
    for (const c of needs) {
      if (pipelineStageAlignInFlight.has(c.id)) continue;
      const patch = buildPipelineStageAlignPatch(c);
      if (!patch) continue;
      pipelineStageAlignInFlight.add(c.id);
      try {
        await updateContractInDB(c.id, { ...patch, isRenewal: Boolean(c.isRenewal) });
      } catch (e) {
        console.warn('[Nexus] No se pudo guardar la etapa:', c.dominio || c.id, e.message);
      } finally {
        pipelineStageAlignInFlight.delete(c.id);
      }
    }
  }, 400);
};
const scheduleContractDisplayBackfill = (contracts = []) => {
  if (displayBackfillTimer) clearTimeout(displayBackfillTimer);
  displayBackfillTimer = setTimeout(async () => {
    const needs = contracts.filter((c) => {
      if (!c.id || c.isRenewal) return false;
      const missingDate = !c.fechaCreacion && Boolean(c.fecha_creacion || c.ss_creationTime);
      const missingTipo = !c.tipo_persona && Boolean(c.acta_url || c.entityType);
      const missingCrmPipeline = c.crmLeadId
        && c.salesPipelineVisible !== true
        && (c.onboardingStatus === 'ENVIADA' || c.onboardingSentAt || c.onboardingCrmStatus === 'PENDIENTE_CLIENTE');
      return missingDate || missingTipo || missingCrmPipeline;
    });
    for (const c of needs.slice(0, 20)) {
      if (displayBackfillInFlight.has(c.id)) continue;
      displayBackfillInFlight.add(c.id);
      try {
        const patch = {};
        const created = resolveContractCreationDate(c);
        if (!c.fechaCreacion && created) patch.fechaCreacion = created.toISOString();
        if (!c.tipo_persona) {
          patch.tipo_persona = c.entityType || (c.acta_url ? 'Moral' : 'Fisica');
        }
        if (Object.keys(patch).length) {
          await updateContractInDB(c.id, patch);
        }
        if (c.crmLeadId && c.salesPipelineVisible !== true) {
          await repairCrmOnboardingPipelineVisibility(c);
        }
      } catch (e) {
        console.warn('[Nexus] Backfill display contrato:', c.dominio || c.id, e.message);
      } finally {
        displayBackfillInFlight.delete(c.id);
      }
    }
  }, 800);
};

const scheduleEjecutivoBackfill = (contracts = []) => {
  if (ejecutivoBackfillTimer) clearTimeout(ejecutivoBackfillTimer);
  ejecutivoBackfillTimer = setTimeout(async () => {
    const needs = contracts.filter((c) => !isEjecutivoAsignadoValid(c.ejecutivoAsignado));
    if (needs.length === 0) return;
    const execs = await getExecutives().catch(() => []);
    for (const c of needs) {
      if (ejecutivoBackfillInFlight.has(c.id)) continue;
      ejecutivoBackfillInFlight.add(c.id);
      try {
        await backfillEjecutivoForContract(c, execs);
      } catch (e) {
        console.warn('[Nexus] No se pudo asignar ejecutivo automático:', c.dominio || c.id, e.message);
      } finally {
        ejecutivoBackfillInFlight.delete(c.id);
      }
    }
  }, 600);
};

/** Valor para select de ejecutivo: matchea por id de team_members o por email. */
export const getExecutiveSelectValue = (ejecutivoAsignado, executivesList = []) => {
  if (!ejecutivoAsignado) return '';
  const email = normalizeEmail(ejecutivoAsignado.email);
  const byId = executivesList.find((ex) => ex.id === ejecutivoAsignado.id);
  if (byId) return byId.id;
  const byEmail = executivesList.find((ex) => normalizeEmail(ex.email) === email);
  if (byEmail) return byEmail.id;
  return ejecutivoAsignado.id || email || '';
};

/** Slug para URLs del micrositio (?mode=proposal&client=...). */
export const slugifyProposalClient = (name) =>
  (name || 'plexarian').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

/** URL pública del micrositio (usa ID del lead CRM, no el del contrato). */
export const getProposalMicrositeUrl = (record = {}, origin) => {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : 'https://nexus.plexarian.com');
  if (record.proposalMicrositePath) {
    return record.proposalMicrositePath.startsWith('http') ? record.proposalMicrositePath : `${base}${record.proposalMicrositePath}`;
  }
  const shortId = record.shortId;
  if (shortId) return `${base}/?p=${shortId}`;
  const token = record.proposalMicrositeToken || record.crmLeadId;
  if (!token) return null;
  const slug = slugifyProposalClient(record.companyName || record.nombre_comercial || record.razon_social || record.clientName);
  return `${base}/?mode=proposal&client=${slug}&token=${token}`;
};

/** URL pública de la guía DNS de implementación (TTL 30 días). */
export const getDnsSetupMicrositeUrl = (record = {}, origin) => {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : 'https://nexus.plexarian.com');
  const token = record.dnsSetupToken || record.dnsSetup?.token;
  if (!token) return null;
  const domain = record.dnsSetup?.domain || record.dominio || record.domain || '';
  const qs = new URLSearchParams({ mode: 'dns-setup', token });
  if (domain) qs.set('domain', String(domain).toLowerCase().trim());
  return `${base}/?${qs.toString()}`;
};

export const isDnsSetupExpired = (dnsSetup) => {
  if (!dnsSetup?.expiresAt) return Boolean(dnsSetup?.token); // sin fecha → tratar como inválido si había token
  return new Date(dnsSetup.expiresAt).getTime() <= Date.now();
};

/** Genera o regenera la guía DNS del contrato (backend resuelve SPF + arma registros). */
export const generateDnsSetupGuide = async (contractId, { force = false } = {}) => {
  return apiCall('/api/dns-setup/generate', 'POST', { contractId, force });
};

export const updateDnsSetupRecords = async (contractId, { verificationTxt, dkimTxt, token } = {}) => {
  const body = { verificationTxt, dkimTxt };
  if (token) body.token = token;
  if (contractId) body.contractId = contractId;
  return apiCall('/api/dns-setup/records', 'PATCH', body);
};

/** Lectura de una guía DNS por token (utilidad / edición). */
export const fetchDnsSetupByToken = async (token) => {
  const t = String(token || '').trim();
  if (!t) throw new Error('Token requerido');
  return apiCall(`/api/dns-setup/${encodeURIComponent(t)}`, 'GET');
};

/** Elimina una guía standalone (Utilidades). */
export const deleteDnsSetupGuide = async (token) => {
  const t = String(token || '').trim();
  if (!t) throw new Error('Token requerido');
  return apiCall(`/api/dns-setup/${encodeURIComponent(t)}`, 'DELETE');
};

/** Guía DNS standalone (Utilidades) — dominio adicional o sin contrato. */
export const generateStandaloneDnsSetup = async (domain, {
  force = false,
  label,
  contractId,
  customerId,
  domainType,
  parentDomain,
  registrationMode = 'manual',
  dkimTxt,
  createdBy,
  skipVerification = false,
} = {}) => {
  const body = {
    domain,
    force,
    label,
    contractId,
    customerId,
    domainType,
    parentDomain,
    registrationMode,
    dkimTxt,
    createdBy,
    skipVerification,
  };
  return registrationMode === 'nexus'
    ? workspaceProvisionFetch('/api/dns-setup/standalone', body)
    : apiCall('/api/dns-setup/standalone', 'POST', body);
};

/** Búsqueda predictiva de clientes por dominio principal (utilidad DNS). */
export const searchDnsSetupClients = async (q, { signal } = {}) => {
  const query = String(q || '').trim();
  if (query.length < 2) return { query, clients: [] };
  return apiCall(`/api/dns-setup/clients?q=${encodeURIComponent(query)}`, 'GET', null, false, signal);
};

/** Dominios del cliente en GWS: principal, secundarios y alias. */
export const fetchClientGwsDomains = async ({ contractId, customerId, domain, signal } = {}) => {
  const params = new URLSearchParams();
  if (contractId) params.set('contractId', contractId);
  if (customerId) params.set('customerId', customerId);
  if (domain) params.set('domain', domain);
  return apiCall(`/api/dns-setup/gws-domains?${params.toString()}`, 'GET', null, false, signal);
};

/** Verificación inmediata del dominio: libre, del cliente o con GWS en otra cuenta. */
export const checkDnsSetupDomain = async ({ domain, contractId, customerId, signal } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para verificar dominios.');
  const params = new URLSearchParams({ domain: String(domain || '').trim() });
  if (contractId) params.set('contractId', contractId);
  if (customerId) params.set('customerId', customerId);

  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/api/dns-setup/domain-check?${params.toString()}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    signal: signal || undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `Error del servidor: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return data;
};

export const listLabSubdomains = async (parent) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para ver labs GWS.');
  const token = await user.getIdToken();
  const qs = parent ? `?parent=${encodeURIComponent(parent)}` : '';
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains${qs}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

export const createLabSubdomain = async ({ parent, label, host } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para crear un lab.');
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ parent, label, host }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

export const provisionLabGws = async (fqdn) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para crear el cliente GWS.');
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains/provision`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fqdn }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

export const deleteLabSubdomain = async (fqdn) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para eliminar un lab.');
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fqdn }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

export const suggestLabHostName = async ({ parent, skip } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para sugerir un lab.');
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains/suggest-host`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ parent, skip }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

export const publishLabVerificationTxt = async ({ fqdn, txt }) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para publicar el TXT.');
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains/verification-txt`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fqdn, txt }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

async function labAuthHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para Labs GWS.');
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export const requestLabSubdomain = async ({ parent, label, host } = {}) => {
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains/requests`, {
    method: 'POST',
    headers: await labAuthHeaders(),
    body: JSON.stringify({ parent, label, host }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

export const approveLabSubdomainRequest = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains/requests/approve`, {
    method: 'POST',
    headers: await labAuthHeaders(),
    body: JSON.stringify({ id }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

export const rejectLabSubdomainRequest = async (id, note) => {
  const response = await fetch(`${API_BASE_URL}/api/lab-subdomains/requests/reject`, {
    method: 'POST',
    headers: await labAuthHeaders(),
    body: JSON.stringify({ id, note }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error del servidor: ${response.status}`);
  }
  return data;
};

/** Campos de propuesta/micrositio que deben copiarse del lead CRM al contrato. */
export const extractProposalDocFieldsFromLead = (lead = {}, overrides = {}) => {
  const proposal = overrides.proposal ?? lead.proposal ?? null;
  const leadId = lead.id || overrides.crmLeadId || '';
  const companyName = overrides.companyName ?? lead.companyName ?? '';
  const shortId = overrides.shortId ?? lead.shortId ?? '';
  const propuesta_url =
    overrides.propuesta_url
    ?? lead.propuesta_url
    ?? proposal?.url
    ?? proposal?.propuesta_url
    ?? (typeof lead.meetingData?.micrositio === 'string' ? lead.meetingData.micrositio : '')
    ?? '';
  const proposalMicrositeToken = leadId || overrides.proposalMicrositeToken || '';
  const proposalMicrositePath = shortId
    ? `/?p=${shortId}`
    : (proposalMicrositeToken
      ? `/?mode=proposal&client=${slugifyProposalClient(companyName)}&token=${proposalMicrositeToken}`
      : '');
  return {
    shortId,
    propuesta_url,
    proposalMicrositeToken,
    proposalMicrositePath,
    crmLeadId: leadId || overrides.crmLeadId
  };
};

const mergeHistories = (existing = [], incoming = []) => {
  const seen = new Set();
  const out = [];
  for (const ev of [...(existing || []), ...(incoming || [])]) {
    const key = `${ev.timestamp || ''}|${ev.action || ev.title || ''}|${ev.description || ev.note || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ev);
  }
  return out.sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
};

/**
 * Arma el payload de contrato (Dashboard) a partir de un lead CRM + overrides del modal.
 */
export const buildContractPayloadFromCrmLead = (lead, overrides = {}, executivesList = []) => {
  const { _executivesCache, ...safeOverrides } = overrides;
  const execList = executivesList.length > 0 ? executivesList : (_executivesCache || []);

  const contactFirstName = safeOverrides.contactFirstName ?? lead.contactFirstName ?? lead.contactData?.name?.split(' ')[0] ?? '';
  const contactLastName = safeOverrides.contactLastName ?? lead.contactLastName ?? lead.contactData?.name?.split(' ').slice(1).join(' ') ?? '';
  const contactEmail = safeOverrides.contactEmail ?? lead.contactEmail ?? lead.contactData?.email ?? '';
  const companyName = safeOverrides.companyName ?? lead.companyName ?? '';
  const phoneNumber = safeOverrides.phoneNumber ?? lead.phoneNumber ?? lead.contactData?.phone ?? '';
  const domain = (safeOverrides.domain ?? lead.domain ?? '').trim().replace(/\.+$/, '');
  const proposal = preferCompleteProposal(lead.proposal, safeOverrides.proposal) || null;
  const ejecutivoAsignado = resolveEjecutivoAsignado(lead, safeOverrides, execList)
    || resolveEjecutivoAsignado({}, {
      creatorEmail: safeOverrides.creatorEmail ?? lead.creatorEmail,
      creatorName: safeOverrides.creatorName ?? lead.creatorName,
      proposal: safeOverrides.proposal ?? lead.proposal
    }, execList);

  const fullName = `${contactFirstName} ${contactLastName}`.trim()
    || lead.contactData?.name
    || lead.clientName
    || 'Contacto';

  const contactos = {
    ...(lead.contactos || {}),
    admin: {
      nombre: contactFirstName || fullName.split(' ')[0],
      apellido: contactLastName || fullName.split(' ').slice(1).join(' '),
      email: contactEmail,
      telefono: phoneNumber,
      role: lead.contactData?.role || lead.contactRole || ''
    },
    contacto1: {
      nombre: fullName,
      correo: contactEmail,
      telefono: phoneNumber
    }
  };

  const anexoFromProposal = proposal ? buildAnexoFromProposal(proposal) : undefined;

  const proposalDocs = extractProposalDocFieldsFromLead(lead, { ...safeOverrides, proposal, companyName });

  const payload = {
    clientName: companyName,
    companyName,
    nombre_comercial: companyName,
    razon_social: companyName,
    clientEmail: contactEmail,
    domain,
    dominio: domain,
    phoneNumber,
    contactEmail,
    contactFirstName,
    contactLastName,
    industry: safeOverrides.industry ?? lead.industry ?? '',
    location: safeOverrides.location ?? lead.location ?? '',
    emailService: safeOverrides.emailService ?? lead.emailService ?? 'None',
    usersInfo: safeOverrides.usersInfo ?? lead.usersInfo ?? lead.companySize ?? '',
    observations: safeOverrides.observations ?? lead.observations ?? '',
    contactComment: safeOverrides.contactComment ?? lead.contactComment ?? '',
    meetingData: safeOverrides.meetingData ?? lead.meetingData ?? null,
    proposal,
    licencias: resolveLicenciasFromProposal(proposal, safeOverrides.licencias ?? lead.licencias ?? []),
    serviciosAdicionales: resolveServiciosFromProposal(proposal, safeOverrides.serviciosAdicionales ?? lead.serviciosAdicionales ?? []),
    anexo: safeOverrides.anexo ?? anexoFromProposal,
    history: safeOverrides.history ?? lead.history ?? [],
    managementNotes: safeOverrides.managementNotes ?? lead.managementNotes ?? [],
    crmLeadId: lead.id || safeOverrides.crmLeadId,
    phase2Result: lead.phase2Result,
    phase3Result: safeOverrides.phase3Result ?? lead.phase3Result,
    contactos,
    contactData: {
      name: fullName,
      phone: phoneNumber,
      email: contactEmail,
      role: lead.contactData?.role || ''
    },
    creatorEmail: lead.creatorEmail || lead.createdBy?.email,
    creatorName: lead.creatorName || lead.createdBy?.name,
    createdBy: lead.createdBy || lead.creatorEmail,
    ...safeOverrides,
    ...proposalDocs,
    proposal,
    licencias: resolveLicenciasFromProposal(proposal, safeOverrides.licencias ?? lead.licencias ?? []),
    serviciosAdicionales: resolveServiciosFromProposal(proposal, safeOverrides.serviciosAdicionales ?? lead.serviciosAdicionales ?? []),
    anexo: anexoFromProposal || safeOverrides.anexo
  };
  if (ejecutivoAsignado) {
    payload.ejecutivoAsignado = ejecutivoAsignado;
  }

  if (safeOverrides.salesPipelineVisible !== undefined) {
    payload.salesPipelineVisible = safeOverrides.salesPipelineVisible;
  } else if (
    lead.onboardingStatus === 'ENVIADA'
    || safeOverrides.onboardingCrmStatus === 'PENDIENTE_CLIENTE'
    || safeOverrides.onboardingSentAt
  ) {
    payload.salesPipelineVisible = true;
  }

  if (isCrmLeadCommercialBaja(mergeLeadStatusFields(lead, safeOverrides))) {
    payload.masterStatus = 'BAJA';
    payload.phase = 4;
    payload.salesPipelineVisible = false;
  }

  const googleOpportunity = preferGoogleOpportunity(
    lead.googleOpportunity,
    safeOverrides.googleOpportunity
  );
  if (googleOpportunity) payload.googleOpportunity = googleOpportunity;

  return payload;
};

/** Solo sincronizar al Dashboard cuando hay invitación de onboarding o acción explícita. */
export const shouldSyncCrmLeadToDashboard = (lead = {}, overrides = {}) => {
  if (overrides.forceDashboardSync === true) return true;
  if (isCrmLeadCommercialBaja(mergeLeadStatusFields(lead, overrides))) return false;
  if (overrides.salesPipelineVisible === true) return true;
  if (lead.onboardingStatus === 'ENVIADA' || overrides.onboardingStatus === 'ENVIADA') return true;
  if (lead.onboardingSentAt || overrides.onboardingSentAt) return true;
  if (overrides.onboardingCrmStatus === 'PENDIENTE_CLIENTE') return true;
  return false;
};

/** Opción activa de propuesta multi-escenario (recomendada o primera). */
export const resolveActiveProposalOption = (proposal) => {
  if (!proposal) return null;
  const opciones = proposal.opciones;
  if (Array.isArray(opciones) && opciones.length > 0) {
    return opciones.find((o) => o.isRecommended) || opciones[0];
  }
  return null;
};

/** Licencias de propuesta CRM (incluye opciones A/B/C). La propuesta cotizada gana sobre fallback vacío o legado. */
export const resolveLicenciasFromProposal = (proposal, fallback = []) => {
  if (!proposal) {
    return Array.isArray(fallback) && fallback.length > 0 ? fallback : [];
  }
  const active = resolveActiveProposalOption(proposal);
  if (active?.licencias?.length) return active.licencias;
  if (Array.isArray(proposal.licencias) && proposal.licencias.length > 0) return proposal.licencias;
  const opciones = proposal.opciones;
  if (Array.isArray(opciones)) {
    for (const op of opciones) {
      if (Array.isArray(op?.licencias) && op.licencias.length > 0) return op.licencias;
    }
  }
  return Array.isArray(fallback) && fallback.length > 0 ? fallback : [];
};

/** Servicios adicionales de la opción activa o raíz de la propuesta. */
export const resolveServiciosFromProposal = (proposal, fallback = []) => {
  if (Array.isArray(fallback) && fallback.length > 0) return fallback;
  if (!proposal) return [];
  const active = resolveActiveProposalOption(proposal);
  if (active?.serviciosAdicionales?.length) return active.serviciosAdicionales;
  return proposal.serviciosAdicionales || [];
};

/** Fusiona la opción activa de propuesta como fuente de SKU/qty/descuento. */
export const mergeProposalLicenciaFields = (stored = [], fromProposal = []) => {
  if (!fromProposal?.length) return stored;
  if (!stored?.length) return fromProposal;
  const byStored = new Map(stored.map((l) => [l.sku, l]));
  return fromProposal.map((from) => {
    const lic = byStored.get(from.sku) || {};
    return {
      ...lic,
      ...from,
      qty: Number(from.qty) || Number(from.licenses) || Number(from.cantidad) || lic.qty || 0,
      descuento: mergePreservedDiscount(lic, from),
    };
  });
};

/** Licencias comerciales: la opción recomendada de la propuesta CRM define la tabla. */
export const resolveCommercialLicencias = (contract = {}) => {
  const stored = contract.licencias || [];
  const fromProposal = resolveLicenciasFromProposal(contract.proposal, []);
  const storedOnlyPc = stored.length > 0 && stored.every((l) => isPorCobrarSatSku(l.sku));
  const proposalHasCatalog = fromProposal.some((l) => !isPorCobrarSatSku(l.sku));
  if (storedOnlyPc && proposalHasCatalog) return fromProposal;
  if (fromProposal.length > 0) return mergeProposalLicenciaFields(stored, fromProposal);
  return stored;
};

/**
 * Fija licencias/anexo desde propuesta CRM (opción recomendada), con precios del catálogo.
 * Retorna null si no hay propuesta comercial válida.
 */
export const solidifyCommercialTermsFromProposal = (contract = {}, priceList = {}) => {
  const proposal = contract.proposal;
  if (!hasValidCommercialProposal(proposal)) return null;

  const active = resolveActiveProposalOption(proposal);
  // La vigencia de la opción cotizada gana sobre un anexo default (Flexible) heredado del sync.
  const vigenciaLabel = active?.vigenciaLabel || contract.anexo?.vigenciaLabel || proposal.vigenciaLabel || 'Flexible';
  const { priceType, esquema } = resolveTermDiscountPolicy(vigenciaLabel);

  const licencias = resolveCommercialLicencias(contract).map((lic) => ({
    ...lic,
    precioUnitario: resolveLicenseUnitPrice(lic, contract, priceList, { priceType })
  }));

  const merged = {
    ...contract,
    licencias,
    proposal,
    anexo: {
      ...(contract.anexo || {}),
      vigenciaLabel,
      billingFrequency:
        active?.billingFrequency
        || (active?.billingCycle === 'anual' ? 'Anual' : null)
        || contract.anexo?.billingFrequency
        || proposal.billingFrequency
        || 'Mensual',
      esquema
    }
  };
  const anexo = buildAnexoTotalsFromLicencias(merged, priceList);
  const serviciosAdicionales = resolveServiciosFromProposal(proposal, contract.serviciosAdicionales);
  const stampedProposal = stampPrecioUnitarioIntoProposal(
    proposal,
    { ...merged, licencias, custom_prices: getCustomPricesMap(contract) },
    priceList
  );

  return { licencias, anexo, serviciosAdicionales, proposal: stampedProposal };
};

const PIPELINE_STAGES_SOLIDIFY_COMMERCIAL = new Set(['LEGAL_REVIEW', 'SIGNING', 'IMPLEMENTATION']);

/** Recalcula anexo desde licencias + opción activa de propuesta. */
export const buildAnexoTotalsFromLicencias = (contract = {}, priceList = {}) => {
  const licencias = resolveCommercialLicencias(contract);
  const active = resolveActiveProposalOption(contract.proposal);
  const anexo = contract.anexo || {};
  const vigenciaLabel = active?.vigenciaLabel || anexo.vigenciaLabel || 'Flexible';
  const billingFrequency = resolveDisplayBillingFrequency({
    ...contract,
    anexo,
    proposal: contract.proposal
  });
  const { disableLicenseDiscount, priceType, esquema } = resolveTermDiscountPolicy(vigenciaLabel);
  let monthlyLicenses = 0;
  licencias.forEach((lic) => {
    const unit = resolveLicenseUnitPrice(lic, contract, priceList, { priceType });
    const desc = disableLicenseDiscount ? 0 : (lic.descuento || 0);
    monthlyLicenses += unit * (1 - desc / 100) * (lic.qty || 0);
  });
  let oneTimeServices = 0;
  (resolveServiciosFromProposal(contract.proposal, contract.serviciosAdicionales) || []).forEach((serv) => {
    const unit = Number(serv.price) || 0;
    const desc = serv.descuento || 0;
    oneTimeServices += unit * (1 - desc / 100) * (serv.qty || 1);
  });
  const periodMult = BILLING_FREQ_MULTIPLIERS[billingFrequency] || 1;
  const recurringPeriod = monthlyLicenses * periodMult;
  return {
    ...anexo,
    vigenciaLabel,
    billingFrequency,
    currency: resolveContractCurrency({ ...contract, anexo }),
    totalMensual: monthlyLicenses > 0 ? monthlyLicenses : (Number(contract.proposal?.totalMensual) || 0),
    totalContrato: (recurringPeriod > 0 ? recurringPeriod + oneTimeServices : 0)
      || Number(contract.proposal?.totalContrato)
      || Number(contract.proposal?.totalMensual)
      || 0,
    totalServiciosUnico: oneTimeServices,
    esquema
  };
};

function applyProposalAnexoFallback(anexo = {}, contract = {}) {
  if (Number(anexo.totalMensual) > 0) return anexo;
  // Con líneas comerciales el catálogo debe ganar; los totales de propuesta operativa
  // suelen estar inflados (anual metido como mensual, ×12 extra, etc.).
  if (resolveCommercialLicencias(contract).length > 0) return anexo;
  const p = contract.proposal || {};
  if (Number(p.totalMensual) <= 0 && Number(p.totalContrato) <= 0) return anexo;
  const monthly = Number(p.totalMensual) || Number(p.totalContrato) || 0;
  return {
    ...anexo,
    totalMensual: monthly,
    totalContrato: Number(p.totalContrato) > 0 ? Number(p.totalContrato) : monthly,
    billingFrequency: anexo.billingFrequency || p.billingFrequency || 'Mensual',
    currency: anexo.currency || p.currency || contract.currency || 'MXN',
    vigenciaLabel: anexo.vigenciaLabel || p.vigenciaLabel
  };
}

/** Normaliza campos que el tablero y el modal esperan en camelCase. */
export const enrichContractForDisplay = (contract = {}, priceList = {}) => {
  const created = resolveContractCreationDate(contract);
  const tipo = contract.tipo_persona
    || contract.entityType
    || (contract.acta_url ? 'Moral' : null);
  const customMap = getCustomPricesMap(contract);
  const active = resolveActiveProposalOption(contract.proposal);
  const vigenciaLabel = contract.anexo?.vigenciaLabel || active?.vigenciaLabel || contract.proposal?.vigenciaLabel || 'Flexible';
  const { priceType } = resolveTermDiscountPolicy(vigenciaLabel);
  const resolvedLicencias = resolveCommercialLicencias(contract);
  const licencias = resolvedLicencias.map((lic) => {
    const unit = resolveLicenseUnitPrice(lic, contract, priceList, { priceType });
    const custom = customMap[lic.sku];
    const skuLabel = lic.skuLabel || custom?.description || priceList[lic?.sku]?.name || lic.sku;
    return { ...lic, ...(unit > 0 ? { precioUnitario: unit } : {}), skuLabel };
  });
  const withLicencias = licencias.length ? { ...contract, licencias } : contract;
  let anexo = licencias.length ? buildAnexoTotalsFromLicencias(withLicencias, priceList) : (contract.anexo || {});
  anexo = applyProposalAnexoFallback(anexo, contract);
  return {
    ...withLicencias,
    ...(licencias.length ? { licencias, anexo } : { anexo }),
    ...(created && !contract.fechaCreacion ? { fechaCreacion: created.toISOString() } : {}),
    ...(tipo && !contract.tipo_persona ? { tipo_persona: tipo } : {})
  };
};

/** Copia servicios adicionales del contrato a la opción activa (o raíz) de la propuesta CRM. */
export const mergeServiciosIntoProposal = (proposal, serviciosAdicionales = []) => {
  if (!proposal) return null;
  const next = JSON.parse(JSON.stringify(proposal));
  const servs = Array.isArray(serviciosAdicionales) ? serviciosAdicionales : [];
  if (Array.isArray(next.opciones) && next.opciones.length > 0) {
    const activeIdx = next.opciones.findIndex((o) => o.isRecommended);
    const i = activeIdx >= 0 ? activeIdx : 0;
    next.opciones[i] = { ...next.opciones[i], serviciosAdicionales: servs };
  } else {
    next.serviciosAdicionales = servs;
  }
  return next;
};

/**
 * Persiste servicios adicionales en contratos y refleja en propuesta CRM / micrositio.
 */
export const persistCommercialServiciosAdicionales = async (contractId, payload = {}) => {
  const {
    serviciosAdicionales = [],
    proposal = null,
    crmLeadId = null,
    anexo = null,
    totalContrato = null,
    syncCrmLead = true,
    isRenewal = false
  } = payload;

  const mergedProposal = proposal ? mergeServiciosIntoProposal(proposal, serviciosAdicionales) : null;
  const leadId = crmLeadId || null;

  const updates = omitUndefinedDeep({
    serviciosAdicionales,
    ...(mergedProposal ? { proposal: mergedProposal } : {}),
    ...(anexo
      ? {
          anexo: {
            ...anexo,
            ...(totalContrato != null ? { totalContrato } : {})
          }
        }
      : {}),
    lastUpdate: new Date().toISOString(),
    ...(isRenewal ? { isRenewal: true } : {})
  });

  await updateContractInDB(contractId, updates);

  if (syncCrmLead && leadId && mergedProposal) {
    try {
      const leadRef = doc(db, 'crm_leads', leadId);
      const snap = await getDoc(leadRef);
      if (snap.exists()) {
        await updateDoc(leadRef, {
          proposal: mergedProposal,
          updatedAt: serverTimestamp()
        });
      }
    } catch (e) {
      console.warn('No se pudo reflejar servicios en crm_leads:', e);
    }
  }

  return { ...updates, proposal: mergedProposal };
};

export const hasValidCommercialProposal = (proposal) => {
  if (!proposal) return false;
  if (resolveLicenciasFromProposal(proposal).length > 0) return true;
  if (Array.isArray(proposal.serviciosAdicionales) && proposal.serviciosAdicionales.length > 0) return true;
  return (Array.isArray(proposal.opciones) ? proposal.opciones : []).some(proposalOptionHasCommercialLines);
};

/** Servicios del catálogo (migración, dominio, etc.) son pago único, no recurrentes. */
export const isProposalExtraServiceOneTime = () => true;

/**
 * Totales de una opción de propuesta: licencias × frecuencia + servicios pago único.
 */
export const computeProposalOptionTotals = (option = {}, priceList = {}, termsOptions = [], currency = 'MXN', sourceContext = null) => {
  const { disableLicenseDiscount, priceType } = resolveTermDiscountPolicy(option.vigenciaLabel, termsOptions);
  const freqMult = BILLING_FREQ_MULTIPLIERS[option.billingFrequency || 'Mensual'] || 1;
  const fx = currency === 'USD' ? 20 : 1;

  let recurringPeriod = 0;
  (option.licencias || []).forEach((lic) => {
    // Usa precio guardado/custom_prices antes del catálogo para SKUs SAT o líneas legado.
    const base = resolveLicenseUnitPrice(lic, sourceContext || option, priceList, { priceType }) / fx;
    if (base <= 0) return;
    const desc = disableLicenseDiscount ? 0 : (Number(lic.descuento) || 0);
    recurringPeriod += base * (1 - desc / 100) * (Number(lic.qty) || 0) * freqMult;
  });

  let oneTimeServices = 0;
  (option.serviciosAdicionales || []).forEach((serv) => {
    const base = (Number(serv.price) || 0) / fx;
    const desc = Number(serv.descuento) || 0;
    oneTimeServices += base * (1 - desc / 100) * (Number(serv.qty) || 1);
  });

  return { recurringPeriod, oneTimeServices, freqMult };
};

export const PROPOSAL_IVA_RATE = 0.16;

/** Por defecto las propuestas incluyen IVA (16%). Acepta propuesta o contrato con `proposal` anidado. */
export const resolveProposalIncludeIva = (source) => {
  if (!source) return true;
  if (source.includeIva === false) return false;
  if (source.proposal?.includeIva === false) return false;
  return true;
};

export const proposalIvaAmount = (subtotal, includeIva = true) =>
  includeIva !== false ? Number(subtotal || 0) * PROPOSAL_IVA_RATE : 0;

export const proposalTotalWithIva = (subtotal, includeIva = true) =>
  Number(subtotal || 0) + proposalIvaAmount(subtotal, includeIva);

export const proposalIvaFooterNote = (includeIva = true) =>
  includeIva !== false
    ? 'Los precios incluyen IVA (16%).'
    : 'Esta propuesta se emite sin IVA.';

/**
 * Desglose de facturación para Contabilidad: precios PC (precioUnitario/custom_prices),
 * catálogo Nexus, servicios y totales guardados en anexo como respaldo.
 */
export const computeContractBillingBreakdown = (contract = {}, priceList = {}) => {
  const anexo = contract.anexo || {};
  const frequency = anexo.billingFrequency || contract.billingFrequency || 'Mensual';
  const periodMult = { Mensual: 1, Trimestral: 3, Semestral: 6, Anual: 12 }[frequency] || 1;
  const vigenciaLabel = anexo.vigenciaLabel || 'Flexible';
  const { disableLicenseDiscount, priceType } = resolveTermDiscountPolicy(vigenciaLabel);
  const includeIva = resolveProposalIncludeIva(contract);

  const licenseLines = (contract.licencias || []).map((lic, index) => {
    const unit = billingLicenseUnit(lic, resolveLicenseUnitPrice(lic, contract, priceList, { priceType }));
    const discount = billingLicenseDiscount(lic, disableLicenseDiscount);
    const unitNet = unit * (1 - discount / 100);
    const rowTotal = unitNet * (Number(lic.qty) || 0) * periodMult;
    return {
      key: `lic-${index}`,
      type: 'license',
      description: resolveLicenseLabel(lic, contract, priceList),
      sku: lic.sku,
      qty: lic.qty,
      unitNet,
      discount,
      rowTotal
    };
  });

  const serviceLines = (contract.serviciosAdicionales || []).map((serv, index) => {
    const basePrice = Number(serv.price) || 0;
    const discount = Number(serv.descuento) || 0;
    const unitNet = basePrice * (1 - discount / 100);
    const rowTotal = unitNet * (Number(serv.qty) || 1);
    return {
      key: `serv-${index}`,
      type: 'service',
      description: serv.name,
      qty: serv.qty || 1,
      unitNet,
      discount,
      rowTotal,
      billingNote: 'Pago único'
    };
  });

  let subtotal = [...licenseLines, ...serviceLines].reduce((sum, line) => sum + line.rowTotal, 0);
  if (subtotal <= 0) {
    const fallback = getContractDisplayAmount(contract, priceList);
    if (fallback > 0) subtotal = fallback;
  }

  const iva = proposalIvaAmount(subtotal, includeIva);
  const total = subtotal + iva;

  return {
    frequency,
    periodMult,
    includeIva,
    licenseLines,
    serviceLines,
    subtotal,
    iva,
    total
  };
};

/** Guarda includeIva en contrato y, si aplica, en crm_leads vinculado. */
export const persistProposalIncludeIva = async (contractId, includeIva, options = {}) => {
  const {
    existingProposal = {},
    crmLeadId = null,
    syncCrmLead = true,
    isRenewal = false
  } = options;
  const savedIncludeIva = includeIva !== false;
  const proposalPayload = { ...existingProposal, includeIva: savedIncludeIva };
  const updates = omitUndefinedDeep({
    proposal: proposalPayload,
    lastUpdate: new Date().toISOString(),
    ...(isRenewal ? { isRenewal: true } : {})
  });
  await updateContractInDB(contractId, updates);

  if (syncCrmLead && crmLeadId) {
    try {
      const leadRef = doc(db, 'crm_leads', crmLeadId);
      const snap = await getDoc(leadRef);
      if (snap.exists()) {
        const existing = snap.data();
        await updateDoc(leadRef, {
          proposal: { ...(existing.proposal || {}), includeIva: savedIncludeIva }
        });
      }
    } catch (e) {
      console.warn('[persistProposalIncludeIva] CRM sync:', e.message);
    }
  }

  return proposalPayload;
};

/** Guarda la vigencia elegida en Rev. Comercial en la opción activa y en el lead CRM. */
export const persistCommercialVigencia = async (contractId, vigenciaLabel, options = {}) => {
  const {
    proposal = null,
    anexo = null,
    crmLeadId = null,
    syncCrmLead = true,
    isRenewal = false
  } = options;
  const label = String(vigenciaLabel || '').trim();
  if (!label) return { proposal: proposal || null };
  const nextProposal = proposal ? mergeVigenciaIntoProposal(proposal, label) : null;
  const updates = omitUndefinedDeep({
    ...(nextProposal ? { proposal: nextProposal } : {}),
    anexo: { ...(anexo || {}), vigenciaLabel: label },
    lastUpdate: new Date().toISOString(),
    ...(isRenewal ? { isRenewal: true } : {})
  });
  await updateContractInDB(contractId, updates);

  if (syncCrmLead && crmLeadId && nextProposal) {
    try {
      const leadRef = doc(db, 'crm_leads', crmLeadId);
      const snap = await getDoc(leadRef);
      if (snap.exists()) {
        await updateDoc(leadRef, {
          proposal: nextProposal,
          updatedAt: serverTimestamp()
        });
      }
    } catch (e) {
      console.warn('[persistCommercialVigencia] CRM sync:', e.message);
    }
  }

  return { proposal: nextProposal };
};

export const DEFAULT_CONTRACT_ANEXO = {
  vigenciaLabel: 'Flexible',
  esquema: 'Flex',
  totalMensual: 0,
  totalContrato: 0,
  billingFrequency: 'Mensual',
  currency: 'MXN'
};

export const buildAnexoFromProposal = (proposal, totalOverride) => {
  if (!proposal) return { ...DEFAULT_CONTRACT_ANEXO };
  const active = resolveActiveProposalOption(proposal);
  const vigenciaLabel = active?.vigenciaLabel || proposal.vigenciaLabel || proposal.anexo?.vigenciaLabel || 'Flexible';
  const { esquema } = resolveTermDiscountPolicy(vigenciaLabel);
  const billingFrequency =
    active?.billingFrequency
    || (active?.billingCycle === 'anual' ? 'Anual' : null)
    || proposal.billingFrequency
    || proposal.anexo?.billingFrequency
    || 'Mensual';
  const total = totalOverride ?? proposal.totalContrato ?? proposal.totalMensual ?? proposal.anexo?.totalContrato ?? 0;
  return {
    vigenciaLabel,
    esquema,
    totalMensual: proposal.totalMensual ?? proposal.anexo?.totalMensual ?? total,
    totalContrato: total,
    billingFrequency,
    currency: proposal.currency || proposal.anexo?.currency || 'MXN'
  };
};

/**
 * Snapshot de propuesta vigente para no perder historial al generar una nueva.
 */
export const buildProposalHistoryEntry = (record = {}, { note } = {}) => {
  const proposal = record.proposal;
  if (!proposal || !hasValidCommercialProposal(proposal)) return null;
  const prevHistory = Array.isArray(record.proposalHistory) ? record.proposalHistory : [];
  return {
    version: prevHistory.length + 1,
    archivedAt: new Date().toISOString(),
    note: note || 'Propuesta archivada',
    proposal: JSON.parse(JSON.stringify(proposal)),
    licencias: Array.isArray(record.licencias) ? JSON.parse(JSON.stringify(record.licencias)) : [],
    serviciosAdicionales: Array.isArray(record.serviciosAdicionales)
      ? JSON.parse(JSON.stringify(record.serviciosAdicionales))
      : [],
    anexo: record.anexo ? JSON.parse(JSON.stringify(record.anexo)) : null,
    propuesta_url: record.propuesta_url || record.sowDocUrl || proposal.url || null,
    sowDocUrl: record.sowDocUrl || null,
    currency: record.currency || proposal.currency || 'MXN',
    billingFrequency: record.billingFrequency || record.anexo?.billingFrequency || null
  };
};

/**
 * Persiste propuesta editada con el generador CRM en contratos (+ opcional crm_leads).
 * Archiva la propuesta anterior en `proposalHistory` (no se pierde el historial).
 */
export const applyCrmProposalToContract = async (contractId, newProposal, totalCalculado, options = {}) => {
  const { crmLeadId, syncCrmLead = true, isRenewal = false, contact = null } = options;
  const collectionName = await resolveContractCollectionName(contractId, { isRenewal });
  const contractRef = doc(db, collectionName, contractId);
  const existingSnap = await getDoc(contractRef);
  const existing = existingSnap.exists() ? existingSnap.data() : {};

  const active = resolveActiveProposalOption(newProposal);
  const savedCurrency = newProposal.currency === 'USD' ? 'USD' : 'MXN';
  const priceContext = {
    ...existing,
    proposal: null,
    custom_prices: existing.custom_prices || existing.por_cobrar_custom_prices || {},
    licencias: existing.licencias || []
  };
  let proposalPayload = {
    ...newProposal,
    currency: savedCurrency,
    anexo: { ...(newProposal.anexo || {}), currency: savedCurrency }
  };
  const catalog = await getPricesFromDB().catch(() => ({}));
  const solid = solidifyCommercialTermsFromProposal({
    ...priceContext,
    proposal: proposalPayload,
    currency: savedCurrency,
    anexo: { ...(existing.anexo || {}), currency: savedCurrency, ...(newProposal.anexo || {}) }
  }, catalog);
  const licencias = solid?.licencias || resolveLicenciasFromProposal(proposalPayload);
  const serviciosAdicionales = solid?.serviciosAdicionales || resolveServiciosFromProposal(proposalPayload);
  const anexo = solid?.anexo || buildAnexoFromProposal(proposalPayload, totalCalculado);
  if (anexo) anexo.currency = savedCurrency;
  if (!solid?.anexo && totalCalculado != null && anexo) {
    anexo.totalContrato = totalCalculado;
    const mult = BILLING_FREQ_MULTIPLIERS[anexo.billingFrequency] || 1;
    anexo.totalMensual = mult > 1 ? totalCalculado / mult : totalCalculado;
  }

  // Micrositio/PDF leen proposal.opciones[].licencias: sellamos precio unitario ahí.
  proposalPayload = stampPrecioUnitarioIntoProposal(
    proposalPayload,
    { ...priceContext, licencias, custom_prices: priceContext.custom_prices },
    catalog
  );

  const recurringAmount = solid?.anexo
    ? (Number(solid.anexo.totalMensual) || 0) * (BILLING_FREQ_MULTIPLIERS[solid.anexo.billingFrequency] || 1)
    : totalCalculado;

  const archiveEntry = buildProposalHistoryEntry(existing, {
    note: options.archiveNote || 'Propuesta sustituida por una nueva versión'
  });
  const proposalHistory = archiveEntry
    ? [...(Array.isArray(existing.proposalHistory) ? existing.proposalHistory : []), archiveEntry]
    : (existing.proposalHistory || undefined);

  const contactName = String(proposalPayload.contactName || contact?.name || '').trim();
  const contactEmail = String(proposalPayload.contactEmail || contact?.email || '').trim().toLowerCase();
  const adminPatch = (contactName || contactEmail || contact)
    ? {
        contactos: {
          ...(existing.contactos || {}),
          admin: {
            ...(existing.contactos?.admin || {}),
            ...(contactName ? {
              nombre: (contact?.name || contactName.split(' ')[0] || '').trim(),
              apellido: (contact?.lastName || contactName.split(' ').slice(1).join(' ') || '').trim()
            } : {}),
            ...(contactEmail ? { email: contactEmail } : {}),
            ...(contact?.phone ? { telefono: contact.phone } : {})
          }
        },
        ...(contactEmail ? { contactEmail, clientEmail: contactEmail } : {}),
        ...(contactName ? { contactName, clientName: contactName } : {})
      }
    : {};

  const updates = omitUndefinedDeep({
    proposal: proposalPayload,
    licencias,
    serviciosAdicionales,
    anexo,
    billingFrequency: anexo.billingFrequency,
    currency: savedCurrency,
    lastUpdate: new Date().toISOString(),
    ...(proposalHistory ? { proposalHistory } : {}),
    ...(isRenewal ? { isRenewal: true } : {}),
    ...adminPatch
  });

  await updateContractInDB(contractId, updates);

  if (syncCrmLead && crmLeadId) {
    try {
      const leadRef = doc(db, 'crm_leads', crmLeadId);
      const snap = await getDoc(leadRef);
      if (snap.exists()) {
        const leadExisting = snap.data();
        const leadArchive = buildProposalHistoryEntry(leadExisting, {
          note: options.archiveNote || 'Propuesta sustituida por una nueva versión'
        });
        const leadHistory = leadArchive
          ? [...(Array.isArray(leadExisting.proposalHistory) ? leadExisting.proposalHistory : []), leadArchive]
          : undefined;
        await updateDoc(leadRef, omitUndefinedDeep({
          proposal: proposalPayload,
          ...(leadHistory ? { proposalHistory: leadHistory } : {}),
          meetingData: {
            ...(leadExisting.meetingData || {}),
            amount: recurringAmount,
            currency: savedCurrency
          },
          opportunity: {
            ...(leadExisting.opportunity || {}),
            amount: recurringAmount,
            currency: savedCurrency
          },
          updatedAt: serverTimestamp()
        }));
      }
    } catch (e) {
      console.warn('No se pudo reflejar propuesta en crm_leads:', e);
    }
  }

  return {
    ...updates,
    activeOptionTitle: active?.titulo,
    proposalHistory: proposalHistory || existing.proposalHistory || []
  };
};

/** Refleja licencias editadas en contrato y en la opción activa de propuesta CRM. */
export const mergeLicenciasIntoProposal = (proposal, licencias = []) => {
  if (!proposal) return null;
  const next = JSON.parse(JSON.stringify(proposal));
  const normalized = (licencias || []).map((lic) => {
    const qty = Number(lic.qty) || Number(lic.cantidad) || Number(lic.licenses) || 0;
    return { ...lic, qty, cantidad: qty };
  });
  // Opción activa + raíz siempre alineadas (el Doc lee la opción recomendada).
  if (Array.isArray(next.opciones) && next.opciones.length > 0) {
    const activeIdx = next.opciones.findIndex((o) => o.isRecommended);
    const i = activeIdx >= 0 ? activeIdx : 0;
    next.opciones[i] = { ...next.opciones[i], licencias: normalized };
  }
  next.licencias = normalized;
  return next;
};

export const persistCommercialLicencias = async (contractId, payload = {}) => {
  const {
    licencias = [],
    proposal = null,
    crmLeadId = null,
    anexo = null,
    totalContrato = null,
    syncCrmLead = true,
    isRenewal = false,
    priceList = {},
  } = payload;

  const collectionName = await resolveContractCollectionName(contractId, { isRenewal });
  const existingSnap = await getDoc(doc(db, collectionName, contractId));
  const existing = existingSnap.exists() ? existingSnap.data() : {};
  const prevLicencias = resolveLicenciasFromProposal(existing.proposal, existing.licencias || []);
  const dropped = licenseDiscountsDropped(prevLicencias, licencias);
  const archiveEntry = dropped
    ? buildProposalHistoryEntry(existing, {
        note: 'Descuentos anteriores archivados (edición en Rev. Com.)'
      })
    : null;
  const proposalHistory = archiveEntry
    ? [...(Array.isArray(existing.proposalHistory) ? existing.proposalHistory : []), archiveEntry]
    : undefined;

  const mergedProposal = proposal ? mergeLicenciasIntoProposal(proposal, licencias) : null;
  const proposalToStore = rebaseProposalEdits(existing.proposal, proposal, mergedProposal) || mergedProposal;
  const catalog = priceList && Object.keys(priceList).length
    ? priceList
    : await getPricesFromDB().catch(() => ({}));
  const solid = proposalToStore
    ? solidifyCommercialTermsFromProposal({ proposal: proposalToStore, licencias, anexo, currency: anexo?.currency }, catalog)
    : null;

  const updates = omitUndefinedDeep({
    licencias: solid?.licencias || licencias,
    ...(proposalToStore ? { proposal: proposalToStore } : {}),
    ...(solid?.anexo || anexo
      ? {
          anexo: {
            ...(solid?.anexo || anexo),
            ...(totalContrato != null ? { totalContrato } : {}),
          },
        }
      : {}),
    lastUpdate: new Date().toISOString(),
    ...(proposalHistory ? { proposalHistory } : {}),
    ...(isRenewal ? { isRenewal: true } : {}),
  });

  await updateContractInDB(contractId, updates);

  if (syncCrmLead && crmLeadId && mergedProposal) {
    try {
      const leadRef = doc(db, 'crm_leads', crmLeadId);
      const snap = await getDoc(leadRef);
      if (snap.exists()) {
        const leadExisting = snap.data();
        const leadArchive = dropped
          ? buildProposalHistoryEntry(leadExisting, {
              note: 'Descuentos anteriores archivados (edición en Rev. Com.)'
            })
          : null;
        const leadHistory = leadArchive
          ? [...(Array.isArray(leadExisting.proposalHistory) ? leadExisting.proposalHistory : []), leadArchive]
          : undefined;
        const leadProposal = rebaseProposalEdits(leadExisting.proposal, existing.proposal, proposalToStore)
          || proposalToStore;
        await updateDoc(leadRef, omitUndefinedDeep({
          proposal: leadProposal,
          ...(leadHistory ? { proposalHistory: leadHistory } : {}),
          updatedAt: serverTimestamp(),
        }));
      }
    } catch (e) {
      console.warn('No se pudo reflejar licencias en crm_leads:', e);
    }
  }

  return { ...updates, proposal: proposalToStore || mergedProposal };
};

/** Firestore no acepta valores undefined en updateDoc/setDoc. */
export const omitUndefinedDeep = (value, seen = new WeakSet(), depth = 0) => {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value;
  if (depth > 25) return undefined;
  if (seen.has(value)) return undefined;
  seen.add(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => omitUndefinedDeep(item, seen, depth + 1))
      .filter((item) => item !== undefined);
  }
  const out = {};
  for (const [key, val] of Object.entries(value)) {
    if (val === undefined) continue;
    const cleaned = omitUndefinedDeep(val, seen, depth + 1);
    if (cleaned !== undefined) out[key] = cleaned;
  }
  return out;
};

/** Reenvía invitación de onboarding desde Dashboard (Rev. Com.) cuando caducó o el cliente no completó el alta. */
export const resendOnboardingInvitationFromContract = async (contract, options = {}) => {
  if (!contract?.id) throw new Error('Expediente inválido.');
  const contactEmail = options.contactEmail
    || contract.contactos?.admin?.email
    || contract.contactos?.contacto1?.correo
    || contract.contactEmail
    || contract.clientEmail;
  if (!contactEmail || !String(contactEmail).includes('@')) {
    throw new Error('No hay correo de contacto para reenviar la invitación.');
  }
  const companyName = contract.nombre_comercial || contract.razon_social || contract.companyName || contract.clientName || 'Cliente';
  const domain = (contract.dominio || contract.domain || '').trim();
  const exec = contract.ejecutivoAsignado || {};
  const executiveEmail = String(options.executiveEmail || exec.email || '').toLowerCase().trim();
  const executiveName = options.executiveName || exec.name || executiveEmail;
  if (!executiveEmail) {
    throw new Error('No se identificó al ejecutivo. Recarga Nexus e intenta de nuevo.');
  }
  const appOrigin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://nexus.plexarian.com';
  const link = `${appOrigin}/?token=${contract.id}`;

  await sendClientInvitation(contactEmail, companyName, link, executiveName, executiveEmail, domain);

  const inviteData = {
    clientName: companyName,
    clientEmail: contactEmail,
    domain,
    movementType: 'Nuevo Cliente',
    createdBy: exec.id || options.createdBy || 'unknown',
    creatorEmail: executiveEmail || options.creatorEmail || 'unknown',
    creatorName: executiveName || options.creatorName || 'Ejecutivo',
    ejecutivoAsignado: exec.email ? exec : undefined
  };
  await registerInvitationWithId(contract.id, inviteData);

  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + 14);
  const reopenFromCierre = onboardingInvitePipelinePatch(contract);
  const preserveStage = shouldPreserveAdvancedPipelineStage(contract) && !reopenFromCierre;

  await updateContractInDB(contract.id, {
    salesPipelineVisible: true,
    ...(reopenFromCierre || {}),
    ...(preserveStage || reopenFromCierre
      ? {}
      : {
        pipelineStage: 'SALES_REVIEW',
        salesStatus: 'PENDING',
        legalStatus: 'PENDING'
      }),
    onboardingStatus: preserveStage ? (contract.onboardingStatus || 'ENVIADA') : 'ENVIADA',
    onboardingSentAt: preserveStage ? contract.onboardingSentAt : new Date().toISOString(),
    onboardingExpiresAt: expirationDate.toISOString(),
    onboardingCrmStatus: preserveStage
      ? (contract.onboardingCrmStatus || 'COMPLETADO')
      : 'PENDIENTE_CLIENTE',
    ...(contract.clientOrigin ? {} : { clientOrigin: 'crm_onboarding_invite' })
  });

  if (contract.crmLeadId) {
    try {
      const leadRef = doc(db, 'crm_leads', contract.crmLeadId);
      const snap = await getDoc(leadRef);
      if (snap.exists()) {
        await updateDoc(leadRef, {
          onboardingStatus: 'ENVIADA',
          onboardingSentAt: new Date().toISOString(),
          onboardingExpiresAt: expirationDate.toISOString()
        });
      }
    } catch (e) {
      console.warn('No se pudo actualizar lead CRM al reenviar onboarding:', e.message);
    }
  }

  return { link, contactEmail, expiresAt: expirationDate.toISOString() };
};

/** Aprueba propuesta y avanza: Legal, o Implementación si es cliente existente + solo Gemini Enterprise. */
export const advanceContractProposalToLegal = async (contractId, contractData = {}) => {
  const proposal = contractData.proposal;
  const hasLicencias = Array.isArray(contractData.licencias) && contractData.licencias.length > 0;
  if (!hasValidCommercialProposal(proposal) && !hasLicencias) {
    throw new Error('No hay propuesta comercial ni licencias para confirmar.');
  }

  const collectionName = contractData.isRenewal ? 'renewals_metadata' : 'contratos';
  const contractRef = doc(db, collectionName, contractId);
  const docSnap = await getDoc(contractRef);
  const existing = docSnap.exists() ? docSnap.data() : {};
  const mergedForSkip = { ...existing, ...contractData, proposal: proposal || existing.proposal };
  const skipLegal = canSkipLegalToImplementation(mergedForSkip);

  const pendingOnboarding = contractData.crmLeadId
    && !contractData.csf_url
    && !contractData.id_url
    && !contractData.acta_url
    && (contractData.onboardingCrmStatus === 'PENDIENTE_CLIENTE'
      || contractData.onboardingStatus === 'ENVIADA'
      || contractData.onboardingSentAt);
  if (pendingOnboarding && !skipLegal) {
    throw new Error('El cliente aún no completa el onboarding. Reenvía la invitación y espera el expediente antes de pasar a Legal.');
  }

  const targetStage = skipLegal ? 'IMPLEMENTATION' : 'LEGAL_REVIEW';
  const existingHistory = existing.pipelineHistory || contractData.pipelineHistory || [];
  const newHistory = [
    ...existingHistory,
    {
      stage: targetStage,
      date: new Date().toISOString(),
      note: skipLegal
        ? 'Cliente existente · solo Gemini Enterprise: sin Legal'
        : 'Propuesta CRM confirmada'
    }
  ];

  const serviciosAdicionales = contractData.serviciosAdicionales
    ?? resolveServiciosFromProposal(proposal, []);
  let mergedProposal = proposal
    ? mergeServiciosIntoProposal(proposal, serviciosAdicionales)
    : null;
  const editedVigencia = String(contractData.anexo?.vigenciaLabel || '').trim();
  if (editedVigencia && (mergedProposal || proposal)) {
    mergedProposal = mergeVigenciaIntoProposal(mergedProposal || proposal, editedVigencia);
  }
  const mergedForSolid = {
    ...existing,
    ...contractData,
    proposal: mergedProposal ?? proposal ?? null,
    licencias: contractData.licencias
  };
  const catalog = await getPricesFromDB().catch(() => ({}));
  const solid = solidifyCommercialTermsFromProposal(mergedForSolid, catalog);
  const licencias = solid?.licencias
    || resolveLicenciasFromProposal(mergedProposal || proposal, contractData.licencias);
  const anexo = solid?.anexo || {
    ...(contractData.anexo || buildAnexoFromProposal(mergedProposal || proposal)),
    totalContrato: contractData.anexo?.totalContrato
  };
  const finalServicios = solid?.serviciosAdicionales ?? serviciosAdicionales;

  const payload = {
    salesStatus: 'APPROVED',
    pipelineStage: targetStage,
    licencias,
    serviciosAdicionales: finalServicios,
    anexo,
    proposal: mergedProposal ?? proposal ?? null,
    salesPipelineVisible: true,
    pipelineHistory: newHistory,
    lastUpdate: new Date().toISOString(),
    // Legal Lab (cutover = Mesa): sin esta marca el listado Lab filtraba solo sandbox.
    ...(skipLegal ? {} : { _legalLab: true })
  };
  const isRenewalDoc = contractData.isRenewal || collectionName === 'renewals_metadata';
  if (isRenewalDoc) {
    payload.isRenewal = true;
    Object.assign(payload, syncRenewalStageFields({
      ...payload,
      stage: skipLegal ? 'IMPLEMENTACION' : 'LEGAL'
    }));
    if (!skipLegal && !existing.legalStatus) {
      payload.legalStatus = 'PENDING';
    }
    if (!existing.movementType) payload.movementType = 'Renovación';
    // El modal enriquece CSF/ID/acta desde el contrato original (display). Persistimos
    // para que Mesa Legal no dependa del merge y `stage` coincida con el tablero.
    const inheritKeys = [
      'csf_url', 'id_url', 'acta_url', 'razon_social', 'nombre_comercial',
      'rfc', 'entityType', 'tipo_persona', 'contractDocUrl'
    ];
    inheritKeys.forEach((key) => {
      if (!existing[key] && contractData[key]) payload[key] = contractData[key];
    });
    if (
      (payload.csf_url || existing.csf_url || payload.id_url || existing.id_url || payload.acta_url || existing.acta_url)
      && existing.onboardingCrmStatus !== 'COMPLETADO'
    ) {
      payload.onboardingCrmStatus = 'COMPLETADO';
      payload.onboardingStatus = 'COMPLETADO';
    }
  }
  if (skipLegal) {
    payload.legalStatus = LEGAL_STATUS_NOT_REQUIRED;
    payload.legalSkipReason = LEGAL_SKIP_REASON_GEMINI_EXISTING;
  }

  await updateDoc(contractRef, omitUndefinedDeep(payload));

  const crmLeadId = contractData.crmLeadId || contractData.proposalMicrositeToken;
  if (crmLeadId && mergedProposal) {
    try {
      const leadRef = doc(db, 'crm_leads', crmLeadId);
      const snap = await getDoc(leadRef);
      if (snap.exists()) {
        await updateDoc(leadRef, {
          proposal: mergedProposal,
          updatedAt: serverTimestamp()
        });
      }
    } catch (e) {
      console.warn('No se pudo sincronizar servicios al confirmar propuesta:', e);
    }
  }

  if (skipLegal) {
    const fullData = { ...existing, ...contractData };
    triggerNotification('SIGNED', {
      comercialEmail: fullData.ejecutivoAsignado?.email,
      directorEmail: 'christian@plexarian.com',
      razonSocial: fullData.razon_social || fullData.nombre_comercial || fullData.clientName || 'Cliente Desconocido'
    });
    try {
      const domain = (fullData.dominio || fullData.domain || '').trim();
      if (domain) {
        await generateDnsSetupGuide(contractId, { force: false });
      }
    } catch (dnsErr) {
      console.error('Error generando guía DNS al saltar Legal (Gemini existente):', dnsErr);
    }
  } else {
    triggerNotification('PROPOSAL_UPDATED', {
      legalEmail: 'legal@plexarian.com',
      razonSocial: contractData.razon_social || contractData.clientName || contractData.nombre_comercial || 'Cliente',
      ejecutivo: contractData.ejecutivoAsignado?.name
    });
  }

  return { skippedLegal: skipLegal, pipelineStage: targetStage };
};

/** Garantiza marcadores de tablero tras invitación CRM → Dashboard (Rev. Com.). */
const persistCrmOnboardingDashboardMarkers = async (contractId, lead = {}, overrides = {}) => {
  if (!contractId) return;
  const onboardingSent = overrides.onboardingStatus === 'ENVIADA'
    || overrides.onboardingSentAt
    || overrides.onboardingCrmStatus === 'PENDIENTE_CLIENTE'
    || lead.onboardingStatus === 'ENVIADA'
    || lead.onboardingSentAt;
  if (!onboardingSent) return;

  const contractRef = doc(db, 'contratos', contractId);
  const snap = await getDoc(contractRef).catch(() => null);
  const existing = snap?.exists() ? snap.data() : {};
  const reopenFromCierre = onboardingInvitePipelinePatch(existing);
  const preserveStage = shouldPreserveAdvancedPipelineStage(existing) && !reopenFromCierre;
  if (preserveStage) {
    await updateContractInDB(contractId, { salesPipelineVisible: true });
    return;
  }

  await updateContractInDB(contractId, {
    salesPipelineVisible: true,
    pipelineStage: 'SALES_REVIEW',
    salesStatus: 'PENDING',
    ...(reopenFromCierre || {}),
    onboardingStatus: overrides.onboardingStatus || lead.onboardingStatus || 'ENVIADA',
    onboardingSentAt: overrides.onboardingSentAt || lead.onboardingSentAt || new Date().toISOString(),
    onboardingCrmStatus: overrides.onboardingCrmStatus || 'PENDIENTE_CLIENTE',
    clientOrigin: existing.clientOrigin || 'crm_onboarding_invite'
  });
};

/** Crea o actualiza contrato en Dashboard vinculado al lead (por dominio). */
export const syncCrmLeadToDashboardContract = async (lead, overrides = {}) => {
  const leadId = lead?.id || overrides.crmLeadId;
  const freshLead = leadId ? await getLeadById(leadId).catch(() => null) : null;
  const mergedLead = freshLead && !freshLead.isContract
    ? { ...lead, ...freshLead, id: leadId || freshLead.id }
    : lead;

  if (!shouldSyncCrmLeadToDashboard(mergedLead, overrides)) {
    return null;
  }

  const executivesList = overrides._executivesCache?.length
    ? overrides._executivesCache
    : await getExecutives().catch(() => []);

  const payload = buildContractPayloadFromCrmLead(mergedLead, overrides, executivesList);
  if (!payload.ejecutivoAsignado && mergedLead.executiveEmail) {
    payload.ejecutivoAsignado = resolveEjecutivoAsignado(mergedLead, overrides, executivesList)
      || ejecutivoFromEmailFields(
        mergedLead.executiveEmail,
        mergedLead.executiveName,
        mergedLead.executiveId
      );
  }
  const domain = payload.dominio || payload.domain;
  if (!domain) {
    throw new Error('El prospecto debe tener dominio para pasar al Dashboard.');
  }

  const isCrmResync = overrides.forceDashboardSync === true;
  const isOnboardingSync = !isCrmResync && (
    overrides.onboardingStatus === 'ENVIADA'
    || overrides.onboardingSentAt
    || overrides.onboardingCrmStatus === 'PENDIENTE_CLIENTE'
    || mergedLead.onboardingStatus === 'ENVIADA'
  );

  const existing = await fetchContractByDomain(domain);
  if (existing?.id) {
    const reopenFromCierre = isOnboardingSync ? onboardingInvitePipelinePatch(existing) : null;
    const preserveStage = shouldPreserveAdvancedPipelineStage(existing) && !reopenFromCierre;
    const updatePayload = {
      ...payload,
      pipelineStage: (isOnboardingSync && !preserveStage)
        ? 'SALES_REVIEW'
        : (existing.pipelineStage || payload.pipelineStage || 'SALES_REVIEW'),
      salesPipelineVisible: isOnboardingSync
        ? true
        : (payload.salesPipelineVisible ?? existing.salesPipelineVisible ?? false),
      salesStatus: existing.salesStatus || payload.salesStatus || 'PENDING',
      history: mergeHistories(existing.history, payload.history),
      managementNotes: (() => {
        const byKey = new Map();
        for (const n of [...(existing.managementNotes || []), ...(payload.managementNotes || [])]) {
          const key = n.id || `${n.historyTimestamp || ''}|${n.text || ''}`;
          byKey.set(key, n);
        }
        return [...byKey.values()];
      })()
    };
    if (payload.masterStatus === 'BAJA' || Number(payload.phase) === 4) {
      updatePayload.salesPipelineVisible = false;
      updatePayload.masterStatus = 'BAJA';
      updatePayload.phase = 4;
      updatePayload.removedFromSalesPipelineReason = 'CRM_BAJA_COMERCIAL';
      updatePayload.crmLeadMasterStatus = 'BAJA';
      updatePayload.crmLeadPhase = 4;
    }
    const ejecutivoFinal = pickEjecutivoForContract(mergedLead, overrides, executivesList)
      || (isEjecutivoAsignadoValid(payload.ejecutivoAsignado) ? payload.ejecutivoAsignado : null)
      || (isEjecutivoAsignadoValid(existing.ejecutivoAsignado) ? existing.ejecutivoAsignado : null);
    if (isEjecutivoAsignadoValid(ejecutivoFinal)) {
      updatePayload.ejecutivoAsignado = ejecutivoFinal;
    }
    if (isOnboardingSync) {
      updatePayload.onboardingStatus = overrides.onboardingStatus || mergedLead.onboardingStatus || 'ENVIADA';
      updatePayload.onboardingSentAt = overrides.onboardingSentAt || mergedLead.onboardingSentAt || updatePayload.onboardingSentAt;
      updatePayload.onboardingCrmStatus = overrides.onboardingCrmStatus || updatePayload.onboardingCrmStatus || 'PENDIENTE_CLIENTE';
      updatePayload.salesPipelineVisible = true;
      if (reopenFromCierre) {
        Object.assign(updatePayload, reopenFromCierre);
      } else if (!preserveStage) {
        updatePayload.pipelineStage = 'SALES_REVIEW';
      } else {
        updatePayload.pipelineStage = existing.pipelineStage || updatePayload.pipelineStage;
        if (existing.salesStatus) updatePayload.salesStatus = existing.salesStatus;
        if (existing.legalStatus) updatePayload.legalStatus = existing.legalStatus;
      }
    }
    const chosenProposal = preferCompleteProposal(existing.proposal, payload.proposal);
    if (chosenProposal && hasValidCommercialProposal(chosenProposal)) {
      updatePayload.proposal = chosenProposal;
      updatePayload.licencias = mergeProposalLicenciaFields(
        existing.licencias || [],
        resolveLicenciasFromProposal(chosenProposal, [])
      );
      const fromProposalServices = resolveServiciosFromProposal(chosenProposal, []);
      updatePayload.serviciosAdicionales = fromProposalServices.length
        ? fromProposalServices
        : (existing.serviciosAdicionales || []);
      updatePayload.anexo = buildAnexoFromProposal(chosenProposal);
    } else {
      if (!updatePayload.proposal && existing.proposal) {
        updatePayload.proposal = existing.proposal;
      }
      if ((!updatePayload.licencias || updatePayload.licencias.length === 0) && existing.licencias?.length) {
        updatePayload.licencias = existing.licencias;
      }
    }
    if (payload.crmLeadId) updatePayload.crmLeadId = payload.crmLeadId;
    if (payload.proposalMicrositeToken) updatePayload.proposalMicrositeToken = payload.proposalMicrositeToken;
    if (payload.proposalMicrositePath) updatePayload.proposalMicrositePath = payload.proposalMicrositePath;
    if (payload.shortId) updatePayload.shortId = payload.shortId;
    if (payload.propuesta_url) updatePayload.propuesta_url = payload.propuesta_url;
    const mergedGoogleOpportunity = preferGoogleOpportunity(
      existing.googleOpportunity,
      payload.googleOpportunity
    );
    if (mergedGoogleOpportunity) {
      updatePayload.googleOpportunity = mergedGoogleOpportunity;
    } else {
      delete updatePayload.googleOpportunity;
    }
    if (isCrmResync) {
      updatePayload.pipelineStage = existing.pipelineStage || updatePayload.pipelineStage;
      if (existing.pipelineHistory?.length) {
        updatePayload.pipelineHistory = existing.pipelineHistory;
      }
      updatePayload.salesStatus = existing.salesStatus || updatePayload.salesStatus;
      if (existing.legalStatus) updatePayload.legalStatus = existing.legalStatus;
      updatePayload.salesPipelineVisible = existing.salesPipelineVisible ?? updatePayload.salesPipelineVisible;
      delete updatePayload.onboardingStatus;
      delete updatePayload.onboardingSentAt;
      delete updatePayload.onboardingCrmStatus;
      delete updatePayload.clientOrigin;
    }
    await updateContractInDB(existing.id, updatePayload);
    if (isOnboardingSync) {
      await persistCrmOnboardingDashboardMarkers(existing.id, mergedLead, overrides);
    }
    return existing.id;
  }

  const newId = await createContract({
    ...payload,
    _executivesCache: executivesList,
    sourceCollection: 'contratos',
    estado: 'Negociación',
    pipelineStage: 'SALES_REVIEW',
    salesStatus: 'PENDING',
    salesPipelineVisible: isOnboardingSync ? true : (payload.salesPipelineVisible ?? false),
    onboardingStatus: isOnboardingSync ? (overrides.onboardingStatus || mergedLead.onboardingStatus || 'ENVIADA') : undefined,
    onboardingSentAt: isOnboardingSync ? (overrides.onboardingSentAt || mergedLead.onboardingSentAt || new Date().toISOString()) : undefined,
    onboardingCrmStatus: isOnboardingSync ? (overrides.onboardingCrmStatus || 'PENDIENTE_CLIENTE') : undefined,
    clientOrigin: isOnboardingSync ? 'crm_onboarding_invite' : undefined
  });
  if (!payload.crmLeadId && leadId) {
    await updateContractInDB(newId, { crmLeadId: leadId }).catch(() => {});
  }
  if (isOnboardingSync) {
    await persistCrmOnboardingDashboardMarkers(newId, mergedLead, overrides);
  }
  return newId;
};

/** Re-sincroniza datos comerciales del lead CRM sin mover la fase del pipeline. */
export const resyncContractFromCrm = async (contract, executivesCache = []) => {
  const lead = await getCrmLeadForContract(contract);
  if (!lead) {
    throw new Error('No hay lead CRM vinculado a este dominio.');
  }
  return syncCrmLeadToDashboardContract(lead, {
    _executivesCache: executivesCache,
    forceDashboardSync: true
  });
};

export const createContract = async (contractData) => {
  try {
    const { _executivesCache, ...data } = contractData || {};
    const cached = Array.isArray(_executivesCache) ? _executivesCache : [];
    let executive = pickEjecutivoForContract({}, data, cached);
    if (!executive) {
      const execs = await getExecutives().catch(() => []);
      executive = pickEjecutivoForContract({}, data, execs);
    }
    if (!executive) {
      throw new Error(
        `No se pudo asignar ejecutivo al contrato (${data.dominio || data.domain || 'sin dominio'}). `
        + 'Debe venir de la invitación del Dashboard (creatorEmail / ejecutivoAsignado) o del expediente.'
      );
    }

    const docRef = await addDoc(collection(db, "contratos"), omitUndefinedDeep({
      ...data,
      ejecutivoAsignado: executive,
      fechaCreacion: new Date().toISOString(),
      salesStatus: contractData.salesStatus || 'PENDING',
      legalStatus: contractData.legalStatus || 'PENDING',
      estado: contractData.estado || 'Negociación',
      pipelineStage: contractData.pipelineStage || 'SALES_REVIEW',
      salesPipelineVisible: contractData.salesPipelineVisible ?? false,
      anexo: contractData.anexo || DEFAULT_CONTRACT_ANEXO,
      licencias: contractData.licencias || contractData.proposal?.licencias || [],
      serviciosAdicionales: contractData.serviciosAdicionales || contractData.proposal?.serviciosAdicionales || []
    }));
    return docRef.id;
  } catch (e) { throw e; }
};

export const getContracts = async () => {
  const querySnapshot = await getDocs(collection(db, "contratos"));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const subscribeToContracts = (callback, options = {}) => {
  const scopedEmail = normalizeEmail(options.executiveEmail);
  let rawContratos = [];
  let renewalsList = [];
  let priceList = {};
  let notifyTimer = null;

  const enrichRenewalFromContrato = (renewal, contratosByDomain) => {
    const d = (renewal.dominio || renewal.domain || '').trim().toLowerCase();
    const base = d ? contratosByDomain.get(d) : null;
    if (!base) return enrichContractForDisplay(renewal, priceList);
    const mergedProposal = {
      ...(base.proposal || {}),
      ...(renewal.proposal || {}),
      totalMensual: Number(renewal.proposal?.totalMensual) > 0
        ? renewal.proposal.totalMensual
        : (base.proposal?.totalMensual || 0),
      totalContrato: Number(renewal.proposal?.totalContrato) > 0
        ? renewal.proposal.totalContrato
        : (base.proposal?.totalContrato || 0)
    };
    const mergedForLic = { ...base, ...renewal, proposal: mergedProposal };
    const licencias = resolveCommercialLicencias(mergedForLic);
    const mergedAnexo = Number(renewal.anexo?.totalMensual) > 0
      ? renewal.anexo
      : (Number(base.anexo?.totalMensual) > 0 ? base.anexo : (renewal.anexo || base.anexo));
    const mergedIdentity = mergeRenewalDisplayFromOriginal(base, renewal);
    return enrichContractForDisplay({
      ...mergedIdentity,
      salesPipelineVisible: renewal.salesPipelineVisible !== false,
      licencias,
      serviciosAdicionales: renewal.serviciosAdicionales?.length
        ? renewal.serviciosAdicionales
        : base.serviciosAdicionales,
      proposal: mergedProposal,
      propuesta_url: renewal.propuesta_url || base.propuesta_url,
      lista_usuarios_url: renewal.lista_usuarios_url || base.lista_usuarios_url || base.userListSheetUrl,
      userListSheetUrl: renewal.userListSheetUrl || base.userListSheetUrl || base.lista_usuarios_url,
      csf_url: renewal.csf_url || base.csf_url,
      id_url: renewal.id_url || base.id_url,
      acta_url: renewal.acta_url || base.acta_url,
      contractDocUrl: renewalContractDocUrl(renewal, base.contractDocUrl),
      fad_zip_url: renewal.previousFadZipUrl && !renewal.fad_zip_url
        ? null
        : (renewal.fad_zip_url || base.fad_zip_url),
      contactos: renewal.contactos?.admin?.email ? renewal.contactos : base.contactos,
      anexo: mergedAnexo,
      razon_social: renewal.razon_social || base.razon_social,
      nombre_comercial: renewal.nombre_comercial || base.nombre_comercial,
      migrationReportId: renewal.migrationReportId || base.migrationReportId,
      crmLeadId: renewal.crmLeadId || base.crmLeadId
    }, priceList);
  };

  const flushNotify = () => {
    notifyTimer = null;
    const contratosList = rawContratos.map((c) => enrichContractForDisplay(c, priceList));
    const contratosByDomain = new Map();
    contratosList.forEach((c) => {
      const d = (c.dominio || c.domain || '').trim().toLowerCase();
      if (d) contratosByDomain.set(d, c);
    });
    const enrichedRenewals = renewalsList.map((r) => enrichRenewalFromContrato(r, contratosByDomain));
    const renewalIds = new Set(enrichedRenewals.map((r) => r.id));
    const visibleContratos = filterContratosHiddenByActiveRenewal(contratosList, enrichedRenewals)
      .filter((c) => !renewalIds.has(c.id));
    const all = [...visibleContratos, ...enrichedRenewals];
    scheduleEjecutivoBackfill(all);
    scheduleContractDisplayBackfill(all);
    schedulePipelineStageAlign(all);
    const aligned = all.map((c) => {
      const patch = buildPipelineStageAlignPatch(c);
      return patch ? { ...c, ...patch } : c;
    });
    callback(aligned);
  };

  const notify = () => {
    if (notifyTimer) clearTimeout(notifyTimer);
    notifyTimer = setTimeout(flushNotify, 80);
  };

  getPricesFromDB()
    .then((p) => {
      priceList = p || {};
      notify();
    })
    .catch((e) => console.warn('[Nexus] Catálogo de precios para montos Kanban:', e?.message || e));

  const applyContratosSnap = (querySnapshot) => {
    rawContratos = querySnapshot.docs.map((docSnap) => ({
      ...docSnap.data(),
      id: docSnap.id
    }));
    notify();
  };
  const qAllContratos = query(collection(db, "contratos"));
  let stopContratos = () => {};
  let contratosRetryTimer = null;
  let contratosRetry = 0;
  let closed = false;
  const listenAllContratos = () => {
    stopContratos = onSnapshot(
      qAllContratos,
      (snap) => {
        contratosRetry = 0;
        applyContratosSnap(snap);
      },
      (error) => {
        console.error("Error en tiempo real contratos:", error);
        notify();
        if (closed) return;
        const wait = Math.min(15000, 1500 * (2 ** contratosRetry));
        contratosRetry += 1;
        try { stopContratos(); } catch (_) { /* listener ya cerrado */ }
        clearTimeout(contratosRetryTimer);
        contratosRetryTimer = setTimeout(listenAllContratos, wait);
      }
    );
  };
  if (scopedEmail) {
    const qScoped = query(
      collection(db, "contratos"),
      or(
        where("ejecutivoAsignado.email", "==", scopedEmail),
        where("creatorEmail", "==", scopedEmail),
        where("executiveEmail", "==", scopedEmail),
        where("proposal.executiveEmail", "==", scopedEmail)
      )
    );
    stopContratos = onSnapshot(qScoped, applyContratosSnap, (error) => {
      console.warn('[Nexus] Contratos filtrados no disponibles, usando tablero completo:', error?.message || error);
      stopContratos();
      listenAllContratos();
    });
  } else {
    listenAllContratos();
  }

  const renewalBelongs = (r) => {
    if (!scopedEmail) return true;
    return normalizeEmail(r.ejecutivoAsignado?.email) === scopedEmail
      || normalizeEmail(r.executiveEmail) === scopedEmail
      || normalizeEmail(r.creatorEmail) === scopedEmail
      || normalizeEmail(r.proposal?.executiveEmail) === scopedEmail;
  };

  const q2 = query(collection(db, "renewals_metadata"), where("stage", "in", ["PROPUESTA", "LEGAL", "FIRMA", "IMPLEMENTACION", "CIERRE"]));
  let stopRenewals = () => {};
  let renewalsRetryTimer = null;
  let renewalsRetry = 0;
  const applyRenewalsSnap = (querySnapshot) => {
    renewalsList = querySnapshot.docs.map(doc => {
      const data = doc.data();
      const dominio = data.domain || data.customer || doc.id;
      const execEmail = data.executiveEmail || data.proposal?.executiveEmail || data.ejecutivoAsignado?.email;
      const execName = data.executiveName || data.proposal?.executiveName || data.ejecutivoAsignado?.name;
      const ejecutivoAsignado = isEjecutivoAsignadoValid(data.ejecutivoAsignado)
        ? data.ejecutivoAsignado
        : (execEmail ? { email: execEmail, name: execName || execEmail, id: data.ejecutivoAsignado?.id || '' } : null);
      return {
        ...data,
        id: doc.id,
        dominio,
        domain: dominio,
        isRenewal: true,
        pipelineStage: renewalStageToPipelineStage(data),
        salesStatus: data.salesStatus || (data.stage === 'PROPUESTA' && data.onboardingCrmStatus === 'PENDIENTE_CLIENTE' ? 'PENDING' : 'APPROVED'),
        executiveEmail: execEmail,
        executiveName: execName,
        ejecutivoAsignado: ejecutivoAsignado || data.ejecutivoAsignado,
        creatorEmail: data.creatorEmail
      };
    }).filter((r) => {
      if (r.stage === 'PROPUESTA') {
        const onboardingReady = Boolean(
          r.onboardingCrmStatus === 'PENDIENTE_CLIENTE'
          || r.onboardingCrmStatus === 'COMPLETADO'
          || r.onboardingSentAt
          || r.onboardingStatus === 'ENVIADA'
          || r.renewal_onboarding_completed
        );
        if (!onboardingReady) return false;
      }
      return renewalBelongs(r);
    });
    notify();
  };
  const listenRenewals = () => {
    stopRenewals = onSnapshot(
      q2,
      (snap) => {
        renewalsRetry = 0;
        applyRenewalsSnap(snap);
      },
      (error) => {
        console.error("Error en tiempo real renewals:", error);
        notify();
        if (closed) return;
        const wait = Math.min(15000, 1500 * (2 ** renewalsRetry));
        renewalsRetry += 1;
        try { stopRenewals(); } catch (_) { /* listener ya cerrado */ }
        clearTimeout(renewalsRetryTimer);
        renewalsRetryTimer = setTimeout(listenRenewals, wait);
      }
    );
  };
  listenRenewals();

  return () => {
    closed = true;
    if (notifyTimer) clearTimeout(notifyTimer);
    clearTimeout(contratosRetryTimer);
    clearTimeout(renewalsRetryTimer);
    stopContratos();
    stopRenewals();
  };
};

/** Replica expediente y datos comerciales a todos los registros del mismo dominio. */
export const syncContractFieldsByDomain = async (domain, patch, excludeId = null) => {
  const d = (domain || '').trim().toLowerCase();
  const syncPatch = pickDomainSyncPatch(patch);
  if (!d || !Object.keys(syncPatch).length) return { updated: 0 };

  syncPatch.lastDomainSyncAt = new Date().toISOString();
  const collections = ['contratos', 'crm_leads', 'invitations', 'renewals_metadata'];
  let updated = 0;

  for (const collName of collections) {
    for (const field of ['dominio', 'domain']) {
      const q = query(collection(db, collName), where(field, '==', d));
      const snap = await getDocs(q);
      for (const docSnap of snap.docs) {
        if (excludeId && docSnap.id === excludeId) continue;
        await setDoc(docSnap.ref, omitUndefinedDeep(syncPatch), { merge: true });
        updated += 1;
      }
    }
  }
  return { updated };
};

export { assessContractIntegrity } from '../utils/contractIntegrity';

const RENEWAL_PIPELINE_STAGES = new Set(['PROPUESTA', 'LEGAL', 'FIRMA', 'IMPLEMENTACION', 'CIERRE']);

function isRenewalMetadataRecord(data = {}) {
  return Boolean(
    data.isRenewal
    || data.renewal_onboarding_completed
    || (data.stage && RENEWAL_PIPELINE_STAGES.has(String(data.stage).toUpperCase()))
  );
}

async function resolveContractCollectionName(id, updates = {}) {
  if (updates.isRenewal === true) return 'renewals_metadata';
  if (updates.isRenewal === false) return 'contratos';
  const renSnap = await getDoc(doc(db, 'renewals_metadata', id));
  if (renSnap.exists() && isRenewalMetadataRecord(renSnap.data())) {
    return 'renewals_metadata';
  }
  const conSnap = await getDoc(doc(db, 'contratos', id));
  if (conSnap.exists()) return 'contratos';
  if (renSnap.exists()) return 'renewals_metadata';
  return 'contratos';
}

/**
 * Admin/Dirección: vuelve a mostrar un expediente cerrado/oculto en el kanban comercial.
 * No cambia pipelineStage ni crea lead CRM.
 */
export const reopenContractInSalesPipeline = async (contractId, options = {}) => {
  if (!contractId) throw new Error('Expediente inválido.');
  const collectionName = await resolveContractCollectionName(contractId, {
    isRenewal: options.isRenewal === true ? true : (options.isRenewal === false ? false : undefined)
  });
  const contractRef = doc(db, collectionName, contractId);
  const snap = await getDoc(contractRef);
  if (!snap.exists()) throw new Error('No se encontró el expediente.');

  const now = new Date().toISOString();
  const actor = options.actorName || options.actorEmail || 'admin';
  const historyEntry = {
    action: 'REOPEN_SALES_PIPELINE',
    title: 'Reabierto en pipeline comercial',
    description: `Visible de nuevo en el tablero comercial (${actor}).`,
    timestamp: now,
    by: options.actorEmail || actor
  };

  const existing = snap.data() || {};
  const history = Array.isArray(existing.history) ? [...existing.history, historyEntry] : [historyEntry];

  await updateDoc(contractRef, {
    salesPipelineVisible: true,
    removedFromSalesPipelineReason: deleteField(),
    lastUpdate: now,
    history: arrayUnion(historyEntry)
  });

  return {
    id: contractId,
    salesPipelineVisible: true,
    removedFromSalesPipelineReason: null,
    history
  };
};

/**
 * Admin/Dirección: mueve un expediente cerrado/operativo a Rev. Comercial
 * para cotizar producto nuevo / upsell sin crear lead CRM.
 */
export const moveContractToCommercialReview = async (contractId, options = {}) => {
  if (!contractId) throw new Error('Expediente inválido.');
  const collectionName = await resolveContractCollectionName(contractId, {
    isRenewal: options.isRenewal === true ? true : (options.isRenewal === false ? false : undefined)
  });
  const contractRef = doc(db, collectionName, contractId);
  const snap = await getDoc(contractRef);
  if (!snap.exists()) throw new Error('No se encontró el expediente.');

  const existing = snap.data() || {};
  const merged = { id: contractId, ...existing, ...options.contractPatch };
  const integrity = assessContractIntegrity(merged, 'SALES_REVIEW');
  if (!integrity.canAdvance) {
    throw new Error(integrity.message || 'No se puede mover a Rev. Comercial.');
  }

  const now = new Date().toISOString();
  const actor = options.actorName || options.actorEmail || 'admin';
  const historyEntry = {
    action: 'MOVE_TO_COMMERCIAL_REVIEW',
    title: 'Enviado a Rev. Comercial',
    description: `Upsell / producto nuevo: pasado a Rev. Comercial por ${actor}.`,
    timestamp: now,
    by: options.actorEmail || actor,
    fromStage: existing.pipelineStage || null,
    toStage: 'SALES_REVIEW'
  };

  const pipelineHistory = [
    ...(Array.isArray(existing.pipelineHistory) ? existing.pipelineHistory : []),
    { stage: 'SALES_REVIEW', date: now, note: 'Reapertura comercial (admin)' }
  ];
  const history = Array.isArray(existing.history) ? [...existing.history, historyEntry] : [historyEntry];

  const updates = {
    pipelineStage: 'SALES_REVIEW',
    salesStatus: 'PENDING',
    estado: 'Negociación',
    status: 'Negociación',
    salesPipelineVisible: true,
    removedFromSalesPipelineReason: deleteField(),
    lastUpdate: now,
    pipelineHistory,
    history: arrayUnion(historyEntry)
  };

  if (collectionName === 'renewals_metadata' || existing.isRenewal) {
    updates.stage = 'PROPUESTA';
    updates.isRenewal = true;
  }

  await updateDoc(contractRef, updates);

  return {
    id: contractId,
    pipelineStage: 'SALES_REVIEW',
    salesStatus: 'PENDING',
    estado: 'Negociación',
    status: 'Negociación',
    salesPipelineVisible: true,
    removedFromSalesPipelineReason: null,
    pipelineHistory,
    history
  };
};

export const updateContractInDB = async (id, updates) => {
  const collectionName = await resolveContractCollectionName(id, updates);

  // If it's a renewal being updated by Legal, we also want to update the 'stage' field for the Kanban
  if (updates.isRenewal && updates.legalStatus) {
      if (updates.legalStatus === 'APPROVED') {
          updates.stage = 'FIRMA';
      }
  }

  const contractRef = doc(db, collectionName, id);
  const existingSnap = await getDoc(contractRef);
  const existing = existingSnap.exists() ? existingSnap.data() : {};
  const allowPipelineRegress = updates.allowPipelineRegress === true;
  delete updates.allowPipelineRegress;

  if (
    shouldPreserveAdvancedPipelineStage(existing)
    && !allowPipelineRegress
    && ['SALES_REVIEW', 'ONBOARDING'].includes(String(updates.pipelineStage || '').toUpperCase())
  ) {
    delete updates.pipelineStage;
    if (updates.salesStatus === 'PENDING' && existing.salesStatus && existing.salesStatus !== 'PENDING') {
      delete updates.salesStatus;
    }
    if (
      updates.legalStatus === 'PENDING'
      && existing.legalStatus
      && existing.legalStatus !== 'PENDING'
    ) {
      delete updates.legalStatus;
    }
  }

  if (updates.proposal) {
    updates.proposal = mergePreserveProposalLines(
      existing.proposal,
      updates.proposal,
      existing.licencias || updates.licencias
    );
  }
  if (
    Array.isArray(updates.licencias)
    && updates.licencias.length === 0
    && Array.isArray(existing.licencias)
    && existing.licencias.length > 0
  ) {
    delete updates.licencias;
  }
  if (
    updates.anexo
    && Number(existing.anexo?.totalMensual) > 0
    && !(Number(updates.anexo.totalMensual) > 0)
  ) {
    updates.anexo = {
      ...existing.anexo,
      ...updates.anexo,
      totalMensual: existing.anexo.totalMensual,
      totalContrato: existing.anexo.totalContrato || existing.anexo.totalMensual
    };
  }

  // Comercial confirma propuesta → Legal, o Implementación si aplica excepción Gemini existente
  let reopenLegal = null;
  const wantsLegal =
    updates.pipelineStage === 'LEGAL_REVIEW'
    || (updates.salesStatus === 'APPROVED' && (!updates.pipelineStage || updates.pipelineStage === 'LEGAL_REVIEW'));

  if (wantsLegal) {
    const snapForGate = await getDoc(contractRef);
    const existingForGate = snapForGate.exists() ? snapForGate.data() : {};
    const { canAdvanceOnboardingToLegal } = await import('../utils/contractIntegrity');
    const mergedGate = {
      ...existingForGate,
      ...updates,
      salesStatus: updates.salesStatus || existingForGate.salesStatus || 'APPROVED',
      proposal: updates.proposal || existingForGate.proposal,
      licencias: updates.licencias || existingForGate.licencias
    };
    const sendingToLegal = updates.pipelineStage === 'LEGAL_REVIEW';
    if (sendingToLegal && canSkipLegalToImplementation(mergedGate)) {
      updates.pipelineStage = 'IMPLEMENTATION';
      updates.salesStatus = updates.salesStatus || 'APPROVED';
      updates.legalStatus = LEGAL_STATUS_NOT_REQUIRED;
      updates.legalSkipReason = LEGAL_SKIP_REASON_GEMINI_EXISTING;
      if (updates.isRenewal || collectionName === 'renewals_metadata') {
        updates.stage = 'IMPLEMENTACION';
        updates.isRenewal = true;
      }
    } else if ((updates.isRenewal || collectionName === 'renewals_metadata')) {
      if (canAdvanceOnboardingToLegal(mergedGate)) {
        updates.stage = 'LEGAL';
        updates.pipelineStage = 'LEGAL_REVIEW';
        updates.salesStatus = updates.salesStatus || 'APPROVED';
        reopenLegal = buildReopenLegalQueuePatch(existingForGate);
        if (reopenLegal) Object.assign(updates, reopenLegal.set);
      } else {
        // Queda en Rev. Comercial hasta que el cliente complete onboarding
        updates.stage = existingForGate.stage === 'LEGAL' ? 'LEGAL' : 'PROPUESTA';
        updates.pipelineStage = 'SALES_REVIEW';
        updates.salesStatus = updates.salesStatus || 'APPROVED';
        updates.salesPipelineVisible = true;
      }
    }
  }

  let cleaned = omitUndefinedDeep(updates);
  if (reopenLegal) {
    for (const key of reopenLegal.deleteKeys) cleaned[key] = deleteField();
  }

  const shouldSolidify =
    PIPELINE_STAGES_SOLIDIFY_COMMERCIAL.has(cleaned.pipelineStage)
    || (cleaned.legalStatus === 'APPROVED' && cleaned.pipelineStage === 'SIGNING')
    || (updates.isRenewal && ['PROPUESTA', 'LEGAL', 'FIRMA', 'CIERRE'].includes(cleaned.stage));

  if (shouldSolidify) {
    const snap = await getDoc(contractRef);
    const current = snap.exists() ? { id, ...snap.data(), isRenewal: updates.isRenewal } : { id, ...cleaned };
    const catalog = await getPricesFromDB().catch(() => ({}));
    const solid = solidifyCommercialTermsFromProposal({ ...current, ...cleaned }, catalog);
    if (solid) {
      cleaned = {
        ...cleaned,
        ...solid,
        proposal: mergePreserveProposalLines(
          current.proposal,
          solid.proposal,
          current.licencias || cleaned.licencias
        ),
        salesPipelineVisible: true
      };
    }
  }

  if (collectionName === 'renewals_metadata' || cleaned.isRenewal || updates.isRenewal) {
    cleaned = {
      ...cleaned,
      isRenewal: true,
      ...syncRenewalStageFields({ ...cleaned, isRenewal: true })
    };
  }

  const alignedStage = buildPipelineStageAlignPatch({ ...existing, ...cleaned });
  if (alignedStage) {
    cleaned.pipelineStage = alignedStage.pipelineStage;
    cleaned.pipelineHistory = alignedStage.pipelineHistory;
    cleaned.lastUpdate = alignedStage.lastUpdate;
    if (collectionName === 'renewals_metadata' || cleaned.isRenewal || updates.isRenewal) {
      const renewalStage = pipelineStageToRenewalStage(alignedStage.pipelineStage);
      if (renewalStage) cleaned.stage = renewalStage;
    }
  }

  await setDoc(contractRef, cleaned, { merge: true });

  const domain = cleaned.dominio || cleaned.domain;
  if (domain && Object.keys(pickDomainSyncPatch(cleaned)).length > 0) {
    await syncContractFieldsByDomain(domain, cleaned, id).catch((e) => {
      console.warn('[Nexus] syncContractFieldsByDomain:', domain, e.message);
    });
  }
};

/** Guarda el alta de Google en el lead y, si ya hay expediente, en el contrato. */
export const persistGoogleOpportunity = async ({
  leadId = null,
  contractId = null,
  domain = '',
  googleOpportunity
} = {}) => {
  const clean = omitUndefinedDeep(googleOpportunity);
  const slotRequired = clean?.gws?.required === true || clean?.ge?.required === true;
  if (!clean || (clean.required !== true && !slotRequired)) {
    throw new Error('Alta de Google inválida.');
  }
  if (leadId) {
    await updateLeadStatus(leadId, {
      googleOpportunity: clean,
      skipGenericHistory: true
    });
  }
  let resolvedContractId = contractId || null;
  if (!resolvedContractId && domain) {
    const existing = await fetchContractByDomain(String(domain).trim());
    resolvedContractId = existing?.id || null;
  }
  if (resolvedContractId) {
    await updateContractInDB(resolvedContractId, { googleOpportunity: clean });
  }
  return { leadId: leadId || null, contractId: resolvedContractId };
};

/** Reasigna ejecutivo en contratos o renewals_metadata (renovaciones en Kanban Legal). */
export const reassignContract = async (contractId, newExecutiveData, options = {}) => {
  const { isRenewal = false, dominio, domain } = options;
  await updateContractInDB(contractId, {
    ejecutivoAsignado: newExecutiveData,
    isRenewal
  });

  const d = (dominio || domain || '').trim().toLowerCase();
  if (!d) return;

  if (isRenewal) {
    const shadow = await fetchContractByDomain(d);
    if (shadow?.id && shadow.id !== contractId) {
      await updateContractInDB(shadow.id, { ejecutivoAsignado: newExecutiveData });
    }
  }
};

export const deleteContract = async (id) => {
  await deleteDoc(doc(db, "contratos", id));
};

// ==========================================
// 3. GESTIÓN DE EQUIPO (IAM)
// ==========================================
export const getExecutives = async () => {
  // Broad list to catch all possible sales roles
  const salesRoles = [
    "sales", "comercial", "comerciales", "ventas", "ejecutivo", "ejecutivos", 
    "ejecutivo comercial", "ejecutivos comerciales", "account executive",
    "mark & sales", "comercial y renovaciones", "renovaciones y comercial", "pre-venta",
    "comercial & agenda skillsync"
  ];
  const q = query(collection(db, "team_members"), where("role", "in", salesRoles));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getAllTeamMembers = async () => {
  const querySnapshot = await getDocs(collection(db, "team_members"));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

/** Mes en que el ejecutivo sale de capacitación y entra a la etapa 1 de Planet. */
export const setPlanetStart = async (dbId, monthKey) => {
  if (!dbId || !/^\d{4}-\d{2}$/.test(String(monthKey || ''))) return false;
  await updateDoc(doc(db, 'team_members', dbId), {
    planetStart: monthKey,
    planetTraining: false,
    updatedAt: serverTimestamp()
  });
  return true;
};

export const addTeamMember = async (memberData) => {
  await addDoc(collection(db, "team_members"), {
    ...memberData,
    active: true,
    createdAt: new Date().toISOString()
  });
};

export const deleteTeamMember = async (id) => {
  await deleteDoc(doc(db, "team_members", id));
};

export const addExecutive = addTeamMember;
export const deleteExecutive = deleteTeamMember;

// ==========================================
// 4. GESTIÓN DE VIGENCIAS
// ==========================================
export const getTerms = async () => {
  try {
    const snapshot = await getDocs(collection(db, "terms"));
    return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
  } catch (e) {
    console.error("Error obteniendo vigencias:", e);
    return mergeCommercialTerms([]);
  }
};

export const addTerm = async (termData) => {
  await addDoc(collection(db, "terms"), termData);
};

export const deleteTerm = async (id) => {
  await deleteDoc(doc(db, "terms", id));
};

// ==========================================
// 5. GESTIÓN DE SERVICIOS ADICIONALES
// ==========================================
export const getExtraServices = async () => {
  try {
    const snapshot = await getDocs(collection(db, "servicios_extra"));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (e) {
    console.error("Error obteniendo servicios extra:", e);
    return [];
  }
};

export const addExtraService = async (serviceData) => {
  await addDoc(collection(db, "servicios_extra"), serviceData);
};

export const deleteExtraService = async (id) => {
  await deleteDoc(doc(db, "servicios_extra", id));
};

const domainHostingAuthFetch = async (endpoint, { method = 'GET', body, signal } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: body != null ? JSON.stringify(body) : undefined,
    signal
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Error ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

export const getDomainHostingCatalog = async () => {
  const data = await domainHostingAuthFetch('/api/domain-hosting/clients');
  return Array.isArray(data?.clients) ? data.clients : [];
};

export const searchDomainHostingClients = (q, signal) => (
  domainHostingAuthFetch(`/api/domain-hosting/search?q=${encodeURIComponent(q)}`, { signal })
);

export const setDomainHostingOnClient = ({
  contractId,
  domain,
  enabled,
  anniversaryMonth,
  aliases,
  status,
  notes,
  billOnDomain
} = {}) => (
  domainHostingAuthFetch('/api/domain-hosting', {
    method: 'PATCH',
    body: { contractId, domain, enabled, anniversaryMonth, aliases, status, notes, billOnDomain }
  })
);

export const seedDomainHostingCatalog = (body = {}) => (
  domainHostingAuthFetch('/api/domain-hosting/seed', { method: 'POST', body })
);

export const injectDomainHosting = ({ domain, dryRun = false } = {}) => (
  domainHostingAuthFetch('/api/domain-hosting/inject', {
    method: 'POST',
    body: {
      domain: domain || undefined,
      dryRun: dryRun ? '1' : '0'
    }
  })
);

// ==========================================
// 6. GESTIÓN DE KANBAN Y NOTIFICACIONES
// ==========================================

export const inviteToSkillSync = async (contractId, fullData, sendEmail = false, currentUser = null, origen = 'INVITACION_MANUAL') => {
  try {
    const q = query(collection(db, "skillsync_tickets"), where("contractId", "==", contractId));
    const qs = await getDocs(q);
    let ticketId;
    let ticketData;
    if (qs.empty) {
      const settingsSnap = await getDoc(doc(db, "settings", "skillsync"));
      let dynamicWorkshops = {};
      
      if (settingsSnap.exists() && settingsSnap.data().workshops) {
        settingsSnap.data().workshops.forEach(ws => {
          dynamicWorkshops[ws.id] = 'PENDING';
        });
      }
      
      ticketData = {
        contractId: contractId,
        nombreComercial: fullData.nombre_comercial || fullData.razon_social || fullData.customerName || "Cliente",
        dominio: fullData.dominio || fullData.customerDomain || "N/A",
        contactoNombre: fullData.contactos?.admin?.nombre || fullData.contactoNombre || "N/A",
        contactoEmail: fullData.contactos?.admin?.email || fullData.contactoEmail || "N/A",
        contactoTelefono: fullData.contactos?.admin?.telefono || fullData.contactoTelefono || "N/A",
        licencias: fullData.licencias || fullData.proposal?.licencias || fullData.anexo?.licencias || [],
        workshops: dynamicWorkshops,
        origen: origen,
        estado: 'ACTIVO',
        createdAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, "skillsync_tickets"), ticketData);
      ticketId = docRef.id;
      ticketData.id = ticketId;
      console.log("💳 SkillSync ticket autogenerado para: ", ticketData.dominio);
    } else {
      ticketId = qs.docs[0].id;
      ticketData = { id: ticketId, ...qs.docs[0].data() };
      
      // Update contact details on the existing ticket if they were provided
      const updates = {};
      if (fullData.contactos?.admin?.nombre) {
        updates.contactoNombre = fullData.contactos.admin.nombre;
        ticketData.contactoNombre = fullData.contactos.admin.nombre;
      }
      if (fullData.contactos?.admin?.email) {
        updates.contactoEmail = fullData.contactos.admin.email;
        ticketData.contactoEmail = fullData.contactos.admin.email;
      }
      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, "skillsync_tickets", ticketId), updates);
      }
    }

    if (sendEmail) {
      if (!ticketData.contactoEmail || ticketData.contactoEmail === 'N/A') {
        throw new Error("El cliente no tiene un email de contacto registrado en admin.contactos.");
      }
      if (!ticketData.dominio || ticketData.dominio === 'N/A') {
        throw new Error("El cliente no tiene un dominio válido registrado.");
      }
      const executiveEmail = currentUser?.email || 'christian@plexarian.com';
      const executiveName = currentUser?.displayName || currentUser?.name || 'SkillSync Training Team';
      
      await apiCall('/api/skillsync/send-form', 'POST', {
        ticketId: ticketId,
        clientEmail: ticketData.contactoEmail,
        clientName: ticketData.contactoNombre,
        companyName: ticketData.nombreComercial,
        domain: ticketData.dominio,
        executiveEmail: executiveEmail,
        executiveName: executiveName
      });
    }
  } catch (e) {
    console.error("Error en inviteToSkillSync: ", e);
    throw e;
  }
};

export const updateContractStage = async (contractId, newStage, contractData = {}) => {
  try {
    const isRenewal = contractData.isRenewal;
    const collectionName = isRenewal ? "renewals_metadata" : "contratos";
    const contractRef = doc(db, collectionName, contractId);
    const isInvoiced = newStage === 'CLOSED';

    const docSnap = await getDoc(contractRef);
    let fullData = contractData;
    if (docSnap.exists()) {
      const dataFromDb = docSnap.data();
      fullData = { ...dataFromDb, ...contractData };
    }

    const integrity = assessContractIntegrity(fullData, newStage);
    if (!integrity.canAdvance) {
      throw new Error(integrity.message);
    }
    if (integrity.warnings?.length) {
      console.warn('[Nexus integridad]', fullData.dominio || contractId, integrity.warnings.join(' '));
    }

    const existingHistory = fullData.pipelineHistory || [];
    const newHistory = [...existingHistory, { stage: newStage, date: new Date().toISOString() }];

    const updates = {
      pipelineStage: newStage,
      lastUpdate: new Date().toISOString(),
      status: isInvoiced ? 'ACTIVE' : 'Negociación',
      pipelineHistory: newHistory
    };

    if (newStage === 'IMPLEMENTATION' && canSkipLegalToImplementation(fullData)) {
      updates.salesStatus = 'APPROVED';
      updates.legalStatus = LEGAL_STATUS_NOT_REQUIRED;
      updates.legalSkipReason = LEGAL_SKIP_REASON_GEMINI_EXISTING;
    }

    if (isRenewal) {
      const renewalStage = pipelineStageToRenewalStage(newStage);
      if (renewalStage) updates.stage = renewalStage;
      if (isInvoiced) updates.facturado = true;
    }

    if (isInvoiced) {
      const lockedAdjustment = lockBillingAdjustmentOnInvoice(fullData);
      if (lockedAdjustment) updates.billingAdjustment = lockedAdjustment;
    }

    if (PIPELINE_STAGES_SOLIDIFY_COMMERCIAL.has(newStage)) {
      const catalog = await getPricesFromDB().catch(() => ({}));
      const solid = solidifyCommercialTermsFromProposal(fullData, catalog);
      if (solid) {
        updates.licencias = solid.licencias;
        updates.anexo = solid.anexo;
        updates.serviciosAdicionales = solid.serviciosAdicionales;
        if (solid.proposal) updates.proposal = solid.proposal;
      }
    }

    await updateDoc(contractRef, updates);

    if (!fullData.razon_social || !fullData.ejecutivoAsignado || !fullData.contactos) {
      // Ya se obtuvo fullData arriba si existía
    }

    if (newStage === 'IMPLEMENTATION') {
      triggerNotification('SIGNED', {
        comercialEmail: fullData.ejecutivoAsignado?.email,
        directorEmail: 'christian@plexarian.com',
        razonSocial: fullData.razon_social || "Cliente Desconocido"
      });

      // Guía DNS automática (TTL 30 días) — no bloquea el avance del kanban
      try {
        const domain = (fullData.dominio || fullData.domain || '').trim();
        if (domain) {
          await generateDnsSetupGuide(contractId, { force: false });
        }
      } catch (dnsErr) {
        console.error('Error generando guía DNS al entrar a IMPLEMENTATION:', dnsErr);
      }
    }

    const hasBeenInBilling = existingHistory.some(h => h.stage === 'BILLING' || h.stage === 'CLOSED');

    if ((newStage === 'BILLING' || (newStage === 'CLOSED' && !isInvoiced)) && !hasBeenInBilling) {
      triggerNotification('IMPLEMENTATION_DONE', {
        comercialEmail: fullData.ejecutivoAsignado?.email,
        accountingEmail: 'facturacion@plexarian.com',
        razonSocial: fullData.razon_social || "Cliente Desconocido",
        dominio: fullData.dominio || "Sin Dominio",
        ejecutivoNombre: fullData.ejecutivoAsignado?.name || "Ejecutivo Plexarian",
        csfUrl: fullData.csf_url || null
      });

      // --- CREAR TICKET SKILLSYNC AUTOMÁTICAMENTE ---
      try {
        // Marcamos origen 'NUEVO_CONTRATO' cuando viene del Kanban
        await inviteToSkillSync(contractId, fullData, false, null, 'NUEVO_CONTRATO');
      } catch (err) {
        console.error("Error al generar ticket SkillSync desde Kanban: ", err);
      }
      // ----------------------------------------------
    }

    if (isInvoiced) {
      const adminContact = fullData.contactos?.admin || {};
      const pagosContact = fullData.contactos?.pagos || {};

      triggerNotification('INVOICED', {
        comercialEmail: fullData.ejecutivoAsignado?.email,
        razonSocial: fullData.razon_social,
        nombreComercial: fullData.nombre_comercial || fullData.razon_social,
        dominio: fullData.dominio,
        contactosCliente: [
          { email: adminContact.email, nombre: adminContact.nombre },
          { email: pagosContact.email, nombre: pagosContact.nombre }
        ].filter(c => c.email),
        isRenewal: fullData.isRenewal
      });
    }

  } catch (e) { console.error("Error moviendo tarjeta:", e); throw e; }
};

// ==========================================
// 7. AUTH & IAM
// ==========================================
const findUserInDB = async (email) => {
  const q = query(collection(db, "team_members"), where("email", "==", email));
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    if (userData.active === false) return null;
    return { dbId: userDoc.id, ...userData };
  }
  return null;
};

export const loginPlexarianUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    const cleanEmail = email.toLowerCase().trim();
    const dbUser = await findUserInDB(cleanEmail);

    if (dbUser) {
      return {
        uid: user.uid, email: cleanEmail, role: dbUser.role,
        name: dbUser.name, dbId: dbUser.dbId, avatar: dbUser.nexusAvatarUrl || dbUser.photo
      };
    } else {
      await signOut(auth);
      throw new Error("Usuario no registrado en el sistema administrativo.");
    }
  } catch (error) { console.error("Login error:", error); throw error; }
};

export const restorePlexarianSession = async (firebaseUser) => {
  try {
    const cleanEmail = firebaseUser.email.toLowerCase().trim();
    const dbUser = await findUserInDB(cleanEmail);
    if (dbUser) {
      return {
        uid: firebaseUser.uid, email: cleanEmail, role: dbUser.role,
        name: dbUser.name, dbId: dbUser.dbId, avatar: dbUser.nexusAvatarUrl || dbUser.photo
      };
    } else { await signOut(auth); return null; }
  } catch (error) { return null; }
};

export const logoutPlexarian = async () => { await signOut(auth); };

/** Carga puntual del perfil IAM (más fiable que el primer onSnapshot). */
export const loadUserProfile = async (email) => {
  const cleanEmail = (email || '').toLowerCase().trim();
  if (!cleanEmail) return null;
  const q = query(collection(db, "team_members"), where("email", "==", cleanEmail));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const userDoc = snapshot.docs[0];
  return { dbId: userDoc.id, ...userDoc.data() };
};

/** Guarda el enlace de Google Calendar Scheduling del ejecutivo en team_members. */
export const saveExecutiveCalendarSchedulingUrl = async (email, url) => {
  const cleanEmail = (email || '').toLowerCase().trim();
  const cleanUrl = String(url || '').trim();
  if (!cleanEmail || !cleanUrl) return false;
  const q = query(collection(db, 'team_members'), where('email', '==', cleanEmail));
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    console.warn(`[CAL LINK] No se encontró team_members para ${cleanEmail}`);
    return false;
  }
  await updateDoc(snapshot.docs[0].ref, {
    calendarSchedulingUrl: cleanUrl,
    calendarSchedulingUrlUpdatedAt: new Date().toISOString()
  });
  return true;
};

export const subscribeToUserProfile = (email, onUpdate, onError) => {
  const cleanEmail = (email || '').toLowerCase().trim();
  let profileConfirmed = false;

  const q = query(collection(db, "team_members"), where("email", "==", cleanEmail));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      if (profileConfirmed) {
        console.warn('[AUTH] Snapshot vacío tras perfil confirmado — ignorando (sin cerrar sesión).');
        return;
      }
      if (snapshot.metadata.fromCache) {
        return;
      }
      onError("Usuario no encontrado (Snapshot Vacio)");
      return;
    }
    const userDoc = snapshot.docs[0];
    const userData = userDoc.data();
    profileConfirmed = true;
    onUpdate({ dbId: userDoc.id, ...userData });
  }, (error) => {
    console.error("Error suscribiendo al perfil:", error);
    if (!profileConfirmed) onError(error);
  });
  return unsubscribe;
};

export const updateUserPreferences = async (dbId, prefs) => {
  try {
    const docRef = doc(db, "team_members", dbId);
    await updateDoc(docRef, { ...prefs, updatedAt: serverTimestamp() });
    return true;
  } catch (e) {
    console.error("Error updating user preferences:", e);
    throw e;
  }
};

const MOTIVATION_PAUSE_MS = 7 * 24 * 60 * 60 * 1000;

export const motivationTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
};

const isSameLocalDay = (ms, now = new Date()) => {
  if (!ms) return false;
  const d = new Date(ms);
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
};

export const addOneMonth = (from = new Date()) => {
  const d = new Date(from);
  d.setMonth(d.getMonth() + 1);
  return d;
};

/** True if this account should not see the daily motivation modal right now. */
export const isMotivationHidden = (user) => {
  if (!user) return true;
  if (user.motivationStatus === 'off') return true;
  if (user.motivationStatus === 'paused' && motivationTimestampMs(user.motivationPausedUntil) > Date.now()) {
    return true;
  }
  return isSameLocalDay(motivationTimestampMs(user.motivationLastShownAt));
};

/**
 * Whether to show the keep / pause / never choices.
 * First visit and after a pause always ask. After "keep", ask again one month later.
 */
export const shouldAskMotivationPreference = (user) => {
  if (!user || user.motivationStatus === 'off') return false;
  if (user.motivationStatus === 'paused') {
    return motivationTimestampMs(user.motivationPausedUntil) <= Date.now();
  }
  if (user.motivationStatus === 'on') {
    const askAt = motivationTimestampMs(user.motivationAskAgainAt);
    if (!askAt) return false;
    return Date.now() >= askAt;
  }
  return true;
};

/**
 * Atomically claims today's motivation show for this team member.
 * Returns false if another device already claimed it, or if the user opted out / paused.
 */
export const claimDailyMotivationShow = async (dbId) => {
  if (!dbId) return false;
  const ref = doc(db, 'team_members', dbId);
  try {
    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return false;
      const data = snap.data() || {};
      if (data.motivationStatus === 'off') return false;
      if (data.motivationStatus === 'paused' && motivationTimestampMs(data.motivationPausedUntil) > Date.now()) {
        return false;
      }
      if (isSameLocalDay(motivationTimestampMs(data.motivationLastShownAt))) return false;
      const patch = {
        motivationLastShownAt: new Date(),
        updatedAt: serverTimestamp(),
      };
      if (data.motivationStatus === 'on' && !motivationTimestampMs(data.motivationAskAgainAt)) {
        patch.motivationAskAgainAt = addOneMonth();
      }
      tx.update(ref, patch);
      return true;
    });
  } catch (e) {
    console.error('Error claiming daily motivation:', e);
    return false;
  }
};

/** Persist the user's choice after seeing the daily motivation modal. */
export const setMotivationPreference = async (dbId, choice) => {
  if (!dbId) return false;
  const patch = { updatedAt: serverTimestamp() };
  if (choice === 'never') {
    patch.motivationStatus = 'off';
    patch.motivationPausedUntil = deleteField();
    patch.motivationAskAgainAt = deleteField();
  } else if (choice === 'pause') {
    patch.motivationStatus = 'paused';
    patch.motivationPausedUntil = new Date(Date.now() + MOTIVATION_PAUSE_MS);
    patch.motivationAskAgainAt = deleteField();
  } else {
    patch.motivationStatus = 'on';
    patch.motivationPausedUntil = deleteField();
    patch.motivationAskAgainAt = addOneMonth();
  }
  await updateDoc(doc(db, 'team_members', dbId), patch);
  return true;
};

export const uploadSidebarBackground = async (dbId, file) => {
  try {
    const fileRef = ref(storage, `backgrounds/${dbId}_${Date.now()}_${file.name}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);
    return url;
  } catch (e) {
    console.error("Error uploading background:", e);
    throw e;
  }
};

export const syncTeamMemberGoogleProfile = async (email, googleName, googlePhoto) => {
  if (!email || (!googleName && !googlePhoto)) return;
  try {
    const q = query(collection(db, "team_members"), where("email", "==", email));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const currentData = userDoc.data();
      const docRef = doc(db, "team_members", userDoc.id);

      const updates = {};
      if (googleName && currentData.name !== googleName) {
        updates.name = googleName;
      }
      // Consider sizes or changes in photoURL if available
      if (googlePhoto && currentData.photo !== googlePhoto) {
        updates.photo = googlePhoto;
      }

      const batch = writeBatch(db);
      let madeChanges = false;

      if (Object.keys(updates).length > 0) {
        batch.update(docRef, updates);
        madeChanges = true;
      }

      // Si existe un vínculo con RH, actualizamos su expediente con los datos directos de Google
      if (currentData.linkedEmployeeId) {
        const empRef = doc(db, "employees", currentData.linkedEmployeeId);
        const empUpdates = {};

        // Push the google photo to HR directly from Google Auth
        if (googlePhoto) empUpdates.photoUrl = googlePhoto;

        // Push the google name to HR if we got a new one
        if (googleName) {
          const parts = googleName.split(' ');
          empUpdates.nombre = parts[0];
          empUpdates.apellidoPaterno = parts.slice(1).join(' ') || "";
        }

        if (Object.keys(empUpdates).length > 0) {
          batch.update(empRef, empUpdates);
          madeChanges = true;
        }
      }

      if (madeChanges) {
        await batch.commit();
        console.log("✅ Perfil de Google Workspace sincronizado en IAM y RH.");
      }
    }
  } catch (error) {
    console.error("Error sincronizando perfil de Google Workspace:", error);
  }
};

// ==========================================
// 8. GESTIÓN DE ACCESOS (TOKENS CLIENTES)
// ==========================================

export const createInvitationToken = async (inviteData) => {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + 14);

  const tokenRef = await addDoc(collection(db, "invitations"), {
    ...inviteData,
    expiresAt: expirationDate.toISOString(),
    used: false,
    createdAt: new Date().toISOString()
  });

  await ensureDashboardContractFromInvitation(inviteData, tokenRef.id);
  return tokenRef.id;
};

export const registerInvitationWithId = async (tokenId, inviteData) => {
  try {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 14);

    await setDoc(doc(db, "invitations", tokenId), {
      ...inviteData,
      expiresAt: expirationDate.toISOString(),
      used: false,
      createdAt: new Date().toISOString()
    });
    await ensureDashboardContractFromInvitation(inviteData, tokenId);
    return true;
  } catch (e) {
    console.error("Error registering invitation with ID:", e);
    throw e;
  }
};

export const verifyToken = async (tokenId) => {
  try {
    const docRef = doc(db, "invitations", tokenId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return { valid: false, error: "Token no existe" };

    const data = docSnap.data();

    // VALIDACIÓN DE UN SOLO USO (MUERTE DEL ENLACE)
    if (data.used === true) {
      return { valid: false, error: "Este enlace ya fue utilizado y no es válido." };
    }

    const now = new Date();
    const expires = new Date(data.expiresAt);

    if (now > expires) return { valid: false, error: "El enlace ha caducado (14 días)" };

    return { valid: true, data };
  } catch (e) {
    console.error("Error verificando token:", e);
    return { valid: false, error: "Error de conexión" };
  }
};

export const getExecutiveInvitations = async (executiveId, executiveEmail = null) => {
  try {
    const q = executiveEmail
      ? query(collection(db, "invitations"), where("creatorEmail", "==", executiveEmail))
      : query(collection(db, "invitations"), where("createdBy", "==", executiveId));

    const snap = await getDocs(q);
    const list = [];
    snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));

    // Si usamos ID y no hay resultados, y tenemos email, probamos por email por si acaso
    if (list.length === 0 && executiveEmail && executiveId !== 'unknown') {
      const q2 = query(collection(db, "invitations"), where("creatorEmail", "==", executiveEmail));
      const snap2 = await getDocs(q2);
      snap2.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
    }

    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getAllInvitations = async () => {
  try {
    const q = query(collection(db, "invitations"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch (e) {
    console.error("Error fetching all invitations:", e);
    return [];
  }
};

export const deleteInvitationToken = async (tokenId) => {
  try {
    await deleteDoc(doc(db, "invitations", tokenId));
    return true;
  } catch (e) {
    console.error("Error deleting invitation:", e);
    throw e;
  }
};

// ==========================================
// 10. AUTOMATIZACIÓN DE ONBOARDING
// ==========================================

export const sendWelcomeEmail = async (contractId) => {
  return await apiCall('/api/onboarding/welcome-email', 'POST', { contractId });
};

export const sendOfficialWelcomeEmail = async (contractId, overrides = {}) => {
  return await apiCall('/api/onboarding/welcome-post-sale-email', 'POST', {
    contractId,
    ...overrides
  });
};

export const sendClientAdminPostImplementationWelcome = async ({
  contractId,
  adminName,
  adminEmail,
  userRole
}) => {
  return await apiCall('/api/onboarding/welcome-post-implementation-email', 'POST', {
    contractId,
    adminName,
    adminEmail,
    userRole
  });
};

export const downloadUserTemplate = (domain = 'Cliente', includePassword = false, contractId = '') => {
  const params = new URLSearchParams({
    domain: String(domain || 'Cliente'),
    includePassword: includePassword ? 'true' : 'false'
  });
  if (contractId) params.set('contractId', String(contractId));
  window.open(`${API_BASE_URL}/api/onboarding/download-user-template?${params.toString()}`, '_blank');
};

export const downloadContractUserList = (contractId) => {
  const id = String(contractId || '').trim();
  if (!id) return;
  window.open(`${API_BASE_URL}/api/onboarding/download-user-list?contractId=${encodeURIComponent(id)}`, '_blank');
};

export const processUserList = async (contractId, file, passwordMode = 'temporal') => {
  const formData = new FormData();
  formData.append('contractId', contractId);
  formData.append('csvFile', file);
  formData.append('passwordMode', passwordMode);
  return await apiCall('/api/onboarding/process-user-list', 'POST', formData, true);
};

export const getPublicContractLicensePlan = async (contractId) => {
  return apiCall(
    `/api/onboarding/license-plan?contractId=${encodeURIComponent(contractId)}`,
    'GET'
  );
};

export const savePublicContractLicensePlan = async (contractId, users) => {
  return apiCall('/api/onboarding/license-plan', 'PUT', { contractId, users });
};

export const markInvitationAsUsed = async (tokenId) => {
  if (!tokenId) return;
  try {
    await updateDoc(doc(db, "invitations", tokenId), { used: true });
  } catch (e) {
    console.error("Error marking invitation as used:", e);
  }
};

export const autoCompleteCRMLeadByDomain = async (domain) => {
  if (!domain) return;
  try {
    const q = query(collection(db, "crm_leads"), where("domain", "==", domain));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const leadSnap = querySnapshot.docs[0];
      await updateLeadStatus(leadSnap.id, {
        phase: 3,
        phase3Result: 'Propuesta Aceptada',
        masterStatus: 'PROSPECTO_ACTIVO',
        onboardingStatus: 'COMPLETADA',
        description: '✅ Fase 3: Onboarding Completado. Contrato autogenerado.'
      });
      console.log(`✅ CRM Lead automáticamente avanzado al Cierre para dominio: ${domain}`);
    }
  } catch (e) {
    console.error("Error auto-completing CRM lead by domain:", e);
  }
};

// ==========================================
// 9. MÓDULO RECURSOS HUMANOS (RH) - COMPLETO
// ==========================================

// A. CREAR INVITACIÓN (Recibe nombre y correo)
export const createEmployeeInvite = async (createdByEmail, candidateName, candidateEmail) => {
  try {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 5); // 5 días de vigencia

    const inviteRef = await addDoc(collection(db, "employee_invites"), {
      createdBy: createdByEmail,
      candidateName: candidateName,
      candidateEmail: candidateEmail,
      createdAt: new Date().toISOString(),
      expiresAt: expirationDate.toISOString(),
      used: false,
      status: 'active'
    });

    return inviteRef.id;
  } catch (e) {
    console.error("Error creando invitación:", e);
    throw e;
  }
};

export const getEmployeeInvites = async () => {
  try {
    const qSnapshot = await getDocs(query(collection(db, "employee_invites"), orderBy("createdAt", "desc")));
    const invites = [];
    qSnapshot.forEach((doc) => {
      invites.push({ id: doc.id, ...doc.data() });
    });
    return invites;
  } catch (e) {
    console.error("Error fetching invites:", e);
    throw e;
  }
};

export const deleteEmployeeInvite = async (inviteId) => {
  try {
    await deleteDoc(doc(db, "employee_invites", inviteId));
  } catch (e) {
    console.error("Error deleting invite:", e);
    throw e;
  }
};

// B. VERIFICAR TOKEN DE EMPLEADO
export const verifyEmployeeToken = async (tokenId) => {
  try {
    if (!tokenId) return { valid: false, error: "Token no proporcionado" };

    const docRef = doc(db, "employee_invites", tokenId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return { valid: false, error: "La invitación no existe." };

    const data = docSnap.data();

    if (data.used) return { valid: false, error: "Este enlace ya fue utilizado anteriormente." };

    const now = new Date();
    const expires = new Date(data.expiresAt);
    if (now > expires) return { valid: false, error: "El enlace ha caducado (Vigencia de 5 días)." };

    return { valid: true, data };
  } catch (e) {
    console.error("Error verificando token:", e);
    return { valid: false, error: "Error de sistema al verificar." };
  }
};

// C. EXTRAER DATOS CON IA
export const extractEmployeeData = async (files) => {
  try {
    const formData = new FormData();
    Object.keys(files).forEach(key => {
      if (files[key]) formData.append(key, files[key]);
    });
    
    const response = await apiCall('/api/onboarding/extract-employee-data', 'POST', formData, true);
    if (!response.success) throw new Error(response.error || "Error extrayendo datos con IA");
    return response.extracted_data;
  } catch (error) {
    console.error("Error en extractEmployeeData:", error);
    throw error;
  }
};

// D. CREAR EMPLEADO (Con token y archivos)
export const createEmployeeRecord = async (data, files, inviteToken = null) => {
  try {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    Object.keys(files).forEach(key => {
      if (files[key]) formData.append(key, files[key]);
    });

    const response = await apiCall('/api/onboarding/upload-employee-record', 'POST', formData, true);
    if (!response.success) throw new Error(response.error || "Error subiendo archivos al servidor");

    await addDoc(collection(db, "employees"), {
      ...data,
      ...response.fileLinks,
      fechaIngreso: new Date().toISOString(),
      active: true,
      department: 'General',
      onboardedViaToken: inviteToken || 'manual'
    });

    if (inviteToken) {
      await updateDoc(doc(db, "employee_invites", inviteToken), {
        used: true,
        usedAt: new Date().toISOString(),
        registeredName: `${data.nombre} ${data.apellidoPaterno}`
      });
    }

    return { success: true };
  } catch (error) {
    console.error("Error RH:", error);
    throw error;
  }
};

// D. OBTENER EMPLEADOS (Adaptado para Kanban RH)
export const getEmployees = async () => {
  try {
    const q = query(collection(db, "employees"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Mapeo para que el Kanban entienda los datos de RH
        pipelineStage: data.active ? 'HIRED' : 'ONBOARDING', // HIRED o ONBOARDING son las etapas
        clientName: `${data.nombre} ${data.apellidoPaterno}`,
        title: data.puesto || 'Candidato',
        company: 'Plexarian Team'
      };
    });
  } catch (e) {
    console.error("Error fetching employees:", e);
    return [];
  }
};

/** Inventario de equipos (company_devices) vía backend + sync GWS. */
const devicesApiCall = async (endpoint, method = 'GET', body = null) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Inicia sesión para consultar el inventario');
  const token = await user.getIdToken();
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  if (method !== 'GET' && body !== null) options.body = JSON.stringify(body);
  const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Error del servidor: ${response.status}`);
  return data;
};

export const getEmployeeDevices = async (assignedToEmail) => {
  const email = (assignedToEmail || '').toLowerCase().trim();
  const qs = email ? `?assignedToEmail=${encodeURIComponent(email)}` : '';
  return await devicesApiCall(`/api/devices${qs}`, 'GET');
};

export const getAllCompanyDevices = async () => {
  return await devicesApiCall('/api/devices', 'GET');
};

export const reconcileCompanyDevices = async () => {
  return await devicesApiCall('/api/devices/reconcile', 'GET');
};

export const syncCompanyDevice = async (id) => {
  return await devicesApiCall(`/api/devices/${encodeURIComponent(id)}/sync`, 'POST', {});
};

/** Baja del Inventario GWS + retiro en Nexus. Requiere confirmSerial exacto. */
export const retireCompanyDevice = async (id, confirmSerial) => {
  return await devicesApiCall(`/api/devices/${encodeURIComponent(id)}`, 'DELETE', { confirmSerial });
};

export const createCompanyDevice = async (payload) => {
  return await devicesApiCall('/api/devices', 'POST', payload);
};

export const updateCompanyDevice = async (id, payload) => {
  return await devicesApiCall(`/api/devices/${encodeURIComponent(id)}`, 'PATCH', payload);
};

// E. APROVISIONAR CUENTA WORKSPACE
export const provisionWorkspaceAccount = async (employeeData) => {
  try {
    const res = await apiCall('/api/workspace/provision-user', 'POST', employeeData);
    if (!res.success) throw new Error(res.error || "Error aprovisionando cuenta en Workspace.");
    return res;
  } catch (error) {
    console.error("Error provisioning workspace account:", error);
    throw error;
  }
};

/** Suspende staff en GWS + kick sesiones GWS + revoca Nexus (Auth + IAM). */
export const suspendStaffUser = async ({ email, requestedBy, dryRun = false }) => {
  const res = await apiCall('/api/workspace/suspend-user', 'POST', { email, requestedBy, dryRun });
  if (!res.success) throw new Error(res.error || 'Error suspendiendo usuario.');
  return res;
};

/** Reactiva staff en GWS + Firebase Auth (el rol de Nexus hay que reasignarlo). */
export const unsuspendStaffUser = async ({ email, requestedBy, dryRun = false }) => {
  const res = await apiCall('/api/workspace/unsuspend-user', 'POST', { email, requestedBy, dryRun });
  if (!res.success) throw new Error(res.error || 'Error reactivando usuario.');
  return res;
};

/** Baja definitiva: suspende GWS + cierra todas las sesiones (equipos y apps) + revoca Nexus. */
export const offboardStaffUser = async ({ email, requestedBy }) => {
  const res = await apiCall('/api/workspace/offboard-user', 'POST', { email, requestedBy });
  if (!res.success) throw new Error(res.error || 'Error dando de baja al usuario.');
  return res;
};

// F. ACTUALIZAR EMPLEADO
export const updateEmployee = async (id, data) => {
  try {
    const ref = doc(db, "employees", id);
    const cleanData = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== undefined));
    await setDoc(ref, cleanData, { merge: true });

    // Call server to sync profile with Google Workspace and update signatures
    if (data.emailCorporativo || data.email || data.nombre || data.puesto || data.telefonoCelular) {
      const corporateEmail = data.emailCorporativo || data.email;
      try {
        await apiCall('/api/workspace/sync-employee-profile', 'POST', {
           employeeId: id,
           email: corporateEmail,
           name: data.nombre,
           familyName: `${data.apellidoPaterno || ''} ${data.apellidoMaterno || ''}`.trim(),
           title: data.puesto || '',
           phone: data.telefonoCelular || ''
        });
      } catch (syncError) {
        console.error("Non-blocking error syncing profile/signature:", syncError);
      }
    }
  } catch (e) {
    console.error("Error updating employee:", e);
    throw e;
  }
};

// F. ELIMINAR EMPLEADO
export const deleteEmployee = async (id) => {
  try {
    await deleteDoc(doc(db, "employees", id));
  } catch (e) {
    console.error("Error deleting employee:", e);
    throw e;
  }
};

/** Avatar visible en Nexus (sidebar, marketing, etc.). No se usa en firmas de correo. */
export const resolveNexusDisplayPhoto = (member, fallbackName = 'U') => {
  if (member?.nexusAvatarUrl) return member.nexusAvatarUrl;
  if (member?.photo) return member.photo;
  if (member?.photoUrl) return member.photoUrl;
  if (member?.foto) return member.foto;
  const name = member?.name || member?.nombre || fallbackName;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=331D40&color=fff`;
};

/** Sube avatar solo para UI de Nexus; la firma sigue usando la foto de Google Workspace. */
export const uploadTeamMemberNexusAvatar = async (email, file) => {
  const cleanEmail = (email || '').toLowerCase().trim();
  if (!cleanEmail || !file) throw new Error('Correo o archivo inválido.');

  const storagePath = `nexus-avatars/${cleanEmail.replace(/[^a-z0-9@._-]/gi, '_')}/avatar_${Date.now()}.jpg`;
  const storageRef = ref(storage, storagePath);
  const snapshot = await uploadBytes(storageRef, file);
  const nexusAvatarUrl = await getDownloadURL(snapshot.ref);

  const batch = writeBatch(db);
  const qTeam = query(collection(db, 'team_members'), where('email', '==', cleanEmail));
  const teamSnap = await getDocs(qTeam);
  if (teamSnap.empty) {
    throw new Error('No se encontró usuario en team_members para actualizar el avatar.');
  }

  let linkedEmployeeId = null;
  teamSnap.docs.forEach((docSnap) => {
    linkedEmployeeId = docSnap.data().linkedEmployeeId || linkedEmployeeId;
    batch.update(docSnap.ref, { nexusAvatarUrl });
  });

  if (linkedEmployeeId) {
    batch.update(doc(db, 'employees', linkedEmployeeId), { nexusAvatarUrl });
  }

  await batch.commit();
  return nexusAvatarUrl;
};

// G. SUBIR FOTO NEXUS (RH / sidebar — no afecta firma Gmail)
export const uploadEmployeePhoto = async (employeeId, file, rfc, corporateEmail = null) => {
  try {
    const employeeRef = doc(db, 'employees', employeeId);
    const empDoc = await getDoc(employeeRef);
    const email = corporateEmail
      || empDoc.data()?.emailCorporativo
      || empDoc.data()?.email;
    if (!email) {
      throw new Error('No hay correo corporativo vinculado para guardar el avatar de Nexus.');
    }
    return await uploadTeamMemberNexusAvatar(email, file);
  } catch (e) {
    console.error('Error uploading Nexus avatar:', e);
    throw e;
  }
};

// ==========================================
// 10. INTEGRACIÓN RH -> IAM
// ==========================================

export const grantAccessToEmployee = async (employeeData, role, corporateEmail) => {
  try {
    if (!corporateEmail.endsWith('@plexarian.com')) {
      throw new Error("El acceso debe ser con un correo @plexarian.com");
    }

    const q = query(collection(db, "team_members"), where("email", "==", corporateEmail));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      throw new Error("Este correo corporativo ya está en uso por otro usuario.");
    }

    const photoUrl = employeeData.foto || employeeData.photoUrl || employeeData.doc_ine_url || `https://ui-avatars.com/api/?name=${employeeData.name || employeeData.nombre}&background=331D40&color=fff`;
    const fullName = employeeData.name || `${employeeData.nombre || ''} ${employeeData.apellidoPaterno || ''}`.trim() || 'Nuevo Usuario';

    const payload = {
      name: fullName,
      email: corporateEmail,
      personalEmail: employeeData.email,
      role: role,
      photo: photoUrl,
      active: true,
      createdAt: new Date().toISOString()
    };

    if (employeeData.id) {
      payload.linkedEmployeeId = employeeData.id;
    }

    await addDoc(collection(db, "team_members"), payload);

    // Auto-fill CRM cold base if role is commercial
    const commercialRoles = ['sales', 'comercial', 'comerciales', 'ventas', 'ejecutivo', 'ejecutivos', 'ejecutivo comercial', 'ejecutivos comerciales', 'account executive', 'mark & sales', 'comercial y renovaciones', 'renovaciones y comercial', 'director', 'pre-venta'];
    if (commercialRoles.includes((role || '').toLowerCase().trim())) {
      console.log(`🚀 Rol comercial asignado. Aprovisionando base fría para ${corporateEmail}...`);
      await syncLeads(corporateEmail).catch(e => console.error("Error aprovisionando leads:", e));
    }

    return { success: true };
  } catch (error) {
    console.error("Error grantAccess:", error);
    throw error;
  }
};

export const updateTeamMemberRoleByEmail = async (email, newRole) => {
  try {
    const safeEmail = email.toLowerCase().trim();
    const q = query(collection(db, "team_members"), where("email", "==", safeEmail));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("Usuario no encontrado en el sistema IAM.");
    }

    const docRef = doc(db, "team_members", querySnapshot.docs[0].id);
    await updateDoc(docRef, { role: newRole });

    // Auto-fill CRM cold base if role is commercial
    const commercialRoles = ['sales', 'comercial', 'comerciales', 'ventas', 'ejecutivo', 'ejecutivos', 'ejecutivo comercial', 'ejecutivos comerciales', 'account executive', 'mark & sales', 'comercial y renovaciones', 'renovaciones y comercial', 'director', 'pre-venta'];
    if (commercialRoles.includes((newRole || '').toLowerCase().trim())) {
      console.log(`🚀 Rol actualizado a comercial. Aprovisionando base fría para ${safeEmail}...`);
      await syncLeads(safeEmail).catch(e => console.error("Error aprovisionando leads:", e));
    }

    return { success: true };
  } catch (error) {
    console.error("Error updating team member role:", error);
    throw error;
  }
};

// ==========================================
// 11. GESTIÓN DE CLIENTES GOOGLE WORKSPACE
// ==========================================
export const getWorkspaceCustomers = async () => {
  return await apiCall('/api/workspace/customers', 'GET');
};

export const getUnifiedCustomers = async ({ force = false } = {}) => {
  const qs = force ? '?force=1' : '';
  return await apiCall(`/api/workspace/unified-customers${qs}`, 'GET');
};

/** Refresca admins de Admin Console GWS para un customerId (cache Firestore). */
export const refreshCustomerGwsAdmins = async (customerId, { domain } = {}) => {
  return await apiCall(
    `/api/workspace/customers/${encodeURIComponent(customerId)}/refresh-admins`,
    'POST',
    { domain: domain || '' }
  );
};

/** Cobranza Por Cobrar (deuda + histórico) para modal del Directorio. */
export const getCustomerBilling = async ({ domain, porCobrarUuid } = {}) => {
  const params = new URLSearchParams();
  if (domain) params.set('domain', domain);
  if (porCobrarUuid) params.set('por_cobrar_uuid', porCobrarUuid);
  const qs = params.toString();
  return await apiCall(`/api/workspace/customer-billing${qs ? `?${qs}` : ''}`, 'GET');
};

/** Mapa due/overdue PC para enriquecer el Directorio sin bloquear la lista. */
export const getPcCustomerDebts = async ({ force = false } = {}) => {
  const qs = force ? '?force=1' : '';
  return await apiCall(`/api/workspace/pc-customer-debts${qs}`, 'GET');
};

/** Reporte comparativo GWS (licencias habilitadas) vs Por Cobrar (facturación por tipo). */
export const fetchGwsPcComparison = async () => {
  return await apiCall('/api/workspace/gws-pc-comparison', 'GET');
};

export const refreshGwsPcComparison = async () => {
  return await apiCall('/api/workspace/gws-pc-comparison/refresh', 'POST');
};

/** Catálogo de identificación GWS (dominio) ↔ Por Cobrar (identifier + RFC) con licencias. */
export const fetchGwsPcIdentification = async (opts = {}) => {
  const params = new URLSearchParams();
  if (opts.refreshContracts === true) {
    params.set('refreshContracts', '1');
    params.set('refreshBillingSchedule', '1');
  }
  const qs = params.toString() ? `?${params.toString()}` : '';
  return await apiCall(`/api/workspace/gws-pc-identification${qs}`, 'GET');
};

/** Contabilidad → Contratos: Excel 2026 · Por Cobrar · GWS (lado a lado, sin ganador). */
export const fetchContratosContabilidad = async () => {
  return await apiCall('/api/workspace/contratos-contabilidad', 'GET');
};

async function contratosContabilidadAuthFetch(path, { method = 'GET', body, signal } = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión.');
  const token = await user.getIdToken();
  const headers = { Authorization: `Bearer ${token}` };
  if (body != null) headers['Content-Type'] = 'application/json';
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
    signal: signal || undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `Error del servidor: ${response.status}`);
    err.status = response.status;
    Object.assign(err, data);
    throw err;
  }
  return data;
}

/** Typeahead alta Contratos: Directorio + GWS/PC. */
export const searchContratosContabilidadAdd = async (q, signal) => {
  const query = encodeURIComponent(String(q || '').trim());
  return contratosContabilidadAuthFetch(
    `/api/workspace/contratos-contabilidad/search?q=${query}`,
    { signal }
  );
};

/** Pre-chequeo alta Contratos: GWS + Directorio (+ autofill). */
export const lookupContratosContabilidadDomain = async (domain, signal) => {
  const q = encodeURIComponent(String(domain || '').trim());
  return contratosContabilidadAuthFetch(
    `/api/workspace/contratos-contabilidad/lookup?domain=${q}`,
    { signal }
  );
};

/** Editar campos Contabilidad-owned (notas, mes, hosting, periodos, baja). */
export const updateContratosContabilidadOverride = async (domain, fields) => {
  const d = encodeURIComponent(String(domain || '').trim());
  return contratosContabilidadAuthFetch(`/api/workspace/contratos-contabilidad/${d}`, {
    method: 'PATCH',
    body: fields,
  });
};

/** Alta manual: solo si existe en GWS y Directorio. */
export const addContratosContabilidadClient = async (payload) => {
  return contratosContabilidadAuthFetch('/api/workspace/contratos-contabilidad', {
    method: 'POST',
    body: payload,
  });
};

/** Regenera identificación masiva (solo lectura en Google y Por Cobrar). */
export const runGwsPcIdentification = async () => {
  const base = resolveLongJobApiBaseUrl();
  const response = await fetch(`${base}/api/workspace/gws-pc-identification/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sync: true }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Error del servidor: ${response.status}`);
  }
  const data = await response.json();
  if (data?.pending && data?.jobId) {
    return waitForGwsPcIdentificationJob(data.jobId);
  }
  return data;
};

export const getGwsPcIdentificationRunStatus = async (jobId) => {
  const qs = jobId ? `?jobId=${encodeURIComponent(jobId)}` : '';
  return await apiCall(`/api/workspace/gws-pc-identification/run-status${qs}`, 'GET');
};

async function waitForGwsPcIdentificationJob(jobId, { timeoutMs = 20 * 60 * 1000, intervalMs = 4000 } = {}) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const st = await getGwsPcIdentificationRunStatus(jobId);
    if (st?.status === 'completed') {
      const fresh = await fetchGwsPcIdentification({ refreshContracts: false });
      return {
        success: true,
        pending: false,
        generatedAt: st.generatedAt || fresh?.generatedAt,
        summary: st.summary || fresh?.summary,
        clients: fresh?.clients || [],
        ...(fresh || {}),
      };
    }
    if (st?.status === 'failed') {
      throw new Error(st.error || 'El cruce falló');
    }
  }
  throw new Error('El cruce superó el tiempo máximo (20 min)');
}

export const refreshGwsPcDomain = async (domain) => {
  return await apiCall('/api/workspace/gws-pc-identification/refresh-domain', 'POST', { domain });
};

/** Nota de análisis para un dominio en el cruce GWS ↔ PC (persiste en Nexus). */
export const saveGwsPcReconcileNote = async ({ domain, note }) => {
  return await apiCall('/api/workspace/gws-pc-reconcile-note', 'POST', { domain, note: note ?? '' });
};

/** Guarda dominio/RFC manual en Nexus y vincula con Por Cobrar (sin modificar PC). */
export const saveGwsPcManualLink = async ({ gwsDomain, pcIdentifier, rfc, gwsLicenses, pcContractUuid }) => {
  const body = {
    gwsDomain,
    pcIdentifier: pcIdentifier || '',
    rfc: rfc || '',
    gwsLicenses: gwsLicenses || [],
    pcContractUuid: pcContractUuid || null
  };
  try {
    return await apiCall('/api/licencias/gws-pc-manual-link', 'POST', body);
  } catch (e) {
    if (String(e.message || '').includes('404')) {
      return await apiCall('/api/workspace/gws-pc-manual-link', 'POST', body);
    }
    throw e;
  }
};

async function workspaceAuthFetch(endpoint, method = 'GET', body = null) {
  const headers = { Accept: 'application/json' };
  if (method !== 'GET' && method !== 'HEAD') headers['Content-Type'] = 'application/json';
  if (auth.currentUser) {
    headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
  }
  const options = { method, headers };
  if (method !== 'GET' && method !== 'HEAD' && body != null) {
    options.body = JSON.stringify(body);
  }
  const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err = new Error(errorData.error || `Error del servidor: ${response.status}`);
    err.status = response.status;
    err.code = errorData.code || null;
    err.hint = errorData.hint || null;
    throw err;
  }
  return response.json();
}

export const getOverdueSuspendQueue = async ({ refresh = false } = {}) => {
  const qs = refresh ? '?refresh=1' : '';
  return workspaceAuthFetch(`/api/workspace/overdue-suspend-queue${qs}`, 'GET');
};

export const putSuspendHold = async (domain, { reason, expiresInDays, createdBy } = {}) => {
  return workspaceAuthFetch(
    `/api/workspace/suspend-holds/${encodeURIComponent(String(domain || '').toLowerCase())}`,
    'PUT',
    { reason, expiresInDays, createdBy }
  );
};

export const deleteSuspendHold = async (domain) => {
  return workspaceAuthFetch(
    `/api/workspace/suspend-holds/${encodeURIComponent(String(domain || '').toLowerCase())}`,
    'DELETE'
  );
};

export const markOverdueInvoicePaid = async (invoiceUuid, { date } = {}) => {
  const uuid = String(invoiceUuid || '').trim();
  return workspaceAuthFetch(
    `/api/workspace/overdue-invoices/${encodeURIComponent(uuid)}/mark-paid`,
    'POST',
    { date: date || undefined }
  );
};

export const suspendCustomer = async (customerId, body = null) => {
  return await apiCall(`/api/workspace/customers/${encodeURIComponent(customerId)}/suspend`, 'POST', body);
};

export const activateCustomer = async (customerId) => {
  return await apiCall(`/api/workspace/customers/${encodeURIComponent(customerId)}/activate`, 'POST');
};

/** Sube usuarios de la hoja lista_usuarios a Admin Console GWS del cliente (Directory reseller). */
export const pushContractUserListToGws = async (contractId, { dryRun = false, limit = null } = {}) => {
  const body = { dryRun: Boolean(dryRun) };
  if (limit != null && Number(limit) > 0) body.limit = Number(limit);
  return await apiCall(
    `/api/workspace/contracts/${encodeURIComponent(contractId)}/push-user-list`,
    'POST',
    body
  );
};

export const getContractLicensePlan = async (contractId) => {
  return workspaceAuthFetch(
    `/api/workspace/contracts/${encodeURIComponent(contractId)}/license-plan`,
    'GET'
  );
};

export const saveContractLicensePlan = async (contractId, users) => {
  return workspaceAuthFetch(
    `/api/workspace/contracts/${encodeURIComponent(contractId)}/license-plan`,
    'PUT',
    { users }
  );
};

export const getLicensePlanApplyStatus = async (contractId) => {
  const fresh = await getContractById(contractId);
  return {
    job: fresh?.licensePlanApplyJob || null,
    report: fresh?.licensePlanApplyReport || null
  };
};

export const applyContractLicensePlan = async (contractId) => {
  return workspaceAuthFetch(
    `/api/workspace/contracts/${encodeURIComponent(contractId)}/apply-license-plan`,
    'POST',
    {}
  );
};

/** Verifica dominio GWS vía Site Verification API (TXT del micrositio). */
export const verifyContractDomain = async (contractId, { adminEmail } = {}) => {
  const body = { contractId };
  if (adminEmail) body.adminEmail = adminEmail;
  return workspaceProvisionFetch(
    `/api/workspace/contracts/${encodeURIComponent(contractId)}/verify-domain`,
    body
  );
};

/** Poll: ¿Directory ya marcó el dominio verified en Workspace? */
export const getContractDomainVerifyStatus = async (contractId) => {
  return workspaceProvisionFetch(
    `/api/workspace/contracts/${encodeURIComponent(contractId)}/verify-domain/status`,
    {}
  );
};

export const updateCustomerAddress = async (customerId, data) => {
  return await apiCall(`/api/workspace/customers/${customerId}`, 'PUT', data);
};

// ==========================================
// 12. GESTIÓN DE FIRMAS Y BADGES
// ==========================================

// Upload an image to Firebase Storage for a badge
export const uploadSignatureBadgeImage = async (file) => {
  try {
    const filename = `badge_${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `signatures/badges/${filename}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  } catch (e) {
    console.error("Error uploading badge image:", e);
    throw e;
  }
};

export const uploadSignatureBannerImage = async (file) => {
  try {
    const filename = `banner_${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `signatures/banners/${filename}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  } catch (e) {
    console.error("Error uploading banner image:", e);
    throw e;
  }
};

export const getSignatureGlobalConfig = async () => {
  try {
    const snap = await getDoc(doc(db, 'signature_global_config', 'default'));
    if (!snap.exists()) {
      return {
        website: 'www.plexarian.com',
        globalPhone: '56 4697 7857',
        banner: {
          enabled: false,
          mode: 'image',
          imageUrl: '',
          linkUrl: '',
          altText: '',
          headline: '',
          subline: '',
          backgroundColor: '#331D40',
          headlineColor: '#FFFFFF',
          sublineColor: '#FF3364'
        }
      };
    }
    return snap.data();
  } catch (e) {
    console.error("Error fetching signature global config:", e);
    throw e;
  }
};

export const saveSignatureGlobalConfig = async (configData) => {
  try {
    await setDoc(doc(db, 'signature_global_config', 'default'), {
      ...configData,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    return { success: true };
  } catch (e) {
    console.error("Error saving signature global config:", e);
    throw e;
  }
};

// Save badge metadata in Firestore
export const saveSignatureBadge = async (badgeData) => {
  try {
    const docRef = await addDoc(collection(db, 'signature_badges'), {
      ...badgeData,
      createdAt: new Date().toISOString()
    });
    return { id: docRef.id, ...badgeData };
  } catch (e) {
    console.error("Error saving badge:", e);
    throw e;
  }
};

// Get all available badges
export const getSignatureBadges = async () => {
  try {
    const q = query(collection(db, 'signature_badges'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (e) {
    console.error("Error fetching badges:", e);
    return [];
  }
};

// Delete a badge
export const deleteSignatureBadge = async (badgeId) => {
  try {
    await deleteDoc(doc(db, 'signature_badges', badgeId));
    return { success: true };
  } catch (e) {
    console.error("Error deleting badge:", e);
    throw e;
  }
};

// Save user signature overrides (phone, title, selected badges)
export const saveUserSignatureConfig = async (email, configData) => {
  try {
    // We use the email as the document ID for easy lookup
    await setDoc(doc(db, 'user_signatures_config', email), {
      ...configData,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    return { success: true };
  } catch (e) {
    console.error("Error saving user config:", e);
    throw e;
  }
};

// Get all user configs (useful for merging on the frontend)
export const getAllUserSignatureConfigs = async () => {
  try {
    const q = query(collection(db, 'user_signatures_config'));
    const snapshot = await getDocs(q);
    const configs = {};
    snapshot.docs.forEach(doc => {
      configs[doc.id] = doc.data();
    });
    return configs;
  } catch (e) {
    console.error("Error fetching user signature configs:", e);
    return {};
  }
};

export const getMarketingUsers = async (includeAll = false) => {
  const queryParam = includeAll ? '&includeAll=true' : '';
  return await apiCall(`/api/workspace/marketing/users?t=${Date.now()}${queryParam}`, 'GET');
};

export const updateSignaturesV2 = async (data) => {
  return await apiCall('/api/workspace/marketing/update-signatures-v2', 'POST', data);
};

export const previewSignatureHTML = async (data) => {
  return await apiCall('/api/workspace/marketing/preview-signature', 'POST', data);
};

// ==========================================
// 13. CONFIGURACIÓN CRM (TIPIFICACIONES)
// Actualizar solo ciertos campos de un lead
export const updateLeadStatus = async (id, data) => {
  try {
    const leadRef = doc(db, "crm_leads", id);

    let shouldReplenishColdBase = false;
    let replenishExecutiveEmail = null;
    /** @type {'union'|'replace'|null} */
    let historyMode = null;
    let historyValue = null;

    // Si hay una actualización de fase o resultado, registramos en el historial
    const leadSnap = await getDoc(leadRef);
    if (leadSnap.exists()) {
      const currentData = leadSnap.data();

      if (data.phase !== undefined) {
        const oldPhase = Number(currentData.phase) || 0;
        const newPhase = Number(data.phase) || 0;
        if (oldPhase === 0 && newPhase > 0) {
          shouldReplenishColdBase = true;
          replenishExecutiveEmail = (data.executiveEmail || currentData.executiveEmail || '').toLowerCase().trim();
        }
      }

      if (data.customHistoryEvent) {
        historyMode = 'union';
        historyValue = data.customHistoryEvent;
        delete data.customHistoryEvent;
      } else if (Array.isArray(data.history)) {
        // Reescritura explícita (ej. borrar recordatorio del historial)
        historyMode = 'replace';
        historyValue = data.history;
        delete data.history;
      } else if (!data.skipGenericHistory) {
        historyMode = 'union';
        const historyPhase = data.phase !== undefined ? data.phase : currentData.phase;
        const historyStatus = data.masterStatus !== undefined ? data.masterStatus : currentData.masterStatus;
        historyValue = {
          timestamp: new Date().toISOString(),
          action: 'STATUS_UPDATE',
          note: data.note || '',
          description: data.description || '',
          ...(historyPhase !== undefined ? { phase: historyPhase } : {}),
          ...(historyStatus !== undefined ? { masterStatus: historyStatus } : {})
        };

        if (data.fromReminder) historyValue.fromReminder = true;
        if (data.reminderScheduled) historyValue.reminderScheduled = data.reminderScheduled;
        if (data.reminderType) historyValue.reminderType = data.reminderType;
      }

      // REGISTRO AUTOMÁTICO DE PRIMERA INTERACCIÓN (Round 2.2)
      if (!currentData.firstContactDate && (data.firstContactResult || data.phase2Result || data.phase3Result)) {
        data.firstContactDate = serverTimestamp();
      }
    }

    const { skipGenericHistory, ...persistData } = data;
    void skipGenericHistory;
    const updatePayload = {
      ...persistData,
      updatedAt: serverTimestamp()
    };
    // Las notas de gestión solo se escriben vía addManagementNote (arrayUnion).
    // Evita que un tipificar/refresh parcial pise el array completo.
    delete updatePayload.managementNotes;
    // No borrar observations accidentalmente con string vacío desde payloads incompletos
    if (updatePayload.observations === undefined || updatePayload.observations === '') {
      delete updatePayload.observations;
    }
    // No borrar empresa/dominio/correo con string vacío (evita corrupción por formulario cruzado o tipificar sin email)
    if (leadSnap.exists()) {
      const existing = leadSnap.data();
      if (updatePayload.companyName === '' && existing.companyName) {
        delete updatePayload.companyName;
      }
      if (updatePayload.domain === '' && existing.domain) {
        delete updatePayload.domain;
      }
      const existingContactEmail = String(
        existing.contactEmail || existing.contactData?.email || existing.meetingData?.contactEmail || ''
      ).trim();
      if (updatePayload.contactEmail === '' && existingContactEmail) {
        delete updatePayload.contactEmail;
      }
      const existingFirstName = String(existing.contactFirstName || existing.firstName || '').trim();
      const existingLastName = String(existing.contactLastName || existing.lastName || '').trim();
      const existingFullName = `${existingFirstName} ${existingLastName}`.trim()
        || String(existing.contactData?.name || '').trim()
        || String(existing.contactName || '').trim()
        || String(existing.meetingData?.contactName || '').trim();
      if (updatePayload.contactFirstName === '' && (existingFirstName || existingFullName)) {
        delete updatePayload.contactFirstName;
      }
      if (updatePayload.contactLastName === '' && (existingLastName || existingFullName)) {
        delete updatePayload.contactLastName;
      }
      const existingPhone = String(
        existing.phoneNumber || existing.contactData?.phone || existing.meetingData?.contactPhone || ''
      ).trim();
      if (updatePayload.phoneNumber === '' && existingPhone) {
        delete updatePayload.phoneNumber;
      }
      if (String(updatePayload.phoneNumber || '').trim()) {
        updatePayload.missingPhone = false;
      }
      if (
        updatePayload.contactData
        && typeof updatePayload.contactData === 'object'
        && !String(updatePayload.contactData.phone || '').trim()
        && existingPhone
      ) {
        updatePayload.contactData = {
          ...existing.contactData,
          ...updatePayload.contactData,
          phone: existingPhone
        };
        if (!updatePayload.phoneNumber) {
          updatePayload.phoneNumber = existingPhone;
        }
      }
      if (
        updatePayload.contactData
        && typeof updatePayload.contactData === 'object'
        && !String(updatePayload.contactData.email || '').trim()
        && existingContactEmail
      ) {
        updatePayload.contactData = {
          ...existing.contactData,
          ...updatePayload.contactData,
          email: existingContactEmail
        };
        if (!updatePayload.contactEmail) {
          updatePayload.contactEmail = existingContactEmail;
        }
      }
      if (
        updatePayload.contactData
        && typeof updatePayload.contactData === 'object'
        && !String(updatePayload.contactData.name || '').trim()
        && existingFullName
      ) {
        updatePayload.contactData = {
          ...(existing.contactData || {}),
          ...updatePayload.contactData,
          name: String(existing.contactData?.name || '').trim() || existingFullName
        };
      }
    }
    // Nunca pisar una propuesta comercial real con null / stub slim ({ hasItems } sin líneas)
    if (Object.prototype.hasOwnProperty.call(updatePayload, 'proposal') && leadSnap.exists()) {
      const existingProposal = leadSnap.data().proposal;
      const existingReal = hasValidCommercialProposal(existingProposal);
      const incoming = updatePayload.proposal;
      const incomingReal = hasValidCommercialProposal(incoming);
      if (existingReal && !incomingReal) {
        delete updatePayload.proposal;
      }
    }
    if (historyMode === 'union' && historyValue) {
      updatePayload.history = arrayUnion(historyValue);
    } else if (historyMode === 'replace') {
      updatePayload.history = historyValue;
    }
    // La propuesta se arma en el cliente; un campo ausente (vigencia, tipo, recomendada)
    // no puede ir como undefined: Firestore rechaza el updateDoc entero.
    ['proposal', 'proposalHistory', 'meetingData', 'opportunity', 'googleOpportunity'].forEach((key) => {
      if (updatePayload[key] != null && typeof updatePayload[key] === 'object') {
        updatePayload[key] = omitUndefinedDeep(updatePayload[key]);
      }
    });

    // Un DESCARTADO de fase 2 no debe sobrevivir a la reactivación: marca falsas bajas aguas abajo.
    if (leadSnap.exists() && hasStaleDiscardFlag({ ...leadSnap.data(), ...persistData })) {
      updatePayload.phase2Result = null;
      persistData.phase2Result = null;
    }

    await updateDoc(leadRef, updatePayload);

    const mergedLead = leadSnap.exists()
      ? { id, ...leadSnap.data(), ...persistData }
      : { id, ...persistData };
    if (isCrmLeadCommercialBaja(mergedLead)) {
      hideLinkedContractForCrmLead(mergedLead).catch((e) => {
        console.warn('Sync contrato Dashboard tras baja CRM:', e.message);
      });
    }

    if (shouldReplenishColdBase && replenishExecutiveEmail) {
      queueColdBaseReplenish(replenishExecutiveEmail);
      return { success: true, replenishQueued: true, replenishExecutiveEmail };
    }

    return { success: true, replenishQueued: false };
  } catch (e) {
    console.error("Error updating lead status:", e);
    throw e;
  }
};

export const assignLeadToExecutive = async (id, data) => {
  const res = await apiCall(`/api/crm/leads/${id}/assign`, 'POST', data);
  try {
    const lead = await getLeadById(id);
    const domain = (lead?.domain || '').trim();
    if (domain && !lead.isContract) {
      const existing = await fetchContractByDomain(domain);
      if (existing?.id) {
        const leadCtx = { ...lead, id };
        if (!shouldLinkCrmLeadToContract(existing, leadCtx)) {
          if (isCrmLeadCommercialBaja(leadCtx) && !isOnboardingExpedienteComplete(existing)) {
            await updateContractInDB(existing.id, {
              ...buildHideFromSalesPipelinePatch('CRM_BAJA_COMERCIAL', leadCtx),
              hiddenFromSalesPipelineAt: new Date().toISOString()
            });
          }
          return res;
        }
        if (data.executiveEmail) {
          const execs = await getExecutives().catch(() => []);
          const email = data.executiveEmail.toLowerCase().trim();
          const match = execs.find((e) => (e.email || '').toLowerCase() === email);
          const ejecutivoAsignado = match || {
            id: email,
            email,
            name: data.executiveName || email
          };
          await updateContractInDB(existing.id, { ejecutivoAsignado });
        }
      }
    }
  } catch (syncErr) {
    console.warn('Sync ejecutivo CRM → Dashboard (reasignación):', syncErr);
  }
  return res;
};

// --- NOTAS CRM / DASHBOARD (ejecutivos + dirección) ---

const DIRECTION_ROLES = ['director', 'gerencia', 'gerente', 'dirección', 'direccion', 'ceo', 'admin', 'manager'];

function buildManagementNoteRecord(noteData = {}) {
  const roleNorm = (noteData.authorRole || '').toLowerCase();
  const isDirection = DIRECTION_ROLES.includes(roleNorm);
  const createdAt = new Date().toISOString();
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    text: String(noteData.text || '').trim(),
    historyTimestamp: noteData.historyTimestamp || null,
    status: isDirection && noteData.historyTimestamp ? 'pending' : 'saved',
    createdAt,
    author: noteData.author || 'usuario',
    authorRole: noteData.authorRole || 'Ejecutivo',
    source: noteData.source || 'crm'
  };
}

function buildHistoryEventForStandaloneNote(note, title = 'Nota de seguimiento') {
  if (!note?.text || note.historyTimestamp) return null;
  return {
    id: `note-${note.id}`,
    action: 'NOTA_SEGUIMIENTO',
    title,
    description: note.text,
    timestamp: note.createdAt,
    user: note.author,
    source: note.source || 'dashboard'
  };
}

/** Nota en expediente Dashboard (+ historial); sincroniza al lead CRM si existe crmLeadId. */
export const addContractManagementNote = async (contractId, noteData = {}) => {
  const text = String(noteData.text || '').trim();
  if (!text) throw new Error('La nota no puede estar vacía.');

  const collectionName = await resolveContractCollectionName(contractId);
  const contractRef = doc(db, collectionName, contractId);
  const snap = await getDoc(contractRef);
  if (!snap.exists()) throw new Error('Expediente no encontrado.');

  const data = snap.data();
  const note = buildManagementNoteRecord({
    ...noteData,
    text,
    source: noteData.source || 'dashboard'
  });
  const managementNotes = [...(data.managementNotes || []), note];
  const historyEvent = buildHistoryEventForStandaloneNote(
    note,
    noteData.historyTitle || 'Actualización comercial (Dashboard)'
  );
  const updates = {
    managementNotes,
    lastUpdate: new Date().toISOString()
  };
  if (historyEvent) {
    updates.history = [...(data.history || []), historyEvent];
  }

  await updateContractInDB(contractId, updates);

  const crmLeadId = data.crmLeadId || noteData.crmLeadId;
  if (crmLeadId) {
    try {
      const leadRef = doc(db, 'crm_leads', crmLeadId);
      const leadSnap = await getDoc(leadRef);
      if (leadSnap.exists()) {
        const leadData = leadSnap.data();
        const leadUpdates = {
          managementNotes: [...(leadData.managementNotes || []), { ...note, source: 'dashboard' }],
          updatedAt: serverTimestamp()
        };
        if (historyEvent) {
          leadUpdates.history = [...(leadData.history || []), historyEvent];
        }
        await updateDoc(leadRef, leadUpdates);
      }
    } catch (syncErr) {
      console.warn('[Nexus] Sync nota Dashboard → CRM:', syncErr.message);
    }
  }

  return { note, historyEvent };
};

/** Guarda observaciones/comentario sin agregar entrada al historial. */
export const saveLeadNotesFields = async (leadId, { observations, contactComment }) => {
  try {
    const leadRef = doc(db, "crm_leads", leadId);
    const payload = { updatedAt: serverTimestamp() };
    if (observations !== undefined) payload.observations = observations;
    if (contactComment !== undefined) payload.contactComment = contactComment;
    await updateDoc(leadRef, payload);
    return { success: true };
  } catch (e) {
    console.error("Error saving lead notes fields:", e);
    throw e;
  }
};

export const addManagementNote = async (leadId, noteData) => {
  try {
    const leadRef = doc(db, "crm_leads", leadId);
    const leadSnap = await getDoc(leadRef);
    if (!leadSnap.exists()) return false;

    const currentData = leadSnap.data();
    const newNote = buildManagementNoteRecord({ ...noteData, source: noteData.source || 'crm' });

    const updateData = {
      managementNotes: arrayUnion(newNote),
      updatedAt: serverTimestamp()
    };

    if (!currentData.firstContactDate) {
      updateData.firstContactDate = serverTimestamp();
    }

    await updateDoc(leadRef, updateData);
    return { success: true, note: newNote };
  } catch (e) {
    console.error("Error adding management note:", e);
    throw e;
  }
};

export const completeManagementNote = async (leadId, noteId) => {
  try {
    const leadRef = doc(db, "crm_leads", leadId);
    const leadSnap = await getDoc(leadRef);
    if (!leadSnap.exists()) return false;

    const currentData = leadSnap.data();
    const managementNotes = (currentData.managementNotes || []).map(note =>
      note.id === noteId ? { ...note, status: 'completed', completedAt: new Date().toISOString() } : note
    );

    await updateDoc(leadRef, {
      managementNotes,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (e) {
    console.error("Error completing management note:", e);
    throw e;
  }
};

export const getCRMSettings = async () => {
  try {
    const docRef = doc(db, "crm_settings", "tipificaciones");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      let modified = false;
      const categories = data.categories || {};

      // Asegurar que las nuevas categorías existan en el documento de Firebase
      if (!categories.MEET_ACTIVO || !categories.MEET_EN_PROCESO || !categories.MEET_DESCARTADO || !categories.PIP_GRIS || !categories.PIP_AMARILLO || !categories.PIP_NARANJA || !categories.PIP_VERDE || !categories.PIP_ROJO) {
        const { CRM_CATEGORIES } = await import('../dashboard/crm/crm-config');
        data.categories = {
          ...CRM_CATEGORIES,
          ...categories // Preservar personalizaciones existentes
        };
        modified = true;
      }

      if (modified) {
        await setDoc(docRef, data);
      }
      return data;
    } else {
      // Si no existe, usamos el default de crm-config.js
      const { CRM_CATEGORIES } = await import('../dashboard/crm/crm-config');
      await setDoc(docRef, { categories: CRM_CATEGORIES });
      return { categories: CRM_CATEGORIES };
    }
  } catch (e) {
    console.error("Error al obtener config CRM:", e);
    const { CRM_CATEGORIES } = await import('../dashboard/crm/crm-config');
    return { categories: CRM_CATEGORIES };
  }
};

export const updateCRMSettings = async (newCategories) => {
  try {
    const docRef = doc(db, "crm_settings", "tipificaciones");
    await setDoc(docRef, {
      categories: newCategories,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (e) {
    console.error("Error al actualizar config CRM:", e);
    throw e;
  }
};

// ==========================================
// 12. LINKEDIN LEADS (DUX-SOUP)
// ==========================================

export const fetchLinkedInLeads = async (opts = {}) => {
    try {
        const params = new URLSearchParams();
        if (opts.limit) params.set('limit', String(opts.limit));
        if (opts.cursor) params.set('cursor', String(opts.cursor));
        const qs = params.toString();
        const res = await apiCall(`/api/crm/linkedin-leads${qs ? `?${qs}` : ''}`, 'GET');
        return res;
    } catch (e) {
        console.error("fetchLinkedInLeads error:", e);
        return opts.limit ? { items: [], hasMore: false, nextCursor: null } : [];
    }
};

export const convertLinkedInLeadToCRM = async (id, executiveEmail, executiveName) => {
    try {
        const res = await apiCall(`/api/crm/linkedin-leads/${id}/convert`, 'POST', { executiveEmail, executiveName });
        return res;
    } catch (e) {
        console.error("convertLinkedInLeadToCRM error:", e);
        throw e;
    }
};

export const enrichLinkedInLeadPhone = async (id, opts = {}) => {
    const res = await apiCall(`/api/crm/linkedin-leads/${id}/enrich-phone`, 'POST', {
        applyIfEmpty: opts.applyIfEmpty !== false,
    });
    return res;
};

/** Busca teléfonos públicos en el dominio del lead CRM (on-demand, 1 lead). */
export const enrichCrmLeadPhone = async (id, opts = {}) => {
    const body = {};
    if (opts.domain) body.domain = opts.domain;
    const res = await apiCall(`/api/crm/leads/${id}/enrich-phone`, 'POST', body);
    return res;
};

export const deleteLinkedInLead = async (id) => {
    try {
        const res = await apiCall(`/api/crm/linkedin-leads/${id}`, 'DELETE');
        return res;
    } catch (e) {
        console.error("deleteLinkedInLead error:", e);
        throw e;
    }
};
export const deleteMarketingContact = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/campaigns/contacts/${id}`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error eliminando el contacto de marketing.');
        }
        return await response.json();
    } catch (error) {
        throw error;
    }
};

export const bulkDeleteMarketingContacts = async (ids) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/campaigns/contacts/bulk-delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error en la eliminación masiva.');
        }
        return await response.json();
    } catch (error) {
        throw error;
    }
};

export const uploadMeetingAttachment = async (leadId, file) => {
    try {
        const uniqueName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `leads/${leadId}/meeting_attachments/${uniqueName}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
    } catch (e) {
        console.error("Error uploading meeting attachment:", e);
        throw e;
    }
};

export const generateMigrationReport = async ({
  contractId,
  clientDomain,
  companyName,
  sources,
  preparedBy,
  userEmail
}) => {
  return await apiCall('/api/migration-reports/generate', 'POST', {
    contractId,
    clientDomain,
    companyName,
    sources,
    preparedBy,
    userEmail
  });
};

export const browseMigrationDrive = async ({ userEmail, folderId, mode, location, driveId }) => {
  return await apiCall('/api/migration-reports/drive/browse', 'POST', {
    userEmail,
    folderId,
    mode,
    location,
    driveId
  });
};

export const listMigrationDriveFolderFiles = async ({ userEmail, folderId, mode, location, driveId }) => {
  return await apiCall('/api/migration-reports/drive/folder-files', 'POST', {
    userEmail,
    folderId,
    mode,
    location,
    driveId
  });
};

export const restoreArchivedContracts = async ({ userEmail, contractId, dryRun = false, all = false } = {}) => {
  return await apiCall('/api/contracts/restore-archived', 'POST', {
    userEmail,
    contractId,
    dryRun,
    all
  });
};

export const sendMigrationReport = async ({
  reportId,
  contractId,
  recipientName,
  recipientEmail,
  senderEmail,
  senderName
}) => {
  return await apiCall('/api/migration-reports/send', 'POST', {
    reportId,
    contractId,
    recipientName,
    recipientEmail,
    senderEmail,
    senderName
  });
};

export const uploadMigrationReport = async (clientId, clientDomain, htmlFile) => {
    try {
        const uniqueName = `migration_report_${Date.now()}.html`;
        const storageRef = ref(storage, `migration_reports/${clientId}/${uniqueName}`);
        
        // Ensure content type is HTML
        const metadata = { contentType: 'text/html' };
        await uploadBytes(storageRef, htmlFile, metadata);
        const fileUrl = await getDownloadURL(storageRef);
        
        const reportData = {
            clientId,
            clientDomain,
            fileUrl,
            fileName: htmlFile.name,
            createdAt: new Date().toISOString(),
            expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
            storagePath: storageRef.fullPath
        };

        const docRef = await addDoc(collection(db, 'migration_reports'), reportData);
        return { id: docRef.id, ...reportData };
    } catch (e) {
        console.error("Error uploading migration report:", e);
        throw e;
    }
};

export const getMigrationReport = async (reportId) => {
    const user = auth.currentUser;
    if (!user) {
        const err = new Error('Inicia sesión para ver este reporte.');
        err.status = 401;
        err.code = 'unauthenticated';
        throw err;
    }
    if (!reportId) return null;

    const withTimeout = (promise, ms, label) => {
        let timer;
        const timeout = new Promise((_, reject) => {
            timer = setTimeout(() => {
                const err = new Error(`Timeout ${label} (${ms}ms)`);
                err.status = 408;
                err.code = 'timeout';
                reject(err);
            }, ms);
        });
        return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
    };

    try {
        const token = await withTimeout(user.getIdToken(), 8000, 'token-reporte');
        const controller = new AbortController();
        const abortTimer = setTimeout(() => controller.abort(), 25000);
        let response;
        try {
            response = await fetch(
                `${API_BASE_URL}/api/migration-reports/${encodeURIComponent(reportId)}`,
                {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal
                }
            );
        } finally {
            clearTimeout(abortTimer);
        }

        const data = await response.json().catch(() => ({}));
        if (response.status === 404) return null;
        if (!response.ok) {
            const err = new Error(data.error || 'Error al cargar el reporte.');
            err.status = response.status;
            err.code = data.code || 'error';
            throw err;
        }
        return data;
    } catch (e) {
        if (e?.name === 'AbortError') {
            const err = new Error('El reporte tardó demasiado en responder.');
            err.status = 408;
            err.code = 'timeout';
            throw err;
        }
        if (e?.code !== 'timeout') {
            console.error('Error fetching migration report:', e);
        }
        throw e;
    }
};

export const deleteMigrationReport = async (reportId) => {
    try {
        const docRef = doc(db, 'migration_reports', reportId);
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
            const data = snapshot.data();
            if (data.storagePath) {
                const storageRef = ref(storage, data.storagePath);
                try {
                    await deleteObject(storageRef);
                } catch (err) {
                    console.warn("Could not delete file from storage:", err);
                }
            }
            await deleteDoc(docRef);
        }
        return true;
    } catch (e) {
        console.error("Error deleting migration report:", e);
        throw e;
    }
};

// ==========================================
// 📢 TABLERO DE AVISOS GLOBAL
// ==========================================
const FIRESTORE_SERVER_READ_MS = 8000;

function withFirestoreTimeout(promise, ms, label) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
            const err = new Error(`Timeout ${label} (${ms}ms)`);
            err.code = 'timeout';
            reject(err);
        }, ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export const getGlobalNotices = async () => {
    const q = query(collection(db, "global_notices"), orderBy("createdAt", "desc"));
    const snapshot = await withFirestoreTimeout(
        getDocsFromServer(q),
        FIRESTORE_SERVER_READ_MS,
        'global-notices'
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const saveGlobalNotice = async (noticeData, noticeId = null) => {
    try {
        const cleanedData = JSON.parse(JSON.stringify(noticeData, (key, value) => {
            return value === undefined ? null : value;
        }));
        if (noticeId) {
            const docRef = doc(db, "global_notices", noticeId);
            await updateDoc(docRef, { ...cleanedData, updatedAt: serverTimestamp() });
            return noticeId;
        } else {
            const docRef = await addDoc(collection(db, "global_notices"), {
                ...cleanedData,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            return docRef.id;
        }
    } catch (e) {
        console.error("Error saving global notice:", e);
        throw e;
    }
};

export const deleteGlobalNotice = async (noticeId) => {
    try {
        await deleteDoc(doc(db, "global_notices", noticeId));
        return true;
    } catch (e) {
        console.error("Error deleting notice:", e);
        throw e;
    }
};

export const uploadNoticeImage = async (file) => {
    try {
        const fileExtension = file.name.split('.').pop();
        const fileName = `notice_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExtension}`;
        const storageRef = ref(storage, `global_notices/${fileName}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
    } catch (e) {
        console.error("Error uploading notice image:", e);
        throw e;
    }
};

export const markNoticeAsRead = async (noticeId, userId, userName, userEmail) => {
    try {
        const viewRef = doc(db, "global_notices", noticeId, "views", userId);
        await setDoc(viewRef, {
            userId,
            userName: userName || "Usuario",
            userEmail: userEmail || "",
            readAt: serverTimestamp()
        }, { merge: true });
        return true;
    } catch (e) {
        console.error("Error marking notice as read:", e);
        throw e;
    }
};

export const getNoticeViews = async (noticeId) => {
    const q = query(collection(db, "global_notices", noticeId, "views"), orderBy("readAt", "desc"));
    const snapshot = await withFirestoreTimeout(
        getDocsFromServer(q),
        FIRESTORE_SERVER_READ_MS,
        'notice-views'
    );
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const fetchCsvPoolStats = async () => apiCall('/api/crm/pool-stats', 'GET');

export const pauseCampaign = async (campaignId) => {
  try {
    const campaignRef = doc(db, "marketing_campaigns", campaignId);
    await updateDoc(campaignRef, {
      status: 'paused',
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error("Error pausing campaign:", error);
    throw error;
  }
};

export const fetchCampaignRecipients = async (campaignId, statusFilt = null) => {
  try {
    const recipientsRef = collection(db, "marketing_campaigns", campaignId, "recipients");
    let q = recipientsRef;
    if (statusFilt) {
      q = query(recipientsRef, where("status", "==", statusFilt));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching recipients:", error);
    return [];
  }
};

// Integración con EasyLex
export const sendToEasylex = async (contractId, signersList, adminEmail, opts = {}) => {
    // Hosting→Cloud Run corta ~60s; EasyLex live suele tardar más → Cloud Run directo.
    const base = String(resolveLongJobApiBaseUrl() || '').replace(/\/$/, '');
    const payload = {
        contractId,
        signersList,
        adminEmail,
        ...(opts.mode != null ? { mode: opts.mode } : {}),
        ...(opts.sendEmail != null ? { sendEmail: opts.sendEmail } : {}),
        ...(opts.fromLegalLab ? { fromLegalLab: true } : {})
    };
    if (!base) {
        return await apiCall('/api/easylex/document/send', 'POST', payload);
    }
    const response = await fetch(`${base}/api/easylex/document/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const err = new Error(data.error || `Error del servidor: ${response.status}`);
        err.status = response.status;
        if (data.code) err.code = data.code;
        throw err;
    }
    return data;
};

/** Anula EasyLex en curso y regresa el expediente a LEGAL_REVIEW. */
export const returnContractToLegal = async (contractId, { reason, adminEmail, notifySigners = true } = {}) =>
  apiCall('/api/easylex/document/return-to-legal', 'POST', {
    contractId,
    reason: reason || null,
    adminEmail: adminEmail || null,
    byEmail: adminEmail || null,
    notifySigners
  });

/** Reenvía invitación Plexarian (/firmar); permite corregir correo/tel. del cliente. */
export const resendEasylexInvite = async (contractId, client = {}, adminEmail) => {
    return await apiCall('/api/easylex/document/resend-invite', 'POST', {
        contractId,
        client,
        adminEmail
    });
};

// ==========================================
// 🎫 MESA DE SOPORTE GWS (support_tickets)
// ==========================================

/** Cache en memoria para no bloquear la UI al reabrir Mesa (misma sesión). */
let supportTicketsCache = null;

export function getSupportTicketsCache() {
  return supportTicketsCache;
}

export const subscribeSupportTickets = (onData, onError) => {
  if (supportTicketsCache) {
    onData(supportTicketsCache);
  }

  const q = query(collection(db, 'support_tickets'), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const tickets = snapshot.docs.map((d) =>
        enrichTicketSla(normalizeSupportTicket({ id: d.id, ...d.data() }))
      );
      supportTicketsCache = tickets;
      onData(tickets);
    },
    (err) => {
      console.error('[support_tickets]', err);
      if (onError) {
        onError(err);
      } else if (supportTicketsCache) {
        onData(supportTicketsCache);
      }
    }
  );
};

export const createSupportTicket = async (fields) => {
  const now = new Date().toISOString();
  const schemaFields = buildCreateFields({
    role: fields.role || 'user',
    question: fields.question || fields.title || '',
    priority: fields.priority,
    category: fields.category,
    subcategory: fields.subcategory,
    supportTier: fields.supportTier,
    ingressChannel: fields.ingressChannel || 'internal',
    source: fields.source || 'internal'
  });
  const payload = {
    domain: (fields.domain || '').trim().toLowerCase(),
    role: fields.role || 'user',
    question: (fields.question || fields.title || '').trim(),
    description: (fields.description || '').trim(),
    phone: (fields.phone || '').trim(),
    contactInfo: (fields.phone || '').trim(),
    userEmail: (fields.userEmail || '').trim().toLowerCase(),
    userName: (fields.userName || '').trim() || null,
    domainAdminEmails: Array.isArray(fields.domainAdminEmails)
      ? [...new Set(fields.domainAdminEmails.map((e) => String(e).trim().toLowerCase()).filter(Boolean))]
      : [],
    sessionId: null,
    chatHistory: [],
    status: 'open',
    ...schemaFields,
    source: fields.source || 'internal',
    assignedToEmail: fields.assignedToEmail || null,
    assignedToName: fields.assignedToName || null,
    assignedAt: fields.assignedToEmail ? now : null,
    internalNotes: fields.internalNotes || '',
    createdAt: now,
    updatedAt: now,
    resolvedAt: null,
    ...buildSlaFieldsForCreate(now, schemaFields.priority),
    emailThread: [],
    emailSubject: null,
    gmailThreadId: null,
    lastGmailMessageId: null,
    activityLog: [
      {
        at: now,
        action: 'created',
        byEmail: fields.createdByEmail || null,
        byName: fields.createdByName || null,
        detail: fields.activityDetail || 'Ticket creado desde Mesa de Soporte'
      }
    ],
    attachments: Array.isArray(fields.attachments) ? fields.attachments : [],
    classificationSource: fields.classificationSource || 'default',
    aiClassifyReason: fields.aiClassifyReason || null
  };
  const docRef = await addDoc(collection(db, 'support_tickets'), payload);
  const ticketRef = docRef.id.slice(-6).toUpperCase();
  await updateDoc(docRef, { ticketRef });

  return { id: docRef.id, ticketRef };
};

export const notifySupportTicketCreated = async (ticketId) => {
  return supportTicketsAuthFetch(`/api/support-tickets/${ticketId}/notify-new`, 'POST', {});
};

export const notifySupportTicketResolved = async (ticketId, { force = false } = {}) => {
  return supportTicketsAuthFetch(`/api/support-tickets/${ticketId}/notify-resolved`, 'POST', { force });
};

export const updateSupportTicket = async (ticketId, updates) => {
  const ref = doc(db, 'support_tickets', ticketId);
  await updateDoc(ref, {
    ...updates,
    updatedAt: new Date().toISOString()
  });
};

export const appendSupportTicketActivity = async (ticketId, entry, extraFields = {}) => {
  const ref = doc(db, 'support_tickets', ticketId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Ticket no encontrado');
  const prev = snap.data().activityLog || [];
  await updateDoc(ref, {
    ...extraFields,
    activityLog: [...prev, { at: new Date().toISOString(), ...entry }],
    updatedAt: new Date().toISOString()
  });
};

const supportTicketsAuthFetch = async (endpoint, method, body) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  const token = await user.getIdToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
  const options = { method, headers };
  if (body != null && method !== 'GET') {
    options.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE_URL}${endpoint}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

export const sendSupportTicketReply = async (ticketId, body, to = null, attachmentIds = []) => {
  return supportTicketsAuthFetch(`/api/support-tickets/${ticketId}/reply`, 'POST', {
    body,
    to,
    attachmentIds
  });
};

export const assignSupportTicket = async (ticketId, { assigneeEmail, assigneeName }) => {
  return supportTicketsAuthFetch(`/api/support-tickets/${ticketId}/assign`, 'POST', {
    assigneeEmail,
    assigneeName
  });
};

export const fetchStellrInvoices = async ({ paid, sync = true } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  const token = await user.getIdToken();
  const params = new URLSearchParams();
  if (paid) params.set('paid', paid);
  if (sync === false) params.set('sync', '0');
  const qs = params.toString();
  const res = await fetch(`${API_BASE_URL}/api/stellr/invoices${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Error ${res.status}`);
    err.code = data.code;
    throw err;
  }
  return data;
};

/** Sube comprobante de pago TD SYNNEX a Drive (no GCP). */
export const uploadStellrPaymentProof = async ({ orderNo, issueDate, dominio, customerName, file }) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  if (!orderNo) throw new Error('Falta el número de pedido.');
  if (!file) throw new Error('Adjunta el comprobante.');
  const token = await user.getIdToken();
  const body = new FormData();
  body.append('file', file);
  if (issueDate) body.append('issueDate', issueDate);
  if (dominio) body.append('dominio', dominio);
  if (customerName) body.append('customerName', customerName);
  const res = await fetch(`${API_BASE_URL}/api/stellr/invoices/${encodeURIComponent(orderNo)}/proof`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data.proof;
};

export const fetchStellrPassthroughClients = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE_URL}/api/stellr/passthrough/clients`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

export const setStellrPassthroughClient = async ({ dominio, enabled, markupPct }) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  if (!dominio) throw new Error('Falta el dominio.');
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE_URL}/api/stellr/passthrough/clients/${encodeURIComponent(dominio)}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ enabled: Boolean(enabled), markupPct }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data.client;
};

const collectionChannelAuthFetch = async (endpoint, { method = 'GET', body, signal } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  const token = await user.getIdToken();
  const headers = { Authorization: `Bearer ${token}` };
  const options = { method, headers, signal };
  if (body != null && method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE_URL}${endpoint}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

export const fetchPaypalCollectionClients = async () => {
  return collectionChannelAuthFetch('/api/billing/collection-channel/clients');
};

export const searchCollectionChannelClients = async (q, signal) => {
  const query = String(q || '').trim();
  if (query.length < 2) return { results: [] };
  return collectionChannelAuthFetch(
    `/api/billing/collection-channel/search?q=${encodeURIComponent(query)}`,
    { signal }
  );
};

export const setClientCollectionChannel = async ({ contractId, domain, channel }) => {
  const data = await collectionChannelAuthFetch('/api/billing/collection-channel', {
    method: 'PATCH',
    body: { contractId, domain, channel },
  });
  return data.client;
};

export const generatePaypalCollectionLink = async ({ invoiceUuid, domain, companyName }) => {
  return collectionChannelAuthFetch('/api/billing/paypal/subscriptions', {
    method: 'POST',
    body: { invoiceUuid, domain, companyName },
  });
};

export const fetchPaypalCollectionContext = async (domain) => {
  return collectionChannelAuthFetch(
    `/api/billing/paypal/subscriptions/context?domain=${encodeURIComponent(domain || '')}`
  );
};

export const sendPaypalCollectionLink = async ({
  domain,
  invoiceUuid,
  companyName,
  emails,
  extraEmails,
}) => {
  return collectionChannelAuthFetch('/api/billing/paypal/subscriptions/send', {
    method: 'POST',
    body: { domain, invoiceUuid, companyName, emails, extraEmails },
  });
};

const gwsInvoiceAuthFetch = async (endpoint, { method = 'GET', body, isForm } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  const token = await user.getIdToken();
  const headers = { Authorization: `Bearer ${token}` };
  const options = { method, headers };
  if (isForm) {
    options.body = body;
  } else if (body != null && method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE_URL}${endpoint}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

export const fetchGoogleWsInvoices = async () => {
  try {
    return await gwsInvoiceAuthFetch('/api/google-ws-invoices/invoices');
  } catch (e) {
    if (/404/.test(String(e.message || ''))) return { success: true, invoices: [] };
    throw e;
  }
};

export const fetchGoogleWsMargin = async ({ yearMonth, id, force } = {}) => {
  const params = new URLSearchParams();
  if (yearMonth) params.set('yearMonth', yearMonth);
  if (id) params.set('id', id);
  if (force) params.set('fresh', '1');
  const qs = params.toString();
  try {
    return await gwsInvoiceAuthFetch(`/api/google-ws-invoices/margin${qs ? `?${qs}` : ''}`);
  } catch (e) {
    if (/404/.test(String(e.message || ''))) return { success: true, invoice: null, report: null };
    throw e;
  }
};

export const fetchGoogleWsCosts = async ({ yearMonth } = {}) => {
  const params = new URLSearchParams();
  if (yearMonth) params.set('yearMonth', yearMonth);
  const qs = params.toString();
  try {
    return await gwsInvoiceAuthFetch(`/api/google-ws-invoices/costs${qs ? `?${qs}` : ''}`);
  } catch (e) {
    if (/404/.test(String(e.message || ''))) return { success: true, byDomain: {} };
    throw e;
  }
};

export const uploadGoogleWsInvoice = async (file) => {
  const form = new FormData();
  form.append('file', file);
  return gwsInvoiceAuthFetch('/api/google-ws-invoices/invoices', { method: 'POST', body: form, isForm: true });
};

export const classifySupportTicket = async ({ title, description, role }) => {
  return supportTicketsAuthFetch('/api/support-tickets/classify', 'POST', {
    title,
    description,
    role
  });
};

export const uploadSupportTicketAttachments = async (ticketId, files, uploadedBy) => {
  if (!files?.length) return [];
  const attachments = [];
  for (const file of files) {
    const safeName = String(file.name || 'archivo').replace(/[^a-zA-Z0-9._-]/g, '_');
    const storageRef = ref(storage, `support_tickets/${ticketId}/${Date.now()}_${safeName}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    attachments.push({
      id: `${Date.now()}_${safeName}`,
      name: file.name,
      type: file.type || 'application/octet-stream',
      storagePath: storageRef.fullPath,
      url,
      uploadedAt: new Date().toISOString(),
      uploadedBy
    });
  }
  const refDoc = doc(db, 'support_tickets', ticketId);
  const snap = await getDoc(refDoc);
  const prev = snap.exists() ? snap.data().attachments || [] : [];
  await updateDoc(refDoc, {
    attachments: [...prev, ...attachments],
    updatedAt: new Date().toISOString()
  });
  return attachments;
};

export const syncSupportTicketEmail = async (ticketId) => {
  return supportTicketsAuthFetch(`/api/support-tickets/${ticketId}/sync-email`, 'POST', {});
};

export const deleteSupportTicket = async (ticketId) => {
  return supportTicketsAuthFetch(`/api/support-tickets/${ticketId}`, 'DELETE');
};

export const fetchSupportTicketAiAssist = async (ticketId, focus = 'full') => {
  return supportTicketsAuthFetch(`/api/support-tickets/${ticketId}/ai-assist`, 'POST', { focus });
};

export const fetchSupportKbPreview = async (limit = 12) => {
  return supportTicketsAuthFetch(`/api/support-tickets/knowledge-preview?limit=${limit}`, 'GET');
};

export const searchSupportClientDomains = async (q) => {
  const res = await searchDnsSetupClients(q);
  return {
    domains: (res.clients || []).map((c) => ({
      domain: c.domain,
      companyName: c.razonSocial || c.companyName || '',
      customerId: c.customerId || null
    }))
  };
};

export const fetchSupportDomainAdmins = async (domain) => {
  return supportTicketsAuthFetch(
    `/api/support-tickets/domain-admins?domain=${encodeURIComponent(domain)}`,
    'GET'
  );
};

export const getSupportDeskTaxonomy = async () => {
  const snap = await getDoc(doc(db, 'support_desk_config', 'taxonomy'));
  if (!snap.exists()) return null;
  return snap.data().categories || null;
};

export const saveSupportDeskTaxonomy = async (categories) => {
  const user = auth.currentUser;
  await setDoc(
    doc(db, 'support_desk_config', 'taxonomy'),
    {
      categories,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.email || null
    },
    { merge: true }
  );
};

/* ——— Packs de dispersión (Contabilidad) ——— */

const dispersalAuthFetch = async (endpoint, { method = 'GET', body, formData } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión en Nexus.');
  const token = await user.getIdToken();
  const headers = { Authorization: `Bearer ${token}` };
  const options = { method, headers };
  if (formData) {
    options.body = formData;
  } else if (body != null && method !== 'GET' && method !== 'DELETE') {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  } else if (body != null && method === 'DELETE') {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE_URL}${endpoint}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Error ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

export const fetchDispersalPacks = () => dispersalAuthFetch('/api/dispersal/packs');
export const fetchDispersalPack = (id) => dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}`);
export const createDispersalPack = (body) => dispersalAuthFetch('/api/dispersal/packs', { method: 'POST', body });
export const deleteDispersalPack = (id) =>
  dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const updateDispersalPack = (id, body) =>
  dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}`, { method: 'PUT', body });
export const addDispersalLine = (id, body) =>
  dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}/lines`, { method: 'POST', body });
export const generateDispersalInvoice = (id, body = {}) =>
  dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}/generate`, { method: 'POST', body });
export const preloadDispersalPack = (id, body = {}) =>
  dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}/preload`, { method: 'POST', body });
export const publishDispersalPack = (id) =>
  dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}/publish`, { method: 'POST', body: {} });
export const uploadDispersalProof = async (id, file, { lineId } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (lineId) formData.append('lineId', lineId);
  return dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}/proofs`, {
    method: 'POST',
    formData,
  });
};
export const uploadDispersalReferenceDoc = async (id, file, lineId) => {
  const formData = new FormData();
  formData.append('file', file);
  return dispersalAuthFetch(
    `/api/dispersal/packs/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}/reference-doc`,
    { method: 'POST', formData },
  );
};
export const deleteDispersalReferenceDoc = (id, lineId) =>
  dispersalAuthFetch(
    `/api/dispersal/packs/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}/reference-doc`,
    { method: 'DELETE' },
  );
export const dispersalReferenceDocUrl = (id, lineId) =>
  `${API_BASE_URL}/api/dispersal/packs/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}/reference-doc`;
export const matchDispersalProof = (id, body) =>
  dispersalAuthFetch(`/api/dispersal/packs/${encodeURIComponent(id)}/match`, { method: 'POST', body });
export const fetchDispersalBeneficiaries = () => dispersalAuthFetch('/api/dispersal/beneficiaries');
export const createDispersalBeneficiary = (body) =>
  dispersalAuthFetch('/api/dispersal/beneficiaries', { method: 'POST', body });
export const updateDispersalBeneficiary = (id, body) =>
  dispersalAuthFetch(`/api/dispersal/beneficiaries/${encodeURIComponent(id)}`, { method: 'PUT', body });
export const deleteDispersalBeneficiary = (id) =>
  dispersalAuthFetch(`/api/dispersal/beneficiaries/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const fetchDispersalSkus = () => dispersalAuthFetch('/api/dispersal/skus');
export const createDispersalSku = (body) =>
  dispersalAuthFetch('/api/dispersal/skus', { method: 'POST', body });
export const updateDispersalSku = (id, body) =>
  dispersalAuthFetch(`/api/dispersal/skus/${encodeURIComponent(id)}`, { method: 'PUT', body });
export const deleteDispersalSku = (id) =>
  dispersalAuthFetch(`/api/dispersal/skus/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const fetchPublicDispersalPack = async (token) => {
  const res = await fetch(`${API_BASE_URL}/api/dispersal/publico/${encodeURIComponent(token)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

export const uploadPublicDispersalProof = async (token, file, { lineId } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (lineId) formData.append('lineId', lineId);
  const res = await fetch(`${API_BASE_URL}/api/dispersal/publico/${encodeURIComponent(token)}/proofs`, {
    method: 'POST',
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `Error ${res.status}`);
  return data;
};

export const publicDispersalExportUrl = (token, kind = 'csv') =>
  `${API_BASE_URL}/api/dispersal/publico/${encodeURIComponent(token)}/export.${kind === 'pdf' ? 'pdf' : 'csv'}`;

export const publicDispersalReferenceDocUrl = (token, lineId) =>
  `${API_BASE_URL}/api/dispersal/publico/${encodeURIComponent(token)}/lines/${encodeURIComponent(lineId)}/reference-doc`;

